import {
  Component,
  ElementRef,
  Input,
  OnInit,
  SimpleChanges,
  OnChanges,
  EventEmitter,
  Output,
  AfterViewInit,
  OnDestroy,
  NgZone,
  ViewChild,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import mapboxgl from 'mapbox-gl';
import {
  ICliente,
  IGeoJSONCircle,
  ILayerMapaPersonalizado,
  IRecorrido,
  IRecorridoDinamico,
  IReporteBase,
  IUbicacion,
  Stop,
  IVistaMarker,
  IValoresEventoBase,
  IValoresEventoTracker,
  IEventoGenerico,
} from 'modelos/src';
import { LoginService } from '../../../../login/login.service';
import { TipoMapaBase } from '../../../../standalone/selector-tipo-mapa/selector-tipo-mapa.component';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { formatDate } from '@angular/common';
import { OsrmService } from '../../../../../auxiliares/servicios/osrm.service';
import { MapboxMapInstance, SeleccionadosBarraLateral } from '../interfaces';
import {
  COLOR_EVENTO,
  COLOR_EXCESO,
  COLOR_PARADA,
  COLOR_PUNTO_ANIMADO,
  COLOR_VIAJE,
  RADIO_MARKER_EVENTO,
  RADIO_MARKER_EXCESO,
  RADIO_MARKER_PARADA,
} from '../colores-viaje';
import { ServiceGeneralVehiculos } from './services/service-general';
import {
  ServiceDibujarVehiculos,
  DibujarCallbacks,
} from './services/service-dibujar';
import { ServiceStorageVehiculos } from './services/service-storage';
import { MapaVehiculosService } from '../mapaVehiculos.service';
import {
  MergedIconParams,
  VehicleCompositeIconService,
} from '../../../../../auxiliares/mapbox/services/vehicle-composite-icon.service';
import { VehicleIconGeneratorService } from '../../../../../auxiliares/mapbox/services/vehicle-icon-generator.service';
import {
  HTMLVehicleMarker,
  HTMLVehicleMarkerConfig,
} from '../../../../../auxiliares/mapbox/html-vehicle-marker';
import { MapCameraService } from '../../../../../auxiliares/mapbox/map-camera.service';
import { API, MAPBOX_TOKEN } from '../../../../../../environments/environment';
import { aplicarEtiquetasMapa } from '../../../../../auxiliares/mapbox/etiquetas-mapa';
import {
  ubicarReportesEnTraza,
  valorInterpolado,
} from '../traza-reportes.util';
import {
  sanitizarHtml,
  escaparHtml,
} from '../../../../../auxiliares/html-seguro.util';

/**
 * Cada cuántas generaciones de ícono se le devuelve el hilo al navegador
 * (ver `conConcurrencia`). Más alto = menos cortes pero tirones más largos.
 */
const TAREAS_ENTRE_CESIONES = 16;

/** Cada cuánto, como mucho, se repinta la flota mientras se generan íconos. */
const MS_ENTRE_REPINTADOS = 150;

/**
 * Cada cuántos reportes se cede el hilo mientras se arma la colección de
 * markers. Sin filtro de cliente el loop recorre la flota entera de una.
 */
const REPORTES_ENTRE_CESIONES = 300;

/** Lo que muestra el reproductor cuando todavía no hay viaje cargado. */
const SIN_FECHA = '--/-- --:--:--';

/**
 * Qué tan lejos de la traza (en píxeles) puede caer un click para contar como
 * click sobre ella: la línea mide 3 o 4 px y apuntarle justo cuesta.
 */
const TOLERANCIA_CLICK_TRAZA_PX = 10;

/** Una pasada del viaje por un punto de la pantalla. */
interface PasadaViaje {
  /** Distancia recorrida hasta ahí, en metros. */
  d: number;
  /** El lugar sobre la traza, [lng, lat]. */
  coord: [number, number];
  /** A cuántos píxeles de la traza quedó el punto. */
  px: number;
}

/**
 * Componente de mapa de vehículos con Mapbox GL JS
 * Soporta múltiples instancias de mapa simultáneas (tiling windows)
 *
 * PASO 1 IMPLEMENTADO:
 * - Múltiples instancias de mapboxgl.Map
 * - Capas base configurables (OSM, satélite, CartoDB, Google, personalizadas)
 * - Persistencia de zoom/center/tipoMapa en localStorage
 * - Selección de mapa activo
 *
 * PENDIENTE (próximos pasos):
 * - Markers de vehículos con estilos complejos
 * - Ubicaciones (polígonos)
 * - Recorridos (líneas)
 * - Círculos de búsqueda
 * - Polylines de búsqueda
 * - Animación de trips
 * - Popups e interacciones
 */
@Component({
  selector: 'app-mapa-individual-vehiculos',
  templateUrl: './mapa-individual.component.html',
  styleUrls: ['./mapa-individual.component.scss'],
  standalone: false,
})
export class MapaIndividualVehiculosComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  private helper = inject(HelperService);
  private serviceGeneral = inject(ServiceGeneralVehiculos);
  private serviceDibujar = inject(ServiceDibujarVehiculos);
  private serviceStorage = inject(ServiceStorageVehiculos);
  private mapaService = inject(MapaVehiculosService);
  private vehicleCompositeIconService = inject(VehicleCompositeIconService);
  private vehicleIconGenerator = inject(VehicleIconGeneratorService);
  private ngZone = inject(NgZone);
  private osrmService = inject(OsrmService);

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // INPUTS
  @Input() reportes?: IReporteBase<'Tracker 4G'>[] = [];
  @Input() cantMapas: number = 1;
  @Input() mapaSeleccionado: number = 1;
  @Input() seleccionadoBarraLateral?: SeleccionadosBarraLateral;
  @Input() seleccionadosGenerales?: SeleccionadosBarraLateral[];
  @Input() ubicaciones?: IUbicacion[];
  @Input() recorridos?: IRecorrido[];
  @Input() recorridosDinamicos?: IRecorridoDinamico[];

  private readonly SESSION_STORAGE_KEY = 'vehiculos_seleccionadosBarraLateral';

  //////////////////////////////////////////////////////////////////////////////////////
  //OUTPUTS
  @Output() reporteSeleccionadoVer = new EventEmitter<{
    reporte: IReporteBase<'Tracker 4G'>;
    mapaNum: number;
  }>();
  /** Zona clickeada en el mapa. `ubicacion` undefined = se deseleccionó. */
  @Output() ubicacionSeleccionadaVer = new EventEmitter<{
    ubicacion?: IUbicacion;
    mapaNum: number;
  }>();
  @Output() mapaSeleccionadoChange = new EventEmitter<number>();
  @Output() public paradaSelectedOut = new EventEmitter<Stop>();
  @Output() public eventoSelectedOut = new EventEmitter<IEventoGenerico>();
  @Output() circuloBusquedaChange = new EventEmitter<IGeoJSONCircle>();

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // MAPAS
  public nombreMap = 'MapaVehiculosComponent';
  public readonly TIPO_MAPA_BASE_KEY = 'tipoMapaBaseVehiculosComponent';
  public tipoMapaBase: TipoMapaBase = 'osm';
  private defaultTipoMapaBase: TipoMapaBase = 'osm';
  private tipoMapaBaseByIndex: Record<number, TipoMapaBase> = {};
  // Cambio de capa base por mapa: generación vigente y listeners pendientes de
  // la reconstrucción (ver aplicarEstiloBase).
  private estiloEpochPorMapa: Record<number, number> = {};
  private estiloListoCleanupPorMapa: Record<number, () => void> = {};
  public mapasPersonalizados: ILayerMapaPersonalizado[] = [];
  public clientePropio: ICliente = LoginService.getClientePropio();
  public mapInstances: MapboxMapInstance[] = [];

  // Tracking de paradas previas para evitar re-renders innecesarios
  private previousParadasByMap = new Map<number, Stop[]>();

  // Tracking de parada/evento seleccionado previo para evitar zoom repetido
  private previousParadaSeleccionadaByMap = new Map<number, Stop | undefined>();
  private previousEventoSeleccionadoByMap = new Map<
    number,
    string | undefined
  >();

  // Tracking de eventos previos para evitar re-renders innecesarios
  private previousEventosByMap = new Map<
    number,
    { longitude: number; latitude: number; id?: string }[]
  >();

  // Recorrido / trip
  public currentTrip?: [number, number][];
  public speed: number = 720;

  /**
   * Color de la reproducción: la traza que va dejando el play y el punto que se
   * mueve. Se guarda por usuario en localStorage, igual que el estilo del
   * panel: `IConfigUsuario` descarta las claves que no declara.
   */
  public colorReproduccion = COLOR_PUNTO_ANIMADO;

  /**
   * Barra de progreso del viaje. Se le escribe el valor directo: el avance se
   * calcula en el loop de la animación, que corre fuera de la zona de Angular,
   * y hacerlo por binding obligaría a un ciclo de detección de cambios por
   * cuadro.
   */
  @ViewChild('progresoViaje')
  private progresoViajeRef?: ElementRef<HTMLInputElement>;

  /**
   * Hora del punto del viaje que se está viendo y hora en la que termina. Se
   * escriben a mano por el mismo motivo que la barra: las mueve el loop de la
   * animación, que corre fuera de la zona de Angular.
   */
  @ViewChild('fechaProgreso')
  private fechaProgresoRef?: ElementRef<HTMLElement>;

  @ViewChild('fechaFinViaje')
  private fechaFinViajeRef?: ElementRef<HTMLElement>;

  /** Línea de tiempo que ya tiene pintada la etiqueta de la hora de fin. */
  private lineaTiempoPintada?: { d: number; t: number }[];

  /** Armado una sola vez: `toLocaleString` por cuadro sale caro. */
  private readonly formatoFechaViaje = new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  // Estado del popup de ayuda
  public showHelp: boolean = false;

  // Suscripción a actualizaciones en tiempo real (WebSocket)
  private realTimeSubscription?: Subscription;
  private realTimeBatchSubscription?: Subscription;

  // Suscripción a cambios en reportes filtrados por búsqueda
  private reportesFiltradosSubscription?: Subscription;

  // Suscripción a pan único desde selección por evento
  private panOnceSubscription?: Subscription;

  // Suscripción al pedido de dibujo desde el panel de búsqueda
  private dibujarCirculoSubscription?: Subscription;

  // Suscripción a cambios en círculo de búsqueda desde la barra lateral
  private circuloBusquedaSubscription?: Subscription;

  // Suscripción a cambios en polígono de búsqueda (ubicación seleccionada)
  private poligonoBusquedaSubscription?: Subscription;

  // Observers de resize para llamar map.resize() cuando el contenedor cambia de tamaño
  private resizeObservers: ResizeObserver[] = [];

  // Versión de recreación: se incrementa en cada llamada a recreateMapInstances.
  // Permite cancelar una recreación en curso cuando llega un nuevo cambio de cantMapas.
  private recreateVersion = 0;

  // Intervalo de refresco de tiles de tráfico (cada 2 min)
  private trafficRefreshInterval: ReturnType<typeof setInterval> | null = null;

  // Intervalo mínimo entre flushes de updates de vehículos (throttle del symbol layer).
  // Cada flush dispara un updateData → el worker de Mapbox re-teselá los símbolos
  // del tile (con zoom alejado, toda la flota en un tile → reconstruye todos).
  // 1500ms reduce ~33% los rebuilds; a ese zoom es imperceptible.
  private readonly VEHICULO_FLUSH_MS = 1500;

  // Historial de rutas para cada mapa (acumulado desde que se selecciona un vehículo)
  private routeHistory = new Map<number, [number, number][]>();
  private readonly MAX_ROUTE_HISTORY = 20000;

  // IDs de vehículos filtrados por búsqueda por cada mapa (si está vacío, mostrar todos)
  private vehiculosFiltradosPorBusqueda = new Map<number, Set<string>>();

  // Colores del tema
  private appliedPrimaryColor: string = '#ffcc33';
  private appliedAccentColor: string = '#ff6633';

  // Callbacks para el servicio de dibujo
  private dibujarCallbacks: DibujarCallbacks = {
    isDibujoPermitido: () => !!this.seleccionadoBarraLateral?.puedeDibujar,
    emitCirculoBusquedaChange: (circle) =>
      this.circuloBusquedaChange.emit(circle),
    getSeleccionadoBarraLateral: () => this.seleccionadoBarraLateral,
  };

  constructor() {
    // IMPORTANTE: Event listeners de window fuera de Angular Zone
    this.ngZone.runOutsideAngular(() => {
      // Escuchar eventos de configuración del mapa
      this._handler3d = (e: any) => this.actualizarConfiguracion3D(e.detail);
      this._handlerDiaNoche = (e: any) =>
        this.actualizarConfiguracionDiaNoche(e.detail);
      this._handlerOscuro = (e: any) => this.actualizarModoOscuroMapa(e.detail);
      this._handlerPois = (e: any) =>
        this.actualizarConfiguracionPOIs(e.detail);
      this._handlerTrafico = (e: any) => this.actualizarCapaTrafico(e.detail);

      window.addEventListener('mapa-3d-toggle', this._handler3d);
      window.addEventListener('mapa-dia-noche-toggle', this._handlerDiaNoche);
      window.addEventListener('mapa-oscuro-toggle', this._handlerOscuro);
      window.addEventListener('mapa-pois-toggle', this._handlerPois);
      window.addEventListener('mapa-traffic-toggle', this._handlerTrafico);
    });
  }

  private _handler3d!: (e: any) => void;
  private _handlerDiaNoche!: (e: any) => void;
  private _handlerOscuro!: (e: any) => void;
  private _handlerPois!: (e: any) => void;
  private _handlerTrafico!: (e: any) => void;

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // LIFECYCLE HOOKS
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  ngOnInit() {
    this.cargarColorReproduccion();

    // Cargar mapas personalizados del cliente
    if (this.clientePropio?.mapLayers) {
      this.mapasPersonalizados = this.clientePropio.mapLayers;
    }

    // Suscribirse a cambios en IDs de vehículos filtrados por búsqueda
    this.reportesFiltradosSubscription =
      this.mapaService.vehiculosFiltradosPorMapa$.subscribe(
        (vehiculosFiltradosMap) => {
          // Actualizar el Map local
          this.vehiculosFiltradosPorBusqueda = vehiculosFiltradosMap;
          // Cuando cambian los filtros, actualizar los markers de los mapas afectados
          this.mapInstances.forEach((instance, idx) => {
            this.cargarMarkersForInstance(instance, idx);
          });
        },
      );

    // Botón "dibujar" del panel de búsqueda: dibuja sobre el mapa activo
    this.dibujarCirculoSubscription =
      this.mapaService.dibujarCirculo$.subscribe(() => this.dibujar());

    // Suscribirse a pan único (desde selección de dirección/ubicación o evento)
    this.panOnceSubscription = this.mapaService.panOnce$.subscribe(
      ({ coords, radius, zoom }) => {
        const activeIndex = Math.max(0, this.mapaSeleccionado - 1);
        const instance = this.mapInstances[activeIndex];
        if (!instance?.map) return;

        if (radius && radius > 0) {
          // Zoom proporcional al radio del círculo para que se vea completo
          this.serviceDibujar.fitBoundsToCircle(instance, coords, radius);
        } else if (instance.cameraService) {
          instance.cameraService.centerOnPoint({
            coordinates: coords,
            // Una parada o un evento se miran de cerca; una dirección, no
            zoom: zoom ?? 12,
            animated: true,
            duration: 900,
          });
        }
      },
    );

    // Suscribirse a cambios en el círculo de búsqueda desde la barra lateral
    this.circuloBusquedaSubscription =
      this.mapaService.circuloBusqueda$.subscribe((circulo) => {
        const activeIndex = Math.max(0, this.mapaService.mapaSeleccionado - 1);
        const instance = this.mapInstances[activeIndex];
        if (!instance) return;

        if (circulo?.coordinates && circulo.radius > 0) {
          // Siempre guardar el dato para persistencia entre mapas
          instance.circle = circulo;
          // Solo dibujar si estamos en la combinación correcta de solapas
          // (zona + vehiculosPorZona + Dirección)
          const sel = this.mapaService.getSeleccionadoActual();
          const bz = sel?.seleccionadosVehiculoZona;
          const debeMostrar =
            sel?.tipoBusqueda === 'zona' &&
            bz?.modoBusquedaZona !== 'zonasPorVehiculo' &&
            bz?.tipoBusqueda !== 'Ubicación';
          if (debeMostrar) {
            this.serviceDibujar.updateCircleOnMap(
              instance,
              circulo.coordinates as [number, number],
              circulo.radius,
            );
          }
        } else if (!circulo?.coordinates) {
          const sel = this.mapaService.getSeleccionadoActual();
          const esResetDesdeZona = sel?.tipoBusqueda === 'zona';

          if (esResetDesdeZona) {
            // Reset explícito desde la solapa zona: limpiar todo incluso si está dibujando
            instance.circle = undefined;
            this.serviceDibujar.desactivarModoEdicionParaInstancia(instance);
          } else if (instance.dibujando) {
            // Cambio de solapa mientras dibuja: no interferir
            return;
          } else {
            // Cambio de solapa sin dibujo activo: ocultar visual, preservar instance.circle
            this.serviceDibujar.desactivarModoEdicionParaInstancia(instance);
          }

          const source = instance.map.getSource(
            instance.drawSrc,
          ) as mapboxgl.GeoJSONSource;
          if (source) {
            source.setData({ type: 'FeatureCollection', features: [] });
          }
        }
      });

    // Suscribirse a cambios en el polígono de búsqueda (ubicación seleccionada)
    this.poligonoBusquedaSubscription =
      this.mapaService.poligonoBusqueda$.subscribe((ubicacion) => {
        const activeIndex = Math.max(0, this.mapaService.mapaSeleccionado - 1);
        const instance = this.mapInstances[activeIndex];
        if (!instance) return;

        if (ubicacion?.geojson) {
          // Persistir polígono en la instancia para restaurar al cambiar de mapa
          instance.polygonUbicacion = ubicacion;

          // Dibujar el polígono real de la ubicación en el drawSrc
          const source = instance.map.getSource(
            instance.drawSrc,
          ) as mapboxgl.GeoJSONSource;
          if (source) {
            const feature: GeoJSON.Feature = {
              type: 'Feature',
              properties: {
                color: ubicacion.color || this.appliedPrimaryColor,
              },
              geometry: ubicacion.geojson as GeoJSON.Geometry,
            };
            source.setData({
              type: 'FeatureCollection',
              features: [feature],
            });
          }

          // Ajustar zoom proporcional al polígono
          const coords = ubicacion.geojson.coordinates;
          if (coords && coords.length > 0) {
            const bounds = new mapboxgl.LngLatBounds();
            if (ubicacion.geojson.type === 'Polygon') {
              (coords[0] as [number, number][]).forEach((c) =>
                bounds.extend(c),
              );
            } else if (ubicacion.geojson.type === 'Point') {
              bounds.extend(coords as [number, number]);
            }
            if (!bounds.isEmpty()) {
              instance.map.fitBounds(bounds, {
                padding: 60,
                maxZoom: 17,
                duration: 900,
              });
            }
          }
        } else {
          // Limpiar polígono persistido
          instance.polygonUbicacion = undefined;

          // Limpiar polígono del mapa, pero solo si no hay un círculo activo
          // (drawSrc es compartido entre círculos y polígonos; limpiar aquí
          // borraría un círculo que debe persistir)
          if (!instance.circle?.coordinates) {
            const source = instance.map.getSource(
              instance.drawSrc,
            ) as mapboxgl.GeoJSONSource;
            if (source) {
              source.setData({ type: 'FeatureCollection', features: [] });
            }
          }
        }
      });

    // TODO: Obtener colores del tema cuando esté disponible el método
    // Por ahora usar colores por defecto
  }

  ngAfterViewInit() {
    // Configurar token de Mapbox
    mapboxgl.accessToken = MAPBOX_TOKEN;

    // Inicializar instancias de mapa
    this.initMapInstances();

    // Suscribirse a actualizaciones en tiempo real (WebSocket)
    this.setupRealTimeUpdates();
  }

  async ngOnChanges(changes: SimpleChanges) {
    // Cambios en reportes → actualizar markers en todos los mapas
    // IMPORTANTE: También procesar firstChange para cargar markers iniciales
    if (changes['reportes']) {
      this.mapInstances.forEach((instance, idx) => {
        // Si seleccionadosGenerales también cambió en este tick y va a llamar a
        // cargarMarkersForInstance para este mapa, omitir aquí para evitar la
        // race condition con createOrUpdateSelectedHTMLMarker (async/await).
        if (
          changes['seleccionadosGenerales'] &&
          !changes['seleccionadosGenerales'].firstChange
        ) {
          const mapaNum = idx + 1;
          const prevSel = changes['seleccionadosGenerales'].previousValue?.find(
            (s: any) => s.mapaSeleccionado === mapaNum,
          );
          const currSel = changes['seleccionadosGenerales'].currentValue?.find(
            (s: any) => s.mapaSeleccionado === mapaNum,
          );
          if (this.requiereRecargaMarkers(prevSel, currSel)) {
            return; // El bloque de seleccionadosGenerales procesará este mapa
          }
        }
        // Con la animacion corriendo, rehacer la coleccion entera compite con
        // el loop del viaje y se ve como tirones. Queda anotada y se hace al
        // terminar: el polling es reconciliacion, no urgencia.
        if (instance.animating) {
          instance.recargaPendiente = true;
          return;
        }

        this.cargarMarkersForInstance(instance, idx);
      });

      // Si hay seguimiento activo, centrar en el vehículo usando servicio centralizado
      // IMPORTANTE: Si hay htmlVehicleMarker activo, él y el CameraFollowController manejan el seguimiento
      // Para vehículos NO seleccionados, se usa cameraService.centerPreservingZoom()
      this.mapInstances.forEach((mapInstance, idx) => {
        const mapaNum = idx + 1;
        const seleccionadoMapa = this.seleccionadosGenerales?.find(
          (s) => s.mapaSeleccionado === mapaNum,
        );

        if (seleccionadoMapa?.followTargetId) {
          mapInstance.siguendoA = seleccionadoMapa.followTargetId;

          // Si hay HTMLVehicleMarker activo, él y el CameraFollowController manejan todo
          if (mapInstance.htmlVehicleMarker) {
            return;
          }

          // Vehículo no seleccionado: usar servicio centralizado para preservar zoom
          const siguendo = this.reportes?.find(
            (r) =>
              r.idEntidad === mapInstance.siguendoA ||
              (Array.isArray(r.idsAsignados) &&
                r.idsAsignados.includes(mapInstance.siguendoA)),
          );
          if (siguendo && mapInstance.cameraService) {
            const valores = this.helper.getValores(siguendo);
            const posicionFollow = this.getPosicionParaMapa(valores, mapaNum);
            if (posicionFollow?.coordinates) {
              const [lng, lat] = posicionFollow.coordinates;
              mapInstance.cameraService.centerPreservingZoom([lng, lat], 800);
            } else {
              console.warn('⚠️ Vehículo sin coordenadas:', siguendo);
            }
          }
        }
      });
    }

    // Cambios en ubicaciones → actualizar polígonos
    if (changes['ubicaciones'] && !changes['ubicaciones'].firstChange) {
      this.mapInstances.forEach((instance, idx) => {
        this.serviceDibujar.addUbicacionesPolygons(
          instance,
          idx,
          this.ubicaciones,
          this.seleccionadosGenerales,
          this.appliedPrimaryColor,
        );
        this.aplicarResaltadoUbicacion(instance, idx);
      });
    }

    // Cambios en recorridos → actualizar líneas
    if (changes['recorridos'] && !changes['recorridos'].firstChange) {
      this.mapInstances.forEach((instance, idx) => {
        this.serviceDibujar.addRecorridosLines(
          instance,
          idx,
          this.recorridos,
          this.seleccionadosGenerales,
          this.appliedAccentColor,
        );
      });
    }

    // Cambios en recorridos dinámicos → actualizar círculos de paradas
    if (
      changes['recorridosDinamicos'] &&
      !changes['recorridosDinamicos'].firstChange
    ) {
      this.mapInstances.forEach((instance, idx) => {
        this.serviceDibujar.addParadasRecorrido(
          instance,
          idx,
          this.recorridosDinamicos,
          this.seleccionadosGenerales,
        );
      });
    }

    // Cambios en cantMapas → recrear instancias
    if (changes['cantMapas'] && !changes['cantMapas'].firstChange) {
      await this.recreateMapInstances();
    }

    // Cambios en reportes → actualizar seguimiento si está activo
    if (changes['reportes'] && !changes['reportes'].firstChange) {
      this.updateFollowingVehicles();
    }

    // Cambios en seleccionadosGenerales → actualizar estado de cada mapa
    if (
      changes['seleccionadosGenerales'] &&
      !changes['seleccionadosGenerales'].firstChange
    ) {
      const prevSeleccionados = changes['seleccionadosGenerales'].previousValue;
      const currentSeleccionados =
        changes['seleccionadosGenerales'].currentValue;

      this.mapInstances.forEach((instance, index) => {
        const mapaNum = index + 1;

        // Obtener seleccionado previo y actual para ESTE mapa específico
        const prevSeleccionado = prevSeleccionados?.find(
          (s: any) => s.mapaSeleccionado === mapaNum,
        );
        const seleccionadoMapa = currentSeleccionados?.find(
          (s: any) => s.mapaSeleccionado === mapaNum,
        );

        // Solo procesar si cambió ESTE mapa específico
        const mapaCambio = this.seleccionadoCambio(
          prevSeleccionado,
          seleccionadoMapa,
        );

        if (!mapaCambio) {
          return; // Saltar al siguiente mapa
        }

        this.applySearchStateToInstance(
          instance,
          index,
          seleccionadoMapa,
          prevSeleccionado,
        );
      });
    }

    // Cambios en mapaSeleccionado
    if (
      changes['mapaSeleccionado'] &&
      !changes['mapaSeleccionado'].firstChange
    ) {
      const idx = Math.max(0, this.mapaSeleccionado - 1);
      this.tipoMapaBase = this.getSavedTipoMapa(idx);

      // Desactivar modo edición/dibujo en el mapa anterior al cambiar
      const prevIdx = Math.max(
        0,
        changes['mapaSeleccionado'].previousValue - 1,
      );
      const prevInstance = this.mapInstances[prevIdx];
      if (prevInstance?.dibujando) {
        this.serviceDibujar.desactivarModoEdicionParaInstancia(prevInstance);
      }

      // Redibujar círculos y polígonos desde estado per-mapa (seleccionadosGenerales)
      // en vez de instance.circle que puede estar contaminado por emisiones globales.
      this.mapInstances.forEach((instance, idx) => {
        const mapaNum = idx + 1;
        const selMapa = this.seleccionadosGenerales?.find(
          (s) => s.mapaSeleccionado === mapaNum,
        );
        const bz = selMapa?.seleccionadosVehiculoZona;
        const esCombCirc =
          selMapa?.tipoBusqueda === 'zona' &&
          bz?.modoBusquedaZona === 'vehiculosPorZona' &&
          bz?.tipoBusqueda === 'Dirección';
        const esCombPoly =
          selMapa?.tipoBusqueda === 'zona' &&
          bz?.tipoBusqueda === 'Ubicación' &&
          !!bz?.ubicacion?.geojson;

        if (esCombCirc && bz?.circulo?.coordinates && bz.circulo.radius > 0) {
          instance.circle = bz.circulo;
          this.serviceDibujar.updateCircleOnMap(
            instance,
            bz.circulo.coordinates as [number, number],
            bz.circulo.radius,
          );
        } else if (esCombPoly && bz?.ubicacion) {
          instance.polygonUbicacion = bz.ubicacion;
          this.serviceDibujar.redrawPersistedPolygon(
            instance,
            this.appliedPrimaryColor,
          );
        } else {
          instance.circle = undefined;
          instance.polygonUbicacion = undefined;
          const drawSource = instance.map.getSource(
            instance.drawSrc,
          ) as mapboxgl.GeoJSONSource;
          if (drawSource) {
            drawSource.setData({ type: 'FeatureCollection', features: [] });
          }
        }
      });
    }
  }

  /**
   * Repinta los polígonos de ubicaciones destacando la zona seleccionada de
   * este mapa. Sin selección vuelve a los valores planos de la capa.
   */
  private aplicarResaltadoUbicacion(
    instance: MapboxMapInstance,
    index: number,
  ): void {
    const seleccionadoMapa = this.seleccionadosGenerales?.find(
      (s) => s.mapaSeleccionado === index + 1,
    );
    const idUbicacion = seleccionadoMapa?.ubicacionSeleccionada?._id;

    const [fillLayer, lineLayer] = instance.ubicacionesLayers;

    if (fillLayer && instance.map.getLayer(fillLayer)) {
      instance.map.setPaintProperty(
        fillLayer,
        'fill-opacity',
        idUbicacion
          ? (['case', ['==', ['get', 'id'], idUbicacion], 0.35, 0.08] as any)
          : 0.15,
      );
    }

    if (lineLayer && instance.map.getLayer(lineLayer)) {
      instance.map.setPaintProperty(
        lineLayer,
        'line-width',
        idUbicacion
          ? (['case', ['==', ['get', 'id'], idUbicacion], 4, 1] as any)
          : 2,
      );
    }
  }

  private applySearchStateToInstance(
    instance: MapboxMapInstance,
    index: number,
    seleccionadoMapa: SeleccionadosBarraLateral | undefined,
    prevSeleccionado: SeleccionadosBarraLateral | undefined,
    opciones?: { mantenerCamara?: boolean },
  ): void {
    const mapaNum = index + 1;

    // Resaltar la zona seleccionada (si cambió, el polígono se repinta)
    this.aplicarResaltadoUbicacion(instance, index);

    // Verificar si hay alguna búsqueda activa
    const tieneBusquedaActiva =
      seleccionadoMapa?.seleccionadosVehiculosRecorridos?.busquedaRealizada ||
      seleccionadoMapa?.seleccionadosVehiculoZona?.busquedaVehiculosPorZona
        ?.vehiculosPasos;

    const followTargetId = seleccionadoMapa?.followTargetId;
    if (followTargetId && !tieneBusquedaActiva) {
      // Solo seguir vehículo si NO hay búsqueda activa
      instance.siguendoA = followTargetId;

      // Si hay HTMLVehicleMarker activo, él y el CameraFollowController manejan todo
      if (!instance.htmlVehicleMarker && instance.cameraService) {
        // Vehículo no seleccionado: usar servicio centralizado para preservar zoom
        // Buscar reporte: primero por activo._id (usado por barra lateral), luego por idEntidad e idsAsignados
        const reporteSeguido = this.reportes?.find(
          (r) =>
            r.activo?._id === followTargetId ||
            r.idEntidad === followTargetId ||
            (Array.isArray(r.idsAsignados) &&
              r.idsAsignados.includes(followTargetId)),
        );
        if (reporteSeguido) {
          const valores = this.helper.getValores(reporteSeguido);
          const posicionSeguido = this.getPosicionParaMapa(valores, mapaNum);
          if (posicionSeguido?.coordinates) {
            const [lng, lat] = posicionSeguido.coordinates;
            instance.cameraService.centerPreservingZoom([lng, lat], 800);
          } else {
            console.warn(
              '⚠️ Reporte seguido encontrado pero sin coordenadas geojson',
            );
          }
        } else {
          console.warn(
            '⚠️ No se encontró reporte con followTargetId:',
            followTargetId,
          );
        }
      }
    } else if (instance.siguendoA) {
      instance.siguendoA = undefined;
      // Limpiar trail al dejar de seguir
      this.clearRouteHistory(index);
    }

    // CRÍTICO: Procesar búsquedas según tipoBusqueda actual para evitar que se pisen
    const tipoBusqueda = seleccionadoMapa?.tipoBusqueda;

    // Limpiar todos los elementos de búsqueda al inicio, luego dibujar solo lo que corresponda
    // Cerrar popup abierto (puede quedar flotando al cambiar de trip o tipo de búsqueda)
    if (instance.popup?.isOpen()) {
      instance.popup.remove();
    }
    this.serviceDibujar.addParadas(instance, []);
    const excesoSrcToClean = instance.map.getSource(
      instance.excesoSrc,
    ) as mapboxgl.GeoJSONSource;
    if (excesoSrcToClean)
      excesoSrcToClean.setData({ type: 'FeatureCollection', features: [] });
    this.serviceDibujar.addEventos(instance, []);
    const tripOverlaySrcToClean = instance.map.getSource(
      instance.tripOverlaySrc,
    ) as mapboxgl.GeoJSONSource;
    if (tripOverlaySrcToClean)
      tripOverlaySrcToClean.setData({
        type: 'FeatureCollection',
        features: [],
      });
    this.serviceDibujar.addPolylinesFromLineStringMapa(
      instance,
      [],
      this.appliedAccentColor,
    );

    // Paradas y eventos ahora son overlays de la búsqueda de viajes: fuera de
    // ella no hay nada que trackear.
    if (tipoBusqueda !== 'recorrido') {
      this.previousParadasByMap.delete(mapaNum);
      this.previousParadaSeleccionadaByMap.delete(mapaNum);
      this.previousEventosByMap.delete(mapaNum);
      this.previousEventoSeleccionadoByMap.delete(mapaNum);
    }

    // Procesar polylines de búsqueda Visitas (en solapa zona + tipo Visitas)
    const busquedaVisitas =
      seleccionadoMapa?.seleccionadosVehiculoZona?.busquedaVisitas;
    if (
      tipoBusqueda === 'zona' &&
      seleccionadoMapa?.seleccionadosVehiculoZona?.tipoBusqueda === 'Visitas' &&
      busquedaVisitas?.polylines?.length
    ) {
      this.serviceDibujar.addPolylinesFromLineStringMapa(
        instance,
        busquedaVisitas.polylines,
        this.appliedAccentColor,
      );
    }

    // Procesar búsqueda de recorridos si existe
    const busquedaRecorridos =
      seleccionadoMapa?.seleccionadosVehiculosRecorridos?.busquedaRealizada;
    const hayPolylinesRecorrido =
      tipoBusqueda === 'recorrido' &&
      !!busquedaRecorridos?.polylines &&
      busquedaRecorridos.polylines.length > 0;

    // Si se deja de mostrar un recorrido con viaje animable (cambio de tab
    // dentro de "Recorridos" a una búsqueda todavía sin resultados, o cambio a
    // otro tipo de búsqueda) hay que detener la animación en curso: si no,
    // CameraFollowController sigue con dragPan bloqueado indefinidamente y el
    // usuario no puede volver a mover el mapa.
    if (!hayPolylinesRecorrido && instance.animating) {
      this.stopAnimation(instance, true);
    }

    if (tipoBusqueda === 'recorrido' && busquedaRecorridos) {
      if (
        busquedaRecorridos.polylines &&
        busquedaRecorridos.polylines.length > 0
      ) {
        // La traza del viaje en curso crece con cada reporte que llega por WS:
        // es el mismo viaje, no uno nuevo. Reencuadrar o reiniciar el progreso
        // en cada mensaje le sacaría el mapa de las manos al usuario.
        const enVivo = !!busquedaRecorridos.actualizacionEnVivo;
        // Un cambio de opción del panel (traza pegada a la calle, reportes
        // snappeados) es el mismo recorrido con otra pinta: se redibuja sin
        // tocar la cámara ni el progreso de la animación. Reencuadra sólo la
        // búsqueda.
        const mantenerCamara =
          enVivo ||
          !!busquedaRecorridos.redibujoSinEncuadrar ||
          !!opciones?.mantenerCamara;

        if (!mantenerCamara) {
          // Parar animación en curso antes de cargar el nuevo viaje (reset desde cero)
          if (instance.animating) {
            this.stopAnimation(instance, true);
          } else {
            // Si no está animando pero hay un viaje previo, también resetear el progreso
            instance.distance = 0;
            instance.tripCurrentPoint = undefined;
          }
        }
        this.serviceDibujar.addPolylinesFromLineStringMapa(
          instance,
          busquedaRecorridos.polylines,
          this.appliedAccentColor,
          { encuadrar: !mantenerCamara },
        );

        // Las horas que muestra el reproductor son las del viaje elegido en el
        // panel, así las dos pantallas dicen lo mismo.
        instance.tripFechaInicio = this.aEpoch(
          busquedaRecorridos.selected?.startTime ??
            busquedaRecorridos.lastTripStartTime,
        );
        instance.tripFechaFin = this.aEpoch(
          busquedaRecorridos.selected?.endTime ??
            busquedaRecorridos.lastTripEndTime,
        );

        // La traza es otra (viaje nuevo, o uno en vivo que creció con el último
        // reporte): el largo y la línea de tiempo se rehacen contra ella. Se
        // rehacen acá y no cuando hagan falta porque el loop de la animación
        // mide el avance contra el largo en cada cuadro.
        instance.tripTotalLength = undefined;
        this.asegurarLargoViaje(instance);
        // Después del render: las etiquetas del reproductor recién existen
        // cuando el panel se dibuja con la búsqueda ya cargada.
        setTimeout(() => this.pintarProgreso(instance));
      }

      // Overlays del viaje: paradas → paradasSrc, exceso → excesoSrc, eventos → eventosSrc
      // Se resuelven solapamientos entre tipos distintos desplazando radialmente los puntos
      const paradasData = busquedaRecorridos.paradasEnViaje ?? [];
      const excesoData = busquedaRecorridos.puntosExcesoVelocidad ?? [];
      const eventosData = (busquedaRecorridos.eventosEnViaje ?? []).filter(
        (ev) => {
          const v = ev.valores as IValoresEventoTracker;
          return v?.geojson?.coordinates?.length >= 2;
        },
      );

      // Resolver solapamientos entre distintos tipos antes de añadir a la source unificada
      const resolvedCoords = this.serviceDibujar.resolveOverlapOffsets({
        parada: paradasData.map((p) => [p.longitude, p.latitude]),
        exceso: excesoData.map((p) => p.coordinate),
        evento: eventosData.map(
          (ev) =>
            (ev.valores as IValoresEventoTracker).geojson.coordinates as [
              number,
              number,
            ],
        ),
      });

      const tripOverlayFeatures: GeoJSON.Feature[] = [
        ...paradasData.map((parada, i) => ({
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: resolvedCoords.parada[i],
          },
          properties: {
            tipo: 'parada',
            radius: RADIO_MARKER_PARADA,
            paradaData: JSON.stringify(parada),
          },
        })),
        ...excesoData.map((punto, i) => ({
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: resolvedCoords.exceso[i],
          },
          properties: {
            tipo: 'exceso-velocidad',
            radius: RADIO_MARKER_EXCESO,
            excesoData: JSON.stringify(punto),
          },
        })),
        ...eventosData.map((evento, i) => ({
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: resolvedCoords.evento[i],
          },
          properties: {
            tipo: 'evento',
            radius: RADIO_MARKER_EVENTO,
            eventoData: JSON.stringify(evento),
          },
        })),
      ];

      const tripOverlaySource = instance.map.getSource(
        instance.tripOverlaySrc,
      ) as mapboxgl.GeoJSONSource;
      if (tripOverlaySource) {
        tripOverlaySource.setData({
          type: 'FeatureCollection',
          features: tripOverlayFeatures,
        });
      }
    }

    // Desactivar modo dibujo/edición cuando puedeDibujar es false.
    // La condición NO depende de prevSeleccionado para cubrir el caso
    // donde no existía estado previo y dibujando quedaba true indefinidamente
    // (bug: primera vez que se cambiaba de solapa zona a otra, el círculo
    // permanecía visible porque dibujando=true impedía la limpieza).
    if (instance.dibujando && !seleccionadoMapa?.puedeDibujar) {
      this.serviceDibujar.desactivarModoEdicionParaInstancia(instance);
    }

    // Limpiar dibujo de círculo/polígono de búsqueda al inicio,
    // análogo a cómo se limpian polylines, paradas y eventos arriba.
    // Luego se redibuja solo lo que corresponda según la solapa activa.
    // No limpiar si el usuario está activamente dibujando/editando.
    if (!instance.dibujando) {
      const drawSource = instance.map.getSource(
        instance.drawSrc,
      ) as mapboxgl.GeoJSONSource;
      if (drawSource) {
        drawSource.setData({ type: 'FeatureCollection', features: [] });
      }
    }

    // Zona: restaurar círculo o polígono según combinación de solapas activa
    // El círculo SOLO debe verse en: zona + vehiculosPorZona + Dirección
    const busquedaZona = seleccionadoMapa?.seleccionadosVehiculoZona;
    const esCombinacionCirculo =
      tipoBusqueda === 'zona' &&
      busquedaZona?.modoBusquedaZona === 'vehiculosPorZona' &&
      busquedaZona?.tipoBusqueda === 'Dirección';
    const esCombinacionPoligono =
      tipoBusqueda === 'zona' &&
      (busquedaZona?.tipoBusqueda === 'Ubicación' ||
        busquedaZona?.tipoBusqueda === 'Visitas') &&
      !!busquedaZona?.ubicacion?.geojson;

    if (esCombinacionCirculo && busquedaZona) {
      // Restaurar círculo desde el campo circulo del estado
      if (
        busquedaZona.circulo?.coordinates &&
        busquedaZona.circulo.radius > 0
      ) {
        const circulo = busquedaZona.circulo;
        instance.circle = circulo;
        instance.polygonUbicacion = undefined;
        this.serviceDibujar.updateCircleOnMap(
          instance,
          circulo.coordinates as [number, number],
          circulo.radius,
        );
      } else if (instance.circle?.coordinates && instance.circle.radius > 0) {
        // Fallback: círculo dibujado manualmente sin datos en estado
        this.serviceDibujar.updateCircleOnMap(
          instance,
          instance.circle.coordinates as [number, number],
          instance.circle.radius,
        );
      }
    } else if (esCombinacionPoligono && busquedaZona) {
      // Restaurar polígono de ubicación
      instance.polygonUbicacion = busquedaZona.ubicacion;
      this.serviceDibujar.redrawPersistedPolygon(
        instance,
        this.appliedPrimaryColor,
      );
    }
    // No necesita rama else: la limpieza ya se hizo arriba

    // Aplicar cambios según tipo de modificación
    if (this.cambioPitchAlignment(prevSeleccionado, seleccionadoMapa)) {
      this.actualizarPitchAlignment(
        instance,
        seleccionadoMapa?.iconPitchAlignment,
      );
    }

    // NUEVO: Detectar cambio en seguimientoActivo y actualizar inmediatamente
    if (
      prevSeleccionado?.seguimientoActivo !==
      seleccionadoMapa?.seguimientoActivo
    ) {
      const followCamera = !!seleccionadoMapa?.seguimientoActivo;

      // Actualizar seguimiento de cámara usando servicio centralizado
      if (instance.cameraService && instance.htmlVehicleMarker) {
        if (followCamera) {
          // Iniciar seguimiento con throttle para change detection
          let lastChangeDetection = 0;
          const onPositionUpdate = () => {
            const now = Date.now();
            if (now - lastChangeDetection > 500) {
              lastChangeDetection = now;
              this.ngZone.run(() => {});
            }
          };

          instance.cameraService.followTarget(
            () => instance.htmlVehicleMarker!.getPosition(),
            onPositionUpdate,
          );

          // Iniciar trail con la posición actual del vehículo
          const pos = instance.htmlVehicleMarker.getPosition();
          if (pos) {
            this.clearRouteHistory(index);
            this.updateRouteHistory(index, [[pos.lng, pos.lat]]);
          }
        } else {
          instance.cameraService.stopFollow();
          // Limpiar trail al desactivar seguimiento
          this.clearRouteHistory(index);
        }
      } else if (!followCamera) {
        // Limpiar trail también si no hay htmlVehicleMarker
        this.clearRouteHistory(index);
      }
    }

    if (this.requiereRecargaMarkers(prevSeleccionado, seleccionadoMapa)) {
      this.cargarMarkersForInstance(instance, index);
    } else if (
      prevSeleccionado?.reporteSeleccionado?.activo?._id !==
      seleccionadoMapa?.reporteSeleccionado?.activo?._id
    ) {
      void this.aplicarCambioDeSeleccion(instance, index);
    }

    // Actualizar ubicaciones si cambió mostrarUbicaciones, mostrarNombreUbicaciones
    // o el filtro de ubicaciones (incluido el de clientes).
    if (
      prevSeleccionado?.mostrarUbicaciones !==
        seleccionadoMapa?.mostrarUbicaciones ||
      prevSeleccionado?.mostrarNombreUbicaciones !==
        seleccionadoMapa?.mostrarNombreUbicaciones ||
      this.firmaFiltrosUbicaciones(prevSeleccionado) !==
        this.firmaFiltrosUbicaciones(seleccionadoMapa)
    ) {
      this.serviceDibujar.addUbicacionesPolygons(
        instance,
        index,
        this.ubicaciones,
        this.seleccionadosGenerales,
        this.appliedPrimaryColor,
      );
    }

    // Actualizar recorridos si cambió mostrarRecorridos o mostrarNombreRecorridos.
    // En modo `seleccionado` también hay que redibujar al cambiar de vehículo o de
    // recorrido asignado, porque de eso depende cuál traza se muestra.
    const cambioVehiculoSeleccionado =
      prevSeleccionado?.reporteSeleccionado?.activo?._id !==
        seleccionadoMapa?.reporteSeleccionado?.activo?._id ||
      prevSeleccionado?.idRecorridoAsignado !==
        seleccionadoMapa?.idRecorridoAsignado;
    if (
      prevSeleccionado?.mostrarRecorridos !==
        seleccionadoMapa?.mostrarRecorridos ||
      prevSeleccionado?.mostrarNombreRecorridos !==
        seleccionadoMapa?.mostrarNombreRecorridos ||
      this.firmaFiltrosRecorridos(prevSeleccionado) !==
        this.firmaFiltrosRecorridos(seleccionadoMapa) ||
      (seleccionadoMapa?.mostrarRecorridos === 'seleccionado' &&
        cambioVehiculoSeleccionado)
    ) {
      this.serviceDibujar.addRecorridosLines(
        instance,
        index,
        this.recorridos,
        this.seleccionadosGenerales,
        this.appliedAccentColor,
      );
    }

    // El avance medido del recorrido cambia con cada reporte del vehículo
    // seleccionado, así que se repinta cuando cambia el snapshot.
    if (
      prevSeleccionado?.cumplimientoRecorrido !==
        seleccionadoMapa?.cumplimientoRecorrido ||
      prevSeleccionado?.idRecorridoAsignado !==
        seleccionadoMapa?.idRecorridoAsignado
    ) {
      this.serviceDibujar.addCumplimientoRecorrido(
        instance,
        index,
        this.recorridos,
        this.seleccionadosGenerales,
        this.appliedAccentColor,
      );
    }
    if (
      prevSeleccionado?.idRecorridoDinamicoAsignado !==
      seleccionadoMapa?.idRecorridoDinamicoAsignado
    ) {
      this.serviceDibujar.addParadasRecorrido(
        instance,
        index,
        this.recorridosDinamicos,
        this.seleccionadosGenerales,
      );
    }
  }

  ngOnDestroy() {
    // Remover event listeners de window
    window.removeEventListener('mapa-3d-toggle', this._handler3d);
    window.removeEventListener('mapa-dia-noche-toggle', this._handlerDiaNoche);
    window.removeEventListener('mapa-oscuro-toggle', this._handlerOscuro);
    window.removeEventListener('mapa-pois-toggle', this._handlerPois);
    window.removeEventListener('mapa-traffic-toggle', this._handlerTrafico);

    // Limpiar intervalo de refresco de tráfico
    if (this.trafficRefreshInterval) {
      clearInterval(this.trafficRefreshInterval);
      this.trafficRefreshInterval = null;
    }

    // Limpiar trails de seguimiento
    this.routeHistory.clear();

    // Cancelar reconstrucciones pendientes de cambios de capa base
    Object.values(this.estiloListoCleanupPorMapa).forEach((cleanup) =>
      cleanup(),
    );
    this.estiloListoCleanupPorMapa = {};

    // Desconectar observers de resize
    this.resizeObservers.forEach((obs) => obs.disconnect());
    this.resizeObservers = [];

    // Cancelar suscripción a actualizaciones en tiempo real
    if (this.realTimeSubscription) {
      this.realTimeSubscription.unsubscribe();
    }
    if (this.realTimeBatchSubscription) {
      this.realTimeBatchSubscription.unsubscribe();
    }

    // Cancelar suscripción a reportes filtrados
    if (this.reportesFiltradosSubscription) {
      this.reportesFiltradosSubscription.unsubscribe();
    }

    // Cancelar suscripción a pan único
    if (this.panOnceSubscription) {
      this.panOnceSubscription.unsubscribe();
    }
    if (this.dibujarCirculoSubscription) {
      this.dibujarCirculoSubscription.unsubscribe();
    }

    // Cancelar suscripción a círculo de búsqueda
    if (this.circuloBusquedaSubscription) {
      this.circuloBusquedaSubscription.unsubscribe();
    }

    // Cancelar suscripción a polígono de búsqueda
    if (this.poligonoBusquedaSubscription) {
      this.poligonoBusquedaSubscription.unsubscribe();
    }

    // Limpiar todos los mapas
    this.mapInstances.forEach((instance) => {
      // Cancelar animaciones de trip
      if (instance.animationFrame) {
        cancelAnimationFrame(instance.animationFrame);
      }
      // Cancelar flush pendiente de updates de vehículos
      if (instance.vehiculoUpdateTimer !== undefined) {
        clearTimeout(instance.vehiculoUpdateTimer);
        instance.vehiculoUpdateTimer = undefined;
      }
      // Cancelar animación de flujo de la traza (requestAnimationFrame infinito)
      if (instance.flujoFrame !== undefined) {
        cancelAnimationFrame(instance.flujoFrame);
        instance.flujoFrame = undefined;
      }
      // Destruir camera service si existe
      if (instance.cameraService) {
        instance.cameraService.destroy();
      }
      // Destruir HTML vehicle marker si existe
      if (instance.htmlVehicleMarker) {
        instance.htmlVehicleMarker.destroy();
      }
      // Remover popup
      if (instance.popup) {
        instance.popup.remove();
      }
      // Limpiar imágenes compuestas del mapa
      this.vehicleCompositeIconService.cleanupMap(instance.map);
      // Destruir mapa
      instance.map.remove();
    });
    this.mapInstances = [];
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // INICIALIZACIÓN DE MAPAS
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private async initMapInstances() {
    // Cargar seleccionados desde sessionStorage si no están disponibles
    if (!this.seleccionadosGenerales) {
      const seleccionadosGuardados =
        this.serviceStorage.loadSeleccionadosFromSessionStorage(
          this.SESSION_STORAGE_KEY,
        );
      if (seleccionadosGuardados) {
        this.seleccionadosGenerales = seleccionadosGuardados;
      }
    }

    // Preparar tipoMapaBase para cada índice (sincrónico, antes de crear mapas)
    for (let i = 0; i < this.cantMapas; i++) {
      const key = this.getInstanceStorageKey(i);
      const stored = localStorage.getItem(key);
      this.tipoMapaBaseByIndex[i] =
        (stored as TipoMapaBase) || this.defaultTipoMapaBase;
    }

    // Crear todas las instancias en paralelo para minimizar el tiempo de espera total.
    // Cada instancia espera el evento 'load' de Mapbox de forma independiente.
    const instances = await Promise.all(
      Array.from({ length: this.cantMapas }, (_, i) =>
        this.createMapInstance(i),
      ),
    );
    this.mapInstances = instances;

    // Observar cambios de tamaño del contenedor (ej: sidenav toggle)
    // para que map.resize() mantenga el canvas actualizado y el CameraFollowController
    // pueda re-centrar correctamente el vehículo seguido.
    // Se usa debounce para evitar el flash negro durante la animación del sidenav.
    for (let i = 0; i < this.cantMapas; i++) {
      const mapInstance = instances[i];
      const container = document.getElementById(`map${i}`);
      if (container) {
        let resizeDebounce: ReturnType<typeof setTimeout> | undefined;
        const observer = new ResizeObserver(() => {
          this.ngZone.runOutsideAngular(() => {
            if (resizeDebounce !== undefined) {
              clearTimeout(resizeDebounce);
            }
            resizeDebounce = setTimeout(() => {
              mapInstance.map.resize();
              resizeDebounce = undefined;
            }, 50);
          });
        });
        observer.observe(container);
        this.resizeObservers.push(observer);
      }
    }

    // Actualizar tipoMapaBase del mapa seleccionado
    this.tipoMapaBase = this.getSavedTipoMapa(
      Math.max(0, this.mapaSeleccionado - 1),
    );

    this.restaurarBusquedasEnInstancias();
  }

  /**
   * Redibuja en cada mapa la búsqueda que se estaba viendo. El estado vive en
   * sessionStorage y sobrevive a la recarga de la pantalla, pero los sources de
   * Mapbox nacen vacíos: sin esto, al recargar volvían los controles de la
   * búsqueda pero no el recorrido debajo.
   *
   * `ngOnChanges` no alcanza: `seleccionadosGenerales` no cambia como @Input,
   * llega con el primer valor o se lee de sessionStorage acá mismo.
   */
  private restaurarBusquedasEnInstancias(): void {
    // Los mapas se crean fuera de la zona de Angular, así que la promesa que
    // los espera también resuelve afuera. El reproductor del viaje aparece
    // cuando Angular ve la traza dibujada, y sin volver a la zona no la ve.
    this.ngZone.run(() => {
      this.mapInstances.forEach((instance, index) => {
        const seleccionadoMapa = this.seleccionadosGenerales?.find(
          (s) => s.mapaSeleccionado === index + 1,
        );
        if (!seleccionadoMapa) return;

        this.applySearchStateToInstance(
          instance,
          index,
          seleccionadoMapa,
          undefined,
        );
      });
    });
  }

  private async createMapInstance(index: number): Promise<MapboxMapInstance> {
    // Restaurar estado guardado
    const savedCenter = this.getSavedCenter(index);
    const savedZoom = this.getSavedZoom(index);
    const tipoMapa =
      this.tipoMapaBaseByIndex[index] || this.defaultTipoMapaBase;

    // CRÍTICO: Crear mapa FUERA de Angular Zone para evitar que Mapbox dispare change detection
    // El loop de renderizado interno de Mapbox usa requestAnimationFrame, MessageChannel y Promises
    // que quedarían patcheados por Zone.js si se crean dentro de Angular Zone
    let map: mapboxgl.Map;
    this.ngZone.runOutsideAngular(() => {
      // Crear mapa de Mapbox
      map = new mapboxgl.Map({
        language: 'es', // Etiquetas del mapa base en español
        container: `map${index}`,
        style: this.getMapboxStyle(tipoMapa),
        center: savedCenter || [-58.443832, -34.587386], // Buenos Aires default
        zoom: savedZoom || 15,
        // PERF: sin fade de símbolos (default 300ms). Con updates frecuentes del
        // source de vehículos, el fade mantenía el render loop de Mapbox activo
        // de forma continua (rAF/compositor cada frame). Con 0, cada updateData
        // renderiza una vez y el mapa vuelve a idle.
        fadeDuration: 0,
        transformRequest: (url: string, resourceType: string) => {
          // Para tiles de servidores externos sin CORS, forzar omit
          if (
            resourceType === 'Tile' &&
            !url.startsWith(window.location.origin)
          ) {
            return {
              url: url,
              credentials: 'omit' as any, // Force omit para servidores sin CORS
            };
          }
          return { url };
        },
      });
      aplicarEtiquetasMapa(map);

      // Agregar controles de navegación
      map.addControl(
        new mapboxgl.NavigationControl({
          showCompass: true,
          showZoom: true,
          visualizePitch: true,
        }),
        'bottom-right',
      );
    });

    // IMPORTANTE: Configurar ciclo día/noche para Mapbox Standard cuando el estilo se carga
    // Ejecutar fuera de Angular Zone
    this.ngZone.runOutsideAngular(() => {
      map.on('style.load', () => {
        // Eliminar el fog atmosférico del estilo (ej. mapbox/standard) para evitar
        // que los HTML markers aparezcan semitransparentes según su posición
        map.setFog(null as any);
        // El tipo vigente, no el de cuando se creó el mapa
        this.configurarCicloDiaNoche(
          map,
          this.tipoMapaBaseByIndex[index] ?? tipoMapa,
        );
      });
    });

    // IDs para sources y layers (únicas por mapa)
    const srcPrefix = `map${index}`;

    // Crear popup para puntos del recorrido
    const popup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: false,
      maxWidth: '300px',
      className: 'dark-popup',
      anchor: 'bottom',
      offset: 14,
    });

    // Crear estructura de instancia
    const instance: MapboxMapInstance = {
      map,
      container: `map${index}`,
      currentZoom: savedZoom || 15,
      popup, // Agregar popup a la instancia

      // Sources
      vehiculosSrc: `${srcPrefix}-vehiculos`,
      ubicacionesSrc: `${srcPrefix}-ubicaciones`,
      recorridosSrc: `${srcPrefix}-recorridos`,
      cumplimientoSrc: `${srcPrefix}-cumplimiento`,
      recorridoParadasSrc: `${srcPrefix}-recorrido-paradas`,
      polylinesSrc: `${srcPrefix}-polylines`,
      drawSrc: `${srcPrefix}-draw`,
      paradasSrc: `${srcPrefix}-paradas`,
      excesoSrc: `${srcPrefix}-exceso`,
      eventosSrc: `${srcPrefix}-eventos`,
      pointsSrc: `${srcPrefix}-points`,
      tripOverlaySrc: `${srcPrefix}-trip-overlay`,

      // Layers (se poblarán cuando se agreguen las capas)
      vehiculosLayers: [],
      ubicacionesLayers: [],
      recorridosLayers: [],
      cumplimientoLayers: [],
      recorridoParadasLayers: [],
      polylinesLayers: [],
      drawLayers: [],
      paradasLayers: [],
      excesoLayers: [],
      eventosLayers: [],
      pointsLayers: [],
      tripLayers: [],
      tripOverlayLayers: [],
    };

    // NUEVO: Crear servicio centralizado de control de cámara
    instance.cameraService = new MapCameraService(map, this.ngZone);
    // Esperar a que el mapa esté cargado
    // Ejecutar fuera de Angular Zone
    await new Promise<void>((resolve) => {
      this.ngZone.runOutsideAngular(() => {
        map.on('load', async () => {
          // IMPORTANTE: Esperar a que las capas se creen antes de registrar eventos
          await this.setupMapLayers(instance, index);
          this.setupMapEvents(instance, index);

          // Volver a entrar en Angular Zone para resolver promesa
          this.ngZone.run(() => {
            // CRÍTICO: Cargar datos iniciales si ya están disponibles
            // (importante para navegación desde otra vista donde el servicio ya tiene datos)

            // Intentar cargar markers si hay reportes
            if (this.reportes && this.reportes.length > 0) {
              this.cargarMarkersForInstance(instance, index);
            }

            // Intentar cargar ubicaciones si existen
            if (this.ubicaciones && this.ubicaciones.length > 0) {
              this.serviceDibujar.addUbicacionesPolygons(
                instance,
                index,
                this.ubicaciones,
                this.seleccionadosGenerales,
                this.appliedPrimaryColor,
              );
              this.aplicarResaltadoUbicacion(instance, index);
            }

            // Intentar cargar recorridos si existen
            if (this.recorridos && this.recorridos.length > 0) {
              this.serviceDibujar.addRecorridosLines(
                instance,
                index,
                this.recorridos,
                this.seleccionadosGenerales,
                this.appliedAccentColor,
              );
              this.serviceDibujar.addCumplimientoRecorrido(
                instance,
                index,
                this.recorridos,
                this.seleccionadosGenerales,
                this.appliedAccentColor,
              );
            }
            if (
              this.recorridosDinamicos &&
              this.recorridosDinamicos.length > 0
            ) {
              this.serviceDibujar.addParadasRecorrido(
                instance,
                index,
                this.recorridosDinamicos,
                this.seleccionadosGenerales,
              );
            }

            resolve();
          });
        });
      });
    });

    return instance;
  }

  private async loadVehicleImages(map: mapboxgl.Map): Promise<void> {
    // Imágenes base (círculos, brújula, centro)
    const baseImages = [
      { id: 'circle-white', url: 'assets/map/vehiculos/circle branco.png' },
      { id: 'center-white', url: 'assets/map/vehiculos/centro branco.png' },
      { id: 'compass-white', url: 'assets/map/vehiculos/brujula branca.png' },
      {
        id: 'vehicle-default',
        url: 'assets/map/vehiculos/vehiculo branco.png',
      },
    ];

    // Íconos por tipo de vehículo
    const tipoVehiculoIcons = [
      {
        id: 'tipo-auto',
        url: 'assets/map/vehiculos/Markers Default/Tipo/auto.png',
      },
      {
        id: 'tipo-camion',
        url: 'assets/map/vehiculos/Markers Default/Tipo/camion.png',
      },
      {
        id: 'tipo-camioneta',
        url: 'assets/map/vehiculos/Markers Default/Tipo/camioneta.png',
      },
      {
        id: 'tipo-colectivo',
        url: 'assets/map/vehiculos/Markers Default/Tipo/colectivo.png',
      },
      {
        id: 'tipo-grua',
        url: 'assets/map/vehiculos/Markers Default/Tipo/grua.png',
      },
      {
        id: 'tipo-moto',
        url: 'assets/map/vehiculos/Markers Default/Tipo/moto.png',
      },
      {
        id: 'tipo-otro',
        url: 'assets/map/vehiculos/Markers Default/Tipo/otro.png',
      },
    ];

    // Íconos por función de vehículo
    const funcionVehiculoIcons = [
      {
        id: 'funcion-ambulancia',
        url: 'assets/map/vehiculos/Markers Default/Funcion/ambulancia.png',
      },
      {
        id: 'funcion-bomberos',
        url: 'assets/map/vehiculos/Markers Default/Funcion/bomberos.png',
      },
      {
        id: 'funcion-mantenimiento',
        url: 'assets/map/vehiculos/Markers Default/Funcion/mantenimiento.png',
      },
      {
        id: 'funcion-otro',
        url: 'assets/map/vehiculos/Markers Default/Funcion/otro.png',
      },
      {
        id: 'funcion-particular',
        url: 'assets/map/vehiculos/Markers Default/Funcion/particular.png',
      },
      {
        id: 'funcion-policia',
        url: 'assets/map/vehiculos/Markers Default/Funcion/policia.png',
      },
      {
        id: 'funcion-seguridad privada',
        url: 'assets/map/vehiculos/Markers Default/Funcion/seguridad privada.png',
      },
      {
        id: 'funcion-servicio tecnico',
        url: 'assets/map/vehiculos/Markers Default/Funcion/servicio tecnico.png',
      },
      {
        id: 'funcion-transporte',
        url: 'assets/map/vehiculos/Markers Default/Funcion/transporte.png',
      },
    ];

    // Combinar todas las imágenes
    const allImages = [
      ...baseImages,
      ...tipoVehiculoIcons,
      ...funcionVehiculoIcons,
    ];

    const loadPromises = allImages.map(({ id, url }) => {
      return new Promise<void>((resolve) => {
        // Verificar si la imagen ya está cargada
        if (map.hasImage(id)) {
          resolve();
          return;
        }

        map.loadImage(url, (error, image) => {
          if (error) {
            console.warn(
              `⚠️ No se pudo cargar imagen ${id} desde ${url}:`,
              error,
            );
            resolve(); // No rechazar, solo advertir
            return;
          }
          // Volver a mirar hasImage: entre el chequeo de arriba y este callback
          // otra carga pudo agregar la misma imagen ("already exists").
          if (image && !map.hasImage(id)) {
            map.addImage(id, image, { sdf: true }); // sdf: true permite recolorear
          }
          resolve();
        });
      });
    });

    await Promise.all(loadPromises);
  }

  private loadArrowIcon(map: mapboxgl.Map): void {
    // Verificar si ya existe
    if (map.hasImage('arrow')) {
      return;
    }

    // Crear un canvas para dibujar la flecha
    const size = 50;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    // Dibujar flecha apuntando hacia arriba (dirección del recorrido)
    ctx.fillStyle = 'white';
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Triángulo de flecha apuntando hacia arriba
    ctx.beginPath();
    ctx.moveTo(size * 0.3, size * 0.75); // Punto inferior izquierdo
    ctx.lineTo(size * 0.5, size * 0.25); // Punta arriba (centro)
    ctx.lineTo(size * 0.7, size * 0.75); // Punto inferior derecho
    ctx.closePath();
    ctx.fill();

    // Agregar la imagen al mapa con SDF para poder recolorear
    const imageData = ctx.getImageData(0, 0, size, size);
    map.addImage('arrow', imageData, { sdf: true });
  }

  private loadFlagIcon(map: mapboxgl.Map): void {
    // Verificar si ya existe
    if (map.hasImage('flag')) {
      return;
    }

    // Crear un canvas para dibujar la bandera de inicio
    // El punto de anclaje estará en el centro-bottom (base del palo)
    const size = 60;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    ctx.fillStyle = 'white';
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;

    // Dibujar todo en la parte superior del canvas
    // El palo empieza desde arriba y termina en el centro-bottom (punto de anclaje)
    const poleWidth = size * 0.05;
    const poleHeight = size * 0.85; // 85% de altura
    const poleX = size / 2 - poleWidth / 2; // Centrado horizontalmente
    const poleTop = size * 0.05; // Empieza 5% desde arriba

    // Palo de la bandera (vertical, centrado)
    ctx.fillRect(poleX, poleTop, poleWidth, poleHeight);

    // Bandera triangular (sale desde el tope del palo)
    const flagTop = poleTop;
    const flagHeight = size * 0.3;
    const flagWidth = size * 0.4;

    ctx.beginPath();
    ctx.moveTo(poleX + poleWidth, flagTop); // Base del triángulo en el palo
    ctx.lineTo(poleX + poleWidth + flagWidth, flagTop + flagHeight / 2); // Punta derecha
    ctx.lineTo(poleX + poleWidth, flagTop + flagHeight); // Base inferior
    ctx.closePath();
    ctx.fill();

    // Agregar la imagen al mapa con SDF para poder recolorear
    const imageData = ctx.getImageData(0, 0, size, size);
    map.addImage('flag', imageData, { sdf: true });
  }

  private loadCheckeredFlagIcon(map: mapboxgl.Map): void {
    // Verificar si ya existe
    if (map.hasImage('checkered-flag')) {
      return;
    }

    // Crear un canvas para dibujar la bandera a cuadros (meta)
    // El punto de anclaje estará en el centro-bottom (base del palo)
    const size = 60;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;

    // Dibujar todo en la parte superior del canvas
    const poleWidth = size * 0.05;
    const poleHeight = size * 0.85; // 85% de altura
    const poleX = size / 2 - poleWidth / 2; // Centrado horizontalmente
    const poleTop = size * 0.05; // Empieza 5% desde arriba

    // Palo de la bandera (vertical, centrado)
    ctx.fillStyle = 'white';
    ctx.fillRect(poleX, poleTop, poleWidth, poleHeight);

    // Bandera rectangular con patrón a cuadros
    const flagLeft = poleX + poleWidth;
    const flagTop = poleTop;
    const flagWidth = size * 0.45;
    const flagHeight = size * 0.3;
    const squareSize = flagWidth / 4;

    // Dibujar cuadros blancos (patrón a cuadros)
    ctx.fillStyle = 'white';
    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 4; col++) {
        if ((row + col) % 2 === 0) {
          ctx.fillRect(
            flagLeft + col * squareSize,
            flagTop + row * (flagHeight / 2),
            squareSize,
            flagHeight / 2,
          );
        }
      }
    }

    // Agregar la imagen al mapa con SDF para poder recolorear
    const imageData = ctx.getImageData(0, 0, size, size);
    map.addImage('checkered-flag', imageData, { sdf: true });
  }

  /**
   * Crea sources y layers propios del mapa. Devuelve false si no hizo nada
   * porque la carga quedó obsoleta (`vigente`, ver `aplicarEstiloBase`).
   *
   * Es idempotente: si el source de vehículos ya está en el estilo actual, las
   * capas ya existen y no se vuelven a agregar (antes tiraba "There is already a
   * source with ID" y abortaba a mitad de camino).
   */
  private async setupMapLayers(
    instance: MapboxMapInstance,
    index: number,
    vigente: () => boolean = () => true,
  ): Promise<boolean> {
    // Obtener configuración para este mapa
    const selectionForInstance = this.seleccionadosGenerales?.find(
      (s) => s.mapaSeleccionado === index + 1,
    );

    // Cargar imágenes de vehículos primero
    await this.loadVehicleImages(instance.map);

    // Tras el await pudo entrar otro cambio de capa base: no tocar un estilo
    // que ya no es el vigente.
    if (!vigente()) return false;
    if (instance.map.getSource(instance.vehiculosSrc)) return true;

    // El estilo nuevo no tiene ninguna capa propia: vaciar los ids que se
    // acumulaban de estilos anteriores.
    instance.vehiculosLayers = [];
    instance.ubicacionesLayers = [];
    instance.recorridosLayers = [];
    instance.cumplimientoLayers = [];
    instance.recorridoParadasLayers = [];
    instance.polylinesLayers = [];
    instance.drawLayers = [];
    instance.paradasLayers = [];
    instance.excesoLayers = [];
    instance.eventosLayers = [];
    instance.pointsLayers = [];
    instance.tripOverlayLayers = [];

    // Cargar ícono de flecha para recorridos
    this.loadArrowIcon(instance.map);

    // Cargar íconos de inicio y fin de recorrido
    this.loadFlagIcon(instance.map);
    this.loadCheckeredFlagIcon(instance.map);

    // ===== SOURCES =====

    // Source para vehículos
    // dynamic: true habilita updateData(diff) → update incremental por feature.id
    // (mueve/recolorea solo el vehículo del mensaje WS, sin reconstruir toda la colección).
    instance.map.addSource(instance.vehiculosSrc, {
      type: 'geojson',
      dynamic: true,
      data: { type: 'FeatureCollection', features: [] },
    } as mapboxgl.GeoJSONSourceSpecification);

    // Source para ubicaciones (polígonos)
    instance.map.addSource(instance.ubicacionesSrc, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    // Source para recorridos (líneas)
    instance.map.addSource(instance.recorridosSrc, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    // Source para la traza del recorrido asignado, partida en tramos cubiertos y
    // pendientes según el avance medido del vehículo seleccionado.
    instance.map.addSource(instance.cumplimientoSrc, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    // Source para las paradas del recorrido asignado, cuando es de puntos
    // (tipoMedicion:'puntos') — para saber a qué destinos tiene que ir el
    // vehículo, no solo la línea.
    instance.map.addSource(instance.recorridoParadasSrc, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    // Source para polylines de búsqueda
    instance.map.addSource(instance.polylinesSrc, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    // Source para círculos de dibujo
    instance.map.addSource(instance.drawSrc, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    // Source para paradas/detenido (con clustering, color celeste)
    instance.map.addSource(instance.paradasSrc, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: true,
      clusterMaxZoom: 15,
      clusterRadius: 28,
    });

    // Source para exceso de velocidad (con clustering, color rojo)
    instance.map.addSource(instance.excesoSrc, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: true,
      clusterMaxZoom: 15,
      clusterRadius: 28,
    });

    // Source para eventos (con clustering, color violeta)
    instance.map.addSource(instance.eventosSrc, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: true,
      clusterMaxZoom: 15,
      clusterRadius: 28,
    });

    // Source para puntos interactivos
    instance.map.addSource(instance.pointsSrc, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    // ===== LAYERS (en orden de renderizado: de atrás hacia adelante) =====

    // 1. Ubicaciones - Fill
    const ubicacionesFillLayer = `${instance.ubicacionesSrc}-fill`;
    instance.map.addLayer({
      id: ubicacionesFillLayer,
      type: 'fill',
      source: instance.ubicacionesSrc,
      paint: {
        'fill-color': ['coalesce', ['get', 'color'], this.appliedPrimaryColor],
        'fill-opacity': 0.15,
      },
    });
    instance.ubicacionesLayers.push(ubicacionesFillLayer);

    // 2. Ubicaciones - Outline
    const ubicacionesLineLayer = `${instance.ubicacionesSrc}-line`;
    instance.map.addLayer({
      id: ubicacionesLineLayer,
      type: 'line',
      source: instance.ubicacionesSrc,
      paint: {
        'line-color': ['coalesce', ['get', 'color'], this.appliedPrimaryColor],
        'line-width': 2,
      },
    });
    instance.ubicacionesLayers.push(ubicacionesLineLayer);

    // 3. Ubicaciones - Labels (texto)
    const ubicacionesLabelLayer = `${instance.ubicacionesSrc}-label`;
    instance.map.addLayer({
      id: ubicacionesLabelLayer,
      type: 'symbol',
      source: instance.ubicacionesSrc,
      layout: {
        'text-field': ['get', 'nombre'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-anchor': 'center',
        'text-offset': [0, 0],
        'text-allow-overlap': false,
        'text-ignore-placement': false,
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': '#000000',
        'text-halo-width': 2,
      },
    });
    instance.ubicacionesLayers.push(ubicacionesLabelLayer);

    // 4. Círculos/polígonos de dibujo - Fill
    const drawFillLayer = `${instance.drawSrc}-fill`;
    instance.map.addLayer({
      id: drawFillLayer,
      type: 'fill',
      source: instance.drawSrc,
      paint: {
        'fill-color': ['coalesce', ['get', 'color'], this.appliedPrimaryColor],
        'fill-opacity': 0.15,
      },
    });
    instance.drawLayers.push(drawFillLayer);

    // 5. Círculos/polígonos de dibujo - Outline
    const drawLineLayer = `${instance.drawSrc}-line`;
    instance.map.addLayer({
      id: drawLineLayer,
      type: 'line',
      source: instance.drawSrc,
      paint: {
        'line-color': ['coalesce', ['get', 'color'], this.appliedPrimaryColor],
        'line-width': 2,
      },
    });
    instance.drawLayers.push(drawLineLayer);

    // 5. Recorridos - Lines
    const recorridosLineLayer = `${instance.recorridosSrc}-line`;
    instance.map.addLayer({
      id: recorridosLineLayer,
      type: 'line',
      source: instance.recorridosSrc,
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 3,
      },
    });
    instance.recorridosLayers.push(recorridosLineLayer);

    // 5a-bis. Cumplimiento del recorrido asignado: dos capas sobre la misma
    // source, filtradas por la property `cubierto`. Son dos y no una porque
    // `line-dasharray` de Mapbox no acepta expresiones data-driven, y el tramo
    // pendiente se dibuja punteado.
    const cumplimientoPendienteLayer = `${instance.cumplimientoSrc}-pendiente`;
    instance.map.addLayer({
      id: cumplimientoPendienteLayer,
      type: 'line',
      source: instance.cumplimientoSrc,
      filter: ['==', ['get', 'cubierto'], false],
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 3,
        'line-dasharray': [2, 2],
        'line-opacity': 0.7,
      },
    });
    instance.cumplimientoLayers.push(cumplimientoPendienteLayer);

    const cumplimientoCubiertoLayer = `${instance.cumplimientoSrc}-cubierto`;
    instance.map.addLayer({
      id: cumplimientoCubiertoLayer,
      type: 'line',
      source: instance.cumplimientoSrc,
      filter: ['==', ['get', 'cubierto'], true],
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 5,
      },
    });
    instance.cumplimientoLayers.push(cumplimientoCubiertoLayer);

    // 5b. Recorridos - Labels (texto)
    const recorridosLabelLayer = `${instance.recorridosSrc}-label`;
    instance.map.addLayer({
      id: recorridosLabelLayer,
      type: 'symbol',
      source: instance.recorridosSrc,
      layout: {
        'text-field': ['get', 'nombre'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-anchor': 'center',
        'text-offset': [0, 0],
        'symbol-placement': 'line-center',
        'text-rotation-alignment': 'map',
        'text-pitch-alignment':
          selectionForInstance?.iconPitchAlignment ?? 'viewport',
        'text-allow-overlap': false,
        'text-ignore-placement': false,
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': '#000000',
        'text-halo-width': 2,
      },
    });
    instance.recorridosLayers.push(recorridosLabelLayer);

    // 5c. Paradas del recorrido asignado (solo si es de puntos, tipoMedicion:
    // 'puntos'): un círculo con el radio real de tolerancia por parada, para
    // saber a qué destinos tiene que ir el vehículo y con qué margen, no solo
    // la línea. Mismo estilo que las zonas/ubicaciones declaradas.
    const recorridoParadasFillLayer = `${instance.recorridoParadasSrc}-fill`;
    instance.map.addLayer({
      id: recorridoParadasFillLayer,
      type: 'fill',
      source: instance.recorridoParadasSrc,
      paint: {
        'fill-color': '#1976d2',
        'fill-opacity': 0.15,
      },
    });
    instance.recorridoParadasLayers.push(recorridoParadasFillLayer);

    const recorridoParadasLineLayer = `${instance.recorridoParadasSrc}-line`;
    instance.map.addLayer({
      id: recorridoParadasLineLayer,
      type: 'line',
      source: instance.recorridoParadasSrc,
      paint: {
        'line-color': '#1976d2',
        'line-width': 2,
      },
    });
    instance.recorridoParadasLayers.push(recorridoParadasLineLayer);

    const recorridoParadasLabelLayer = `${instance.recorridoParadasSrc}-label`;
    instance.map.addLayer({
      id: recorridoParadasLabelLayer,
      type: 'symbol',
      source: instance.recorridoParadasSrc,
      layout: {
        'text-field': ['to-string', ['get', 'numero']],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 11,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#ffffff',
      },
    });
    instance.recorridoParadasLayers.push(recorridoParadasLabelLayer);

    // 6. Polylines de búsqueda - Lines
    const polylinesLineLayer = `${instance.polylinesSrc}-line`;
    instance.map.addLayer({
      id: polylinesLineLayer,
      type: 'line',
      source: instance.polylinesSrc,
      // Sólo líneas: este source también lleva los puntos de las flechas por
      // tramo, y un punto sin la propiedad `dash` rompe la evaluación de
      // `line-dasharray` al armar el tile (y con eso se cae todo el dibujo).
      filter: ['==', ['geometry-type'], 'LineString'],
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'width'],
        'line-dasharray': [
          'case',
          ['get', 'dash'],
          // Guión corto y hueco algo mayor: punteado marcado. Los valores van
          // en anchos de línea.
          ['literal', [1, 1.5]],
          ['literal', [1, 0]], // Si no, línea sólida
        ],
      },
    });
    instance.polylinesLayers.push(polylinesLineLayer);

    // 6b. Polylines - Flechas de dirección
    const polylinesArrowLayer = `${instance.polylinesSrc}-arrow`;
    instance.map.addLayer({
      id: polylinesArrowLayer,
      type: 'symbol',
      source: instance.polylinesSrc,
      filter: ['==', ['geometry-type'], 'LineString'],
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 50, // Espaciado entre flechas en píxeles
        'icon-image': 'arrow', // Usar un ícono de flecha (debemos cargarlo)
        'icon-size': 0.5,
        'icon-rotate': 90, // Rotación de 90 grados en sentido horario para alinear con la dirección del recorrido
        'icon-rotation-alignment': 'map',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: {
        'icon-color': ['get', 'color'], // Usar el mismo color que la línea
      },
    });
    instance.polylinesLayers.push(polylinesArrowLayer);

    // 7. Puntos interactivos
    const pointsLayer = `${instance.pointsSrc}-circle`;
    instance.map.addLayer({
      id: pointsLayer,
      type: 'circle',
      source: instance.pointsSrc,
      filter: [
        'all',
        ['!=', ['get', 'tipo'], 'trip-start'],
        ['!=', ['get', 'tipo'], 'trip-end'],
      ],
      paint: {
        'circle-radius': 5,
        'circle-color': ['get', 'color'],
        'circle-stroke-width': 1,
        'circle-stroke-color': '#ffffff',
      },
    });
    instance.pointsLayers.push(pointsLayer);

    // 7b. Marcadores de inicio de recorrido (bandera verde)
    const tripStartLayer = `${instance.pointsSrc}-trip-start`;
    instance.map.addLayer({
      id: tripStartLayer,
      type: 'symbol',
      source: instance.pointsSrc,
      filter: ['==', ['get', 'tipo'], 'trip-start'],
      layout: {
        'icon-image': 'flag', // Ícono de bandera
        'icon-size': 0.8,
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: {
        'icon-color': '#4CAF50', // Verde
      },
    });
    instance.pointsLayers.push(tripStartLayer);

    // 7c. Marcadores de fin de recorrido (bandera a cuadros - meta)
    const tripEndLayer = `${instance.pointsSrc}-trip-end`;
    instance.map.addLayer({
      id: tripEndLayer,
      type: 'symbol',
      source: instance.pointsSrc,
      filter: ['==', ['get', 'tipo'], 'trip-end'],
      layout: {
        'icon-image': 'checkered-flag', // Ícono de meta
        'icon-size': 0.8,
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: {
        'icon-color': '#000000', // Negro para la bandera a cuadros
      },
    });
    instance.pointsLayers.push(tripEndLayer);

    // 8. Paradas (también usado para puntos de exceso de velocidad en overlays de recorrido)
    // 8-cluster: Burbuja de agrupación cuando hay puntos superpuestos
    const paradasClusterLayer = `${instance.paradasSrc}-cluster`;
    instance.map.addLayer({
      id: paradasClusterLayer,
      type: 'circle',
      source: instance.paradasSrc,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#29B6F6',
          5,
          '#0288D1',
          20,
          '#01579B',
        ],
        'circle-radius': ['step', ['get', 'point_count'], 16, 5, 20, 20, 24],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
    instance.paradasLayers.push(paradasClusterLayer);

    const paradasClusterCountLayer = `${instance.paradasSrc}-cluster-count`;
    instance.map.addLayer({
      id: paradasClusterCountLayer,
      type: 'symbol',
      source: instance.paradasSrc,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 11,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': '#ffffff' },
    });
    instance.paradasLayers.push(paradasClusterCountLayer);

    // 8-halo: Aura exterior para distinguir paradas/exceso de los puntos normales del recorrido
    const paradasHaloLayer = `${instance.paradasSrc}-halo`;
    instance.map.addLayer({
      id: paradasHaloLayer,
      type: 'circle',
      source: instance.paradasSrc,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': [
          'coalesce',
          ['*', ['get', 'radius'], 1.8],
          RADIO_MARKER_PARADA * 1.8,
        ],
        'circle-color': ['coalesce', ['get', 'color'], COLOR_PARADA],
        'circle-opacity': 0.25,
        'circle-stroke-width': 0,
      },
    });
    instance.paradasLayers.push(paradasHaloLayer);

    const paradasLayer = `${instance.paradasSrc}-circle`;
    instance.map.addLayer({
      id: paradasLayer,
      type: 'circle',
      source: instance.paradasSrc,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': ['coalesce', ['get', 'radius'], RADIO_MARKER_PARADA],
        'circle-color': ['coalesce', ['get', 'color'], COLOR_PARADA],
        'circle-stroke-width': 2.5,
        'circle-stroke-color': '#ffffff',
      },
    });
    instance.paradasLayers.push(paradasLayer);

    // 8b. Exceso de velocidad (color rojo, source separado para no mezclar clusters)
    const excesoClusterLayer = `${instance.excesoSrc}-cluster`;
    instance.map.addLayer({
      id: excesoClusterLayer,
      type: 'circle',
      source: instance.excesoSrc,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#ef5350',
          5,
          '#e53935',
          20,
          '#b71c1c',
        ],
        'circle-radius': ['step', ['get', 'point_count'], 16, 5, 20, 20, 24],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
    instance.excesoLayers.push(excesoClusterLayer);

    const excesoClusterCountLayer = `${instance.excesoSrc}-cluster-count`;
    instance.map.addLayer({
      id: excesoClusterCountLayer,
      type: 'symbol',
      source: instance.excesoSrc,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 11,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': '#ffffff' },
    });
    instance.excesoLayers.push(excesoClusterCountLayer);

    const excesoHaloLayer = `${instance.excesoSrc}-halo`;
    instance.map.addLayer({
      id: excesoHaloLayer,
      type: 'circle',
      source: instance.excesoSrc,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': [
          'coalesce',
          ['*', ['get', 'radius'], 1.8],
          RADIO_MARKER_EXCESO * 1.8,
        ],
        'circle-color': COLOR_EXCESO,
        'circle-opacity': 0.25,
        'circle-stroke-width': 0,
      },
    });
    instance.excesoLayers.push(excesoHaloLayer);

    const excesoCircleLayer = `${instance.excesoSrc}-circle`;
    instance.map.addLayer({
      id: excesoCircleLayer,
      type: 'circle',
      source: instance.excesoSrc,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': ['coalesce', ['get', 'radius'], RADIO_MARKER_EXCESO],
        'circle-color': COLOR_EXCESO,
        'circle-stroke-width': 2.5,
        'circle-stroke-color': '#ffffff',
      },
    });
    instance.excesoLayers.push(excesoCircleLayer);

    // 8c. Eventos (markers análogos a paradas pero con color diferente)
    // 8c-cluster: Burbuja de agrupación para eventos
    const eventosClusterLayer = `${instance.eventosSrc}-cluster`;
    instance.map.addLayer({
      id: eventosClusterLayer,
      type: 'circle',
      source: instance.eventosSrc,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#9c29df',
          5,
          '#7b1fa2',
          20,
          '#4a148c',
        ],
        'circle-radius': ['step', ['get', 'point_count'], 16, 5, 20, 20, 24],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
    instance.eventosLayers.push(eventosClusterLayer);

    const eventosClusterCountLayer = `${instance.eventosSrc}-cluster-count`;
    instance.map.addLayer({
      id: eventosClusterCountLayer,
      type: 'symbol',
      source: instance.eventosSrc,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 11,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': '#ffffff' },
    });
    instance.eventosLayers.push(eventosClusterCountLayer);

    // 8b-halo: Aura exterior para eventos
    const eventosHaloLayer = `${instance.eventosSrc}-halo`;
    instance.map.addLayer({
      id: eventosHaloLayer,
      type: 'circle',
      source: instance.eventosSrc,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': RADIO_MARKER_EVENTO * 1.8,
        'circle-color': COLOR_EVENTO,
        'circle-opacity': 0.25,
        'circle-stroke-width': 0,
      },
    });
    instance.eventosLayers.push(eventosHaloLayer);

    const eventosLayer = `${instance.eventosSrc}-circle`;
    instance.map.addLayer({
      id: eventosLayer,
      type: 'circle',
      source: instance.eventosSrc,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': RADIO_MARKER_EVENTO,
        'circle-color': COLOR_EVENTO,
        'circle-stroke-width': 2.5,
        'circle-stroke-color': '#ffffff',
      },
    });
    instance.eventosLayers.push(eventosLayer);

    // 8d. Trip overlays unificados — paradas + exceso + eventos del viaje en una sola source
    // con clusterProperties para poder visualizar tipos combinados en un solo cluster.
    instance.map.addSource(instance.tripOverlaySrc, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: true,
      clusterMaxZoom: 15,
      clusterRadius: 30,
      clusterProperties: {
        countParada: ['+', ['case', ['==', ['get', 'tipo'], 'parada'], 1, 0]],
        countExceso: [
          '+',
          ['case', ['==', ['get', 'tipo'], 'exceso-velocidad'], 1, 0],
        ],
        countEvento: ['+', ['case', ['==', ['get', 'tipo'], 'evento'], 1, 0]],
      },
    });

    // Radios base para clusters según point_count
    const tripBigR: mapboxgl.ExpressionSpecification = [
      'step',
      ['get', 'point_count'],
      18,
      10,
      22,
      50,
      28,
    ];
    const tripMedR: mapboxgl.ExpressionSpecification = [
      'step',
      ['get', 'point_count'],
      13,
      10,
      16,
      50,
      20,
    ];
    const tripSmallR: mapboxgl.ExpressionSpecification = [
      'step',
      ['get', 'point_count'],
      8,
      10,
      10,
      50,
      13,
    ];

    // Capa A — paradas (azul, anillo exterior); visible solo cuando hay paradas en el cluster
    const tripClusterParadaLayer = `${instance.tripOverlaySrc}-cluster-parada`;
    instance.map.addLayer({
      id: tripClusterParadaLayer,
      type: 'circle',
      source: instance.tripOverlaySrc,
      filter: ['all', ['has', 'point_count'], ['>', ['get', 'countParada'], 0]],
      paint: {
        'circle-radius': tripBigR,
        'circle-color': COLOR_PARADA,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
    instance.tripOverlayLayers.push(tripClusterParadaLayer);

    // Capa B — exceso (rojo, anillo medio); radio se ajusta si parada también está presente
    const tripClusterExcesoLayer = `${instance.tripOverlaySrc}-cluster-exceso`;
    instance.map.addLayer({
      id: tripClusterExcesoLayer,
      type: 'circle',
      source: instance.tripOverlaySrc,
      filter: ['all', ['has', 'point_count'], ['>', ['get', 'countExceso'], 0]],
      paint: {
        'circle-radius': [
          'case',
          ['>', ['get', 'countParada'], 0],
          tripMedR,
          tripBigR,
        ],
        'circle-color': COLOR_EXCESO,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
    instance.tripOverlayLayers.push(tripClusterExcesoLayer);

    // Capa C — eventos (violeta, círculo interior); radio se ajusta según cuántos otros tipos hay
    const tripClusterEventoLayer = `${instance.tripOverlaySrc}-cluster-evento`;
    instance.map.addLayer({
      id: tripClusterEventoLayer,
      type: 'circle',
      source: instance.tripOverlaySrc,
      filter: ['all', ['has', 'point_count'], ['>', ['get', 'countEvento'], 0]],
      paint: {
        'circle-radius': [
          'case',
          [
            'all',
            ['>', ['get', 'countParada'], 0],
            ['>', ['get', 'countExceso'], 0],
          ],
          tripSmallR,
          [
            'any',
            ['>', ['get', 'countParada'], 0],
            ['>', ['get', 'countExceso'], 0],
          ],
          tripMedR,
          tripBigR,
        ],
        'circle-color': '#9c29df',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
    instance.tripOverlayLayers.push(tripClusterEventoLayer);

    // Texto del cluster con el total de puntos
    const tripClusterCountLayer = `${instance.tripOverlaySrc}-cluster-count`;
    instance.map.addLayer({
      id: tripClusterCountLayer,
      type: 'symbol',
      source: instance.tripOverlaySrc,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 11,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': '#ffffff' },
    });
    instance.tripOverlayLayers.push(tripClusterCountLayer);

    // Halo para puntos individuales (sin cluster)
    const tripOverlayHaloLayer = `${instance.tripOverlaySrc}-halo`;
    instance.map.addLayer({
      id: tripOverlayHaloLayer,
      type: 'circle',
      source: instance.tripOverlaySrc,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': [
          'coalesce',
          ['*', ['get', 'radius'], 1.8],
          RADIO_MARKER_PARADA * 1.8,
        ],
        'circle-color': [
          'case',
          ['==', ['get', 'tipo'], 'parada'],
          COLOR_PARADA,
          ['==', ['get', 'tipo'], 'exceso-velocidad'],
          COLOR_EXCESO,
          COLOR_EVENTO,
        ],
        'circle-opacity': 0.25,
        'circle-stroke-width': 0,
      },
    });
    instance.tripOverlayLayers.push(tripOverlayHaloLayer);

    // Círculo principal para puntos individuales
    const tripOverlayCircleLayer = `${instance.tripOverlaySrc}-circle`;
    instance.map.addLayer({
      id: tripOverlayCircleLayer,
      type: 'circle',
      source: instance.tripOverlaySrc,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': ['coalesce', ['get', 'radius'], RADIO_MARKER_PARADA],
        'circle-color': [
          'case',
          ['==', ['get', 'tipo'], 'parada'],
          COLOR_PARADA,
          ['==', ['get', 'tipo'], 'exceso-velocidad'],
          COLOR_EXCESO,
          COLOR_EVENTO,
        ],
        'circle-stroke-width': 2.5,
        'circle-stroke-color': '#ffffff',
      },
    });
    instance.tripOverlayLayers.push(tripOverlayCircleLayer);

    // 9. Vehículos - Círculo simple (básico para Paso 2)
    // ===== CAPAS DE VEHÍCULOS (múltiples layers superpuestas) =====

    // ===== SISTEMA DE ÍCONOS COMPUESTOS CON CANVAS API =====
    // Nota: Los íconos son pre-renderizados usando Canvas (VehicleIconGeneratorService)
    // combinando múltiples capas (selección, fondo, anillo de orientación, ícono)
    // en un solo ImageBitmap optimizado para GPU.
    //
    // Ventajas sobre el sistema anterior de 6 capas separadas:
    // - Eliminación del artefacto gris (causado por SDF multicapa)
    // - Reducción de draw calls: 6 → 3 por vehículo (50% menos)
    // - Mejor rendimiento con 50-200+ vehículos simultáneos
    // - Cache inteligente: configuraciones repetidas reutilizan ImageBitmap

    // SOLUCIÓN PARA ARTEFACTOS DE TRANSPARENCIA:
    // Ya NO pre-cargamos imágenes PNG con SDF (causaba recuadros grises).
    // Los anillos de orientación ahora se generan dinámicamente con Canvas
    // en VehicleCompositeIconService.ensureOrientationRing() sin usar SDF.

    // ============================================================================
    // CAPA 1: Ícono compuesto base (selection + background + ícono de vehículo)
    // ============================================================================
    // Esta capa NO rota - siempre mira hacia arriba
    // Generado dinámicamente por VehicleIconGeneratorService
    //
    // ESCALAS DE REFERENCIA - NO MODIFICAR SIN ACTUALIZAR SERVICE:
    // - Normal: 200px canvas × 0.264 = 53px en pantalla
    // - Seleccionado: 240px canvas × 0.276 × 1.2(escala) = ~79px en pantalla
    //
    // Ver: vehicle-icon-generator.service.ts para tamaños del canvas
    // ============================================================================
    const compositeIconLayer = `${instance.vehiculosSrc}-composite`;
    instance.map.addLayer({
      id: compositeIconLayer,
      type: 'symbol',
      source: instance.vehiculosSrc,
      layout: {
        'icon-image': ['get', 'compositeIconKey'], // Key del ícono generado dinámicamente
        'icon-size': [
          '*',
          ['get', 'escala'], // Escala del usuario
          [
            'case',
            ['get', 'isSelected'],
            0.276, // Seleccionados: 240px × 0.276 = 66px final (consistente cross-platform)
            0.264, // Normales: 200px × 0.264 = 53px final (consistente cross-platform)
          ],
        ],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-rotation-alignment': 'viewport', // NO rotar con el mapa - siempre hacia arriba
        'icon-pitch-alignment':
          selectionForInstance?.iconPitchAlignment ?? 'viewport',
      },
    });
    instance.vehiculosLayers.push(compositeIconLayer);

    // CAPA 2: Texto de identificación del vehículo
    // El anillo de orientación ya está bakeado en la imagen del ícono compuesto
    // (ver generateMergedVehicleIcon en vehicle-icon-generator.service.ts)
    const textLayer = `${instance.vehiculosSrc}-text`;
    instance.map.addLayer({
      id: textLayer,
      type: 'symbol',
      source: instance.vehiculosSrc,
      layout: {
        'text-field': ['get', 'identificacion'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 12,
        // Etiquetas que no se solapan entre sí: Mapbox prueba los anchors en
        // orden (arriba del ícono → abajo → derecha → izquierda) y corre el
        // label al primer lugar libre. Con text-variable-anchor el offset se
        // define con text-radial-offset (en ems), text-offset se ignora.
        'text-variable-anchor': ['bottom', 'top', 'right', 'left'],
        'text-radial-offset': 2.2,
        'text-justify': 'auto',
        'text-padding': 2,
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        // El ícono vive en su propia capa (icon-allow-overlap: true), así que
        // ocultar un label nunca oculta el marker del vehículo.
        'text-optional': true,
        // Prioridad de placement estable: seleccionado > en movimiento > resto.
        // Evita que el label que sobrevive a la colisión cambie en cada update.
        'symbol-sort-key': [
          'case',
          ['==', ['get', 'isSelected'], true],
          0,
          ['>', ['coalesce', ['get', 'velocidad'], 0], 5],
          1,
          2,
        ],
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': '#000000',
        'text-halo-width': 2,
      },
    });
    instance.vehiculosLayers.push(textLayer);

    // Tráfico TomTom (solo si el cliente tiene verTrafico habilitado)
    if (this.helper.clienteTraficoHabilitado) {
      const trafficSrc = `map${index}-tomtom-traffic`;
      const trafficLayer = `map${index}-traffic-layer`;

      instance.map.addSource(trafficSrc, {
        type: 'vector',
        tiles: [`${API}/proxy-tiles/tomtom/traffic/{z}/{x}/{y}.pbf`],
        minzoom: 0,
        maxzoom: 22,
        bounds: [-180, -56, -34, 72],
      });

      // Los estilos legacy (light-v11, dark-v11) no soportan el sistema de slots de Mapbox Standard.
      // En esos estilos, las c>apas se renderizan en orden de inserción, por lo que la capa de
      // tráfico (agregada después de los markers) quedaría encima de los vehículos.
      // Solución: en estilos legacy, usar beforeId para insertar el tráfico ANTES de los markers.
      // En estilos Standard, el slot 'middle' garantiza que quede debajo de los símbolos.
      const tipoActual = this.tipoMapaBaseByIndex[index] ?? 'osm';
      const isLegacyStyle =
        tipoActual === 'cartodb' || tipoActual.startsWith('personalizado_');

      const trafficLayerDef = {
        id: trafficLayer,
        type: 'line',
        source: trafficSrc,
        'source-layer': 'Traffic flow',
        ...(!isLegacyStyle ? { slot: 'middle' } : {}),
        paint: {
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            6,
            1,
            12,
            2.5,
            16,
            4,
          ],
          'line-color': [
            'interpolate',
            ['linear'],
            ['get', 'traffic_level'],
            0,
            '#cc0000',
            0.3,
            '#ff6600',
            0.6,
            '#ffcc00',
            0.8,
            '#99cc00',
            1,
            '#33cc33',
          ],
        },
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
          visibility: 'visible',
        },
      };

      // En estilos legacy insertar antes del composite layer para que quede debajo de los markers
      instance.map.addLayer(
        trafficLayerDef as any,
        isLegacyStyle ? compositeIconLayer : undefined,
      );

      // Si el tráfico está desactivado, ocultarlo después de agregar
      if (!this.helper.mapaTraficoEnabled) {
        instance.map.setLayoutProperty(trafficLayer, 'visibility', 'none');
      }

      // Iniciar refresco de tiles cada 2 minutos (solo una vez, para todas las instancias)
      // Si el tile no expiró en Redis (TTL 10 min) no se gasta token de TomTom
      if (!this.trafficRefreshInterval) {
        this.trafficRefreshInterval = setInterval(
          () => {
            this.mapInstances.forEach((inst, idx) => {
              if (!inst.map) return;
              const src = `map${idx}-tomtom-traffic`;
              const source = inst.map.getSource(src) as any;
              if (source) {
                source.setTiles([
                  `${API}/proxy-tiles/tomtom/traffic/{z}/{x}/{y}.pbf?t=${Date.now()}`,
                ]);
              }
            });
          },
          2 * 60 * 1000,
        );
      }
    }

    // NO cargar datos aquí - se cargarán en el callback de map.on('load')
    // para evitar race conditions con la inicialización asíncrona del mapa
    return true;
  }

  private setupMapEvents(instance: MapboxMapInstance, index: number) {
    // IMPORTANTE: Ejecutar todos los event listeners fuera de Angular Zone
    this.ngZone.runOutsideAngular(() => {
      // Guardar centro y zoom cuando el mapa se mueve
      instance.map.on('moveend', () => {
        this.saveCenterAndZoom(index, instance.map);
      });

      // Al terminar de rotar el mapa, recomputar SOLO el ícono (anillo bakeado al
      // nuevo bearing) por feature y aplicarlo con updateData. Evita reconstruir
      // toda la colección / re-evaluar filtros, HTML marker y texto (que no cambian
      // al rotar). Mismo resultado visual, z-order intacto.
      instance.map.on('rotateend', () => {
        this.refreshVehiculoOrientations(instance, index);
      });

      // Guardar zoom cuando cambia
      instance.map.on('zoomend', () => {
        instance.currentZoom = instance.map.getZoom();
      });

      // Arquitectura de capas (una sola capa de ícono):
      // vehiculosLayers[0] = compositeIconLayer (ícono + anillo bakeado)
      // vehiculosLayers[1] = textLayer (visible según config)
      // No hay orientationLayer separada - el anillo está bakeado en el compositeIconKey

      const clickableLayer = instance.vehiculosLayers[0]; // compositeIconLayer - capa base

      if (clickableLayer) {
        // Click en vehículos
        instance.map.on('click', clickableLayer, (e) => {
          if (!e.features || e.features.length === 0) return;

          const feature = e.features[0];
          const vehicleId = String(
            feature.properties?.['vehicleId'] ?? feature.id ?? '',
          );
          const reporte = vehicleId
            ? instance.vehiculoReportes?.get(vehicleId)
            : undefined;

          if (reporte) {
            // CRÍTICO: Volver a entrar en Angular Zone para actualizar UI
            this.ngZone.run(() => {
              // Primero seleccionar este mapa si no está seleccionado
              const mapaNum = index + 1; // Convertir índice 0-based a 1-based
              if (this.mapaSeleccionado !== mapaNum) {
                this.onMapContainerClick(index);
              }

              // Luego emitir el reporte seleccionado con el número de mapa
              this.reporteSeleccionadoVer.emit({ reporte, mapaNum });
            });
          }
        });

        // Cambiar cursor al pasar sobre vehículos
        instance.map.on('mouseenter', clickableLayer, () => {
          instance.map.getCanvas().style.cursor = 'pointer';
        });

        instance.map.on('mouseleave', clickableLayer, () => {
          instance.map.getCanvas().style.cursor = '';
        });
      } else {
        console.error(
          '❌ No se encontró compositeIconLayer en vehiculosLayers',
        );
      }

      // Click en una zona (ubicación) → la selecciona como contexto de búsqueda.
      // Sólo entran las ubicaciones de tipo Polygon: son las únicas que
      // `addUbicacionesPolygons` publica como features.
      const ubicacionesFillLayer = instance.ubicacionesLayers[0];
      if (ubicacionesFillLayer) {
        instance.map.on('click', ubicacionesFillLayer, (e) => {
          // Mientras se dibuja el círculo de búsqueda el click es del dibujo
          if (instance.dibujando) return;
          // La traza del viaje pasa por encima de la zona: el click es de ella.
          if (
            this.puedeAnimar &&
            this.pasadasViajeEnPunto(instance, e.point).length
          ) {
            return;
          }

          const idUbicacion = e.features?.[0]?.properties?.['id'];
          if (!idUbicacion) return;

          const ubicacion = this.ubicaciones?.find(
            (u) => u._id === idUbicacion,
          );
          if (!ubicacion) return;

          this.ngZone.run(() => {
            const mapaNum = index + 1;
            if (this.mapaSeleccionado !== mapaNum) {
              this.onMapContainerClick(index);
            }
            this.ubicacionSeleccionadaVer.emit({ ubicacion, mapaNum });
          });
        });

        instance.map.on('mouseenter', ubicacionesFillLayer, () => {
          if (instance.dibujando) return;
          instance.map.getCanvas().style.cursor = 'pointer';
        });

        instance.map.on('mouseleave', ubicacionesFillLayer, () => {
          if (instance.dibujando) return;
          instance.map.getCanvas().style.cursor = '';
        });
      }

      // Click en cluster de paradas → zoom al nivel de expansión
      const paradasClusterLayer = instance.paradasLayers[0];
      if (paradasClusterLayer) {
        instance.map.on('click', paradasClusterLayer, (e) => {
          const features = instance.map.queryRenderedFeatures(e.point, {
            layers: [paradasClusterLayer],
          });
          if (!features.length) return;
          const clusterId = features[0].properties?.['cluster_id'];
          (
            instance.map.getSource(
              instance.paradasSrc,
            ) as mapboxgl.GeoJSONSource
          ).getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;
            instance.map.easeTo({
              center: (features[0].geometry as GeoJSON.Point).coordinates as [
                number,
                number,
              ],
              zoom: (zoom ?? 14) + 0.5,
            });
          });
        });

        instance.map.on('mouseenter', paradasClusterLayer, () => {
          instance.map.getCanvas().style.cursor = 'pointer';
        });
        instance.map.on('mouseleave', paradasClusterLayer, () => {
          instance.map.getCanvas().style.cursor = '';
        });
      }

      // Click en paradas individuales
      const paradasLayer = instance.paradasLayers[2];
      if (paradasLayer) {
        instance.map.on('click', paradasLayer, (e) => {
          if (!e.features || e.features.length === 0) return;
          const feature = e.features[0];
          const paradaDataStr = feature.properties?.['paradaData'];
          if (paradaDataStr) {
            try {
              const parada = JSON.parse(paradaDataStr);
              const geom = feature.geometry;
              const coords: [number, number] =
                geom.type === 'Point'
                  ? (geom.coordinates.slice() as [number, number])
                  : [parada.longitude, parada.latitude];

              this.ngZone.run(() => {
                const mapaNum = index + 1;
                if (this.mapaSeleccionado !== mapaNum) {
                  this.onMapContainerClick(index);
                }
                this.mostrarPopupParada(instance, parada, coords);
                this.paradaSelectedOut.emit(parada);
              });
            } catch (err) {
              console.error('Error al parsear paradaData:', err);
            }
          }
        });

        instance.map.on('mouseenter', paradasLayer, () => {
          instance.map.getCanvas().style.cursor = 'pointer';
        });
        instance.map.on('mouseleave', paradasLayer, () => {
          instance.map.getCanvas().style.cursor = '';
        });
      }

      // Click en cluster de exceso de velocidad → zoom al nivel de expansión
      const excesoClusterLayer = instance.excesoLayers[0];
      if (excesoClusterLayer) {
        instance.map.on('click', excesoClusterLayer, (e) => {
          const features = instance.map.queryRenderedFeatures(e.point, {
            layers: [excesoClusterLayer],
          });
          if (!features.length) return;
          const clusterId = features[0].properties?.['cluster_id'];
          (
            instance.map.getSource(instance.excesoSrc) as mapboxgl.GeoJSONSource
          ).getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;
            instance.map.easeTo({
              center: (features[0].geometry as GeoJSON.Point).coordinates as [
                number,
                number,
              ],
              zoom: (zoom ?? 14) + 0.5,
            });
          });
        });

        instance.map.on('mouseenter', excesoClusterLayer, () => {
          instance.map.getCanvas().style.cursor = 'pointer';
        });
        instance.map.on('mouseleave', excesoClusterLayer, () => {
          instance.map.getCanvas().style.cursor = '';
        });
      }

      // Click en puntos individuales de exceso de velocidad
      const excesoIndividualLayer = instance.excesoLayers[2];
      if (excesoIndividualLayer) {
        instance.map.on('click', excesoIndividualLayer, (e) => {
          if (!e.features || e.features.length === 0) return;
          const feature = e.features[0];
          const excesoDataStr = feature.properties?.['excesoData'];
          if (excesoDataStr) {
            try {
              const punto = JSON.parse(excesoDataStr);
              this.ngZone.run(() => {
                const mapaNum = index + 1;
                if (this.mapaSeleccionado !== mapaNum) {
                  this.onMapContainerClick(index);
                }
                this.mostrarPopupExcesoVelocidad(instance, punto);
              });
            } catch (err) {
              console.error('Error al parsear excesoData:', err);
            }
          }
        });

        instance.map.on('mouseenter', excesoIndividualLayer, () => {
          instance.map.getCanvas().style.cursor = 'pointer';
        });
        instance.map.on('mouseleave', excesoIndividualLayer, () => {
          instance.map.getCanvas().style.cursor = '';
        });
      }

      // Click en cluster de eventos → zoom al nivel de expansión
      const eventosClusterLayer = instance.eventosLayers[0];
      if (eventosClusterLayer) {
        instance.map.on('click', eventosClusterLayer, (e) => {
          const features = instance.map.queryRenderedFeatures(e.point, {
            layers: [eventosClusterLayer],
          });
          if (!features.length) return;
          const clusterId = features[0].properties?.['cluster_id'];
          (
            instance.map.getSource(
              instance.eventosSrc,
            ) as mapboxgl.GeoJSONSource
          ).getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;
            instance.map.easeTo({
              center: (features[0].geometry as GeoJSON.Point).coordinates as [
                number,
                number,
              ],
              zoom: (zoom ?? 14) + 0.5,
            });
          });
        });

        instance.map.on('mouseenter', eventosClusterLayer, () => {
          instance.map.getCanvas().style.cursor = 'pointer';
        });
        instance.map.on('mouseleave', eventosClusterLayer, () => {
          instance.map.getCanvas().style.cursor = '';
        });
      }

      // Click en eventos individuales
      const eventosLayer = instance.eventosLayers[2];
      if (eventosLayer) {
        instance.map.on('click', eventosLayer, (e) => {
          if (!e.features || e.features.length === 0) return;
          const feature = e.features[0];
          const eventoDataStr = feature.properties?.['eventoData'];
          if (eventoDataStr) {
            try {
              const evento = JSON.parse(eventoDataStr);

              // Coordenadas desde geometría del feature
              const geom = feature.geometry;
              const coords: [number, number] =
                geom.type === 'Point'
                  ? (geom.coordinates.slice() as [number, number])
                  : [0, 0];

              this.ngZone.run(() => {
                const mapaNum = index + 1;
                if (this.mapaSeleccionado !== mapaNum) {
                  this.onMapContainerClick(index);
                }
                this.mostrarPopupEvento(instance, evento, coords);
                this.eventoSelectedOut.emit(evento);
              });
            } catch (err) {
              console.error('Error al parsear eventoData:', err);
            }
          }
        });

        instance.map.on('mouseenter', eventosLayer, () => {
          instance.map.getCanvas().style.cursor = 'pointer';
        });

        instance.map.on('mouseleave', eventosLayer, () => {
          instance.map.getCanvas().style.cursor = '';
        });
      }

      // Click en cluster de trip overlays unificados → zoom al nivel de expansión
      // Registrar handler en TODAS las capas de cluster (parada, exceso, evento)
      // para que funcione aunque el cluster no tenga paradas (ej: solo exceso+evento)
      const tripClusterLayers = instance.tripOverlayLayers.filter(
        (l) =>
          l.endsWith('-cluster-parada') ||
          l.endsWith('-cluster-exceso') ||
          l.endsWith('-cluster-evento'),
      );
      for (const clusterLayer of tripClusterLayers) {
        instance.map.on('click', clusterLayer, (e) => {
          const features = instance.map.queryRenderedFeatures(e.point, {
            layers: tripClusterLayers,
          });
          if (!features.length) return;
          const clusterId = features[0].properties?.['cluster_id'];
          (
            instance.map.getSource(
              instance.tripOverlaySrc,
            ) as mapboxgl.GeoJSONSource
          ).getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;
            instance.map.easeTo({
              center: (features[0].geometry as GeoJSON.Point).coordinates as [
                number,
                number,
              ],
              zoom: (zoom ?? 14) + 0.5,
            });
          });
        });

        instance.map.on('mouseenter', clusterLayer, () => {
          instance.map.getCanvas().style.cursor = 'pointer';
        });
        instance.map.on('mouseleave', clusterLayer, () => {
          instance.map.getCanvas().style.cursor = '';
        });
      }

      // Click en puntos individuales de trip overlays
      const tripOverlayCircleLayer = instance.tripOverlayLayers.find((l) =>
        l.endsWith('-circle'),
      );
      if (tripOverlayCircleLayer) {
        instance.map.on('click', tripOverlayCircleLayer, (e) => {
          if (!e.features || e.features.length === 0) return;
          const feature = e.features[0];
          const tipo = feature.properties?.['tipo'];
          const geom = feature.geometry;
          const coords: [number, number] =
            geom.type === 'Point'
              ? (geom.coordinates.slice() as [number, number])
              : [0, 0];

          this.ngZone.run(() => {
            const mapaNum = index + 1;
            if (this.mapaSeleccionado !== mapaNum) {
              this.onMapContainerClick(index);
            }

            if (tipo === 'parada') {
              const paradaDataStr = feature.properties?.['paradaData'];
              if (paradaDataStr) {
                try {
                  const parada = JSON.parse(paradaDataStr);
                  this.mostrarPopupParada(instance, parada, coords);
                  this.paradaSelectedOut.emit(parada);
                } catch {}
              }
            } else if (tipo === 'exceso-velocidad') {
              const excesoDataStr = feature.properties?.['excesoData'];
              if (excesoDataStr) {
                try {
                  const punto = JSON.parse(excesoDataStr);
                  this.mostrarPopupExcesoVelocidad(instance, punto);
                } catch {}
              }
            } else if (tipo === 'evento') {
              const eventoDataStr = feature.properties?.['eventoData'];
              if (eventoDataStr) {
                try {
                  const evento = JSON.parse(eventoDataStr);
                  // coords = posición del dot desplazado; popup sale de ahí
                  // mostrarPopupEvento extrae las coords reales del evento para el display
                  this.mostrarPopupEvento(instance, evento, coords);
                  this.eventoSelectedOut.emit(evento);
                } catch {}
              }
            }
          });
        });

        // Halo también actúa como área de click (más grande que el círculo)
        const tripOverlayHaloLayer = instance.tripOverlayLayers.find((l) =>
          l.endsWith('-halo'),
        );
        for (const layer of [
          tripOverlayCircleLayer,
          tripOverlayHaloLayer,
        ].filter(Boolean) as string[]) {
          instance.map.on('mouseenter', layer, () => {
            instance.map.getCanvas().style.cursor = 'pointer';
          });
          instance.map.on('mouseleave', layer, () => {
            instance.map.getCanvas().style.cursor = '';
          });
        }
      }

      // Click en puntos del recorrido para mostrar popup
      const pointsLayer = instance.pointsLayers[0];
      if (pointsLayer) {
        instance.map.on('click', pointsLayer, (e) => {
          if (!e.features || e.features.length === 0) return;

          // Si hay paradas, exceso o eventos en este mismo punto, cederles prioridad
          const overlayFeatures = instance.map.queryRenderedFeatures(e.point, {
            layers: [
              ...instance.paradasLayers,
              ...instance.excesoLayers,
              ...instance.eventosLayers,
              ...instance.tripOverlayLayers,
            ].filter(Boolean),
          });
          if (overlayFeatures.length > 0) return;

          const feature = e.features[0];

          // Obtener coordenadas del punto (no del mouse)
          const geometry = feature.geometry;
          if (geometry.type !== 'Point') return;
          const coordinates = geometry.coordinates.slice() as [number, number];

          const pointDataStr = feature.properties?.['pointData'];

          if (pointDataStr && instance.popup) {
            try {
              const pointData = JSON.parse(pointDataStr);

              // Formatear fecha
              const fecha = pointData.fechaDevice
                ? HelperService.parseDate(new Date(pointData.fechaDevice))
                : 'Sin fecha';

              // Formatear velocidad
              const velocidad =
                pointData.velocidad !== undefined &&
                pointData.velocidad !== null
                  ? `${pointData.velocidad.toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km/h`
                  : 'Sin datos';

              // Verificar que hay datos válidos para mostrar
              const hasValidData =
                pointData.fechaDevice ||
                (pointData.velocidad !== undefined &&
                  pointData.velocidad !== null);

              if (hasValidData) {
                const lat = coordinates[1].toFixed(5);
                const lng = coordinates[0].toFixed(5);
                const popupHTML = `
                  <div style="font-family:'Roboto',sans-serif;min-width:200px;background:var(--dp-bg);color:var(--dp-text);border-radius:8px;overflow:hidden;">
                    <div style="padding:8px 28px 8px 12px;display:flex;flex-direction:column;gap:6px;">
                      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:16px;">
                        <span style="font-size:11px;color:var(--dp-label);white-space:nowrap;">Fecha</span>
                        <span style="font-size:12px;font-weight:500;text-align:right;">${fecha}</span>
                      </div>
                      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:16px;">
                        <span style="font-size:11px;color:var(--dp-label);white-space:nowrap;">Velocidad</span>
                        <span style="font-size:12px;font-weight:500;text-align:right;">${velocidad}</span>
                      </div>
                      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:16px;border-top:1px solid var(--dp-border);padding-top:6px;margin-top:1px;">
                        <span style="font-size:11px;color:var(--dp-label);white-space:nowrap;">Posición</span>
                        <span style="font-size:11px;font-family:monospace;color:var(--dp-coords);text-align:right;">${lat}, ${lng}</span>
                      </div>
                    </div>
                  </div>
                `;

                // Usar coordenadas del punto, no del mouse
                instance.popup
                  .setLngLat(coordinates)
                  .setHTML(sanitizarHtml(popupHTML))
                  .addTo(instance.map);
              }
            } catch (err) {
              console.error('Error al parsear pointData:', err);
            }
          } else {
            console.warn('⚠️ Sin pointData o popup:', {
              pointDataStr,
              hasPopup: !!instance.popup,
            });
          }
        });

        instance.map.on('mouseenter', pointsLayer, () => {
          instance.map.getCanvas().style.cursor = 'pointer';
        });

        instance.map.on('mouseleave', pointsLayer, () => {
          instance.map.getCanvas().style.cursor = '';
        });
      }

      // Click en una parada del recorrido asignado (tipoMedicion:'puntos')
      // para mostrar nombre/dirección
      const recorridoParadasFillLayer = instance.recorridoParadasLayers[0];
      if (recorridoParadasFillLayer) {
        instance.map.on('click', recorridoParadasFillLayer, (e) => {
          if (!e.features || e.features.length === 0 || !instance.popup) return;

          const feature = e.features[0];
          const coordinates: [number, number] = [e.lngLat.lng, e.lngLat.lat];

          const numero = feature.properties?.['numero'];
          const nombre = feature.properties?.['nombre'];
          const direccion = feature.properties?.['direccion'];
          const popupHTML = `
            <div style="font-family:'Roboto',sans-serif;min-width:180px;background:var(--dp-bg);color:var(--dp-text);border-radius:8px;overflow:hidden;">
              <div style="padding:8px 28px 8px 12px;display:flex;flex-direction:column;gap:4px;">
                <span style="font-size:12px;font-weight:600;">
                  Parada ${numero}${nombre ? `: ${escaparHtml(nombre)}` : ''}
                </span>
                ${
                  direccion
                    ? `<span style="font-size:11px;color:var(--dp-label);">${escaparHtml(direccion)}</span>`
                    : ''
                }
              </div>
            </div>
          `;
          instance.popup
            .setLngLat(coordinates)
            .setHTML(sanitizarHtml(popupHTML))
            .addTo(instance.map);
        });

        instance.map.on('mouseenter', recorridoParadasFillLayer, () => {
          instance.map.getCanvas().style.cursor = 'pointer';
        });

        instance.map.on('mouseleave', recorridoParadasFillLayer, () => {
          instance.map.getCanvas().style.cursor = '';
        });
      }

      // Mouse sobre la traza del viaje → tooltip con la hora y cursor de click
      // (el click lleva el reproductor ahí, ver abajo). Se evalúa una vez por
      // cuadro: `mousemove` llega más seguido y cada evaluación recorre la
      // traza entera.
      let cuadroTooltip: number | undefined;
      let ultimoMouse: mapboxgl.Point | undefined;
      instance.map.on('mousemove', (e) => {
        ultimoMouse = e.point;
        if (cuadroTooltip !== undefined) return;
        cuadroTooltip = requestAnimationFrame(() => {
          cuadroTooltip = undefined;
          if (ultimoMouse) this.actualizarTooltipTraza(instance, ultimoMouse);
        });
      });
      // Mientras se arrastra o se hace zoom el tooltip quedaba flotando lejos
      // del cursor.
      instance.map.on('movestart', () => this.ocultarTooltipTraza(instance));
      instance.map.getCanvas().addEventListener('mouseleave', () => {
        ultimoMouse = undefined;
        this.ocultarTooltipTraza(instance);
      });

      // Click sobre la traza del viaje → el reproductor salta a ese momento.
      // Si no, cerrar popup y deseleccionar la zona al hacer click fuera de
      // features.
      instance.map.on('click', (e) => {
        // Lo que se dibuja sobre la traza (reportes, paradas, excesos, eventos)
        // tiene su propio click y manda.
        const features = instance.map.queryRenderedFeatures(e.point, {
          layers: [
            ...instance.vehiculosLayers,
            ...instance.paradasLayers,
            ...instance.excesoLayers,
            ...instance.eventosLayers,
            ...instance.pointsLayers,
            ...instance.recorridoParadasLayers,
            ...instance.tripOverlayLayers,
          ].filter(Boolean),
        });

        if (features.length > 0) return;

        // Sin reproductor (vista partida) no hay a dónde saltar.
        const pasadas =
          instance.dibujando || !this.puedeAnimar
            ? []
            : this.pasadasViajeEnPunto(instance, e.point);
        if (pasadas.length) {
          instance.popup?.remove();
          this.irAPasadaViaje(instance, pasadas);
          return;
        }

        // La zona tiene su propio click, que la selecciona.
        const enZona = instance.ubicacionesLayers.length
          ? instance.map.queryRenderedFeatures(e.point, {
              layers: instance.ubicacionesLayers.filter(Boolean),
            })
          : [];
        if (enZona.length > 0) return;

        if (instance.popup) {
          instance.popup.remove();
        }

        // El vehículo seleccionado NO se limpia acá: zona y vehículo son
        // contextos independientes (zona + vehículo = búsqueda de visitas).
        if (instance.dibujando) return;
        const seleccionadoMapa = this.seleccionadosGenerales?.find(
          (s) => s.mapaSeleccionado === index + 1,
        );
        if (seleccionadoMapa?.ubicacionSeleccionada) {
          this.ngZone.run(() => {
            this.ubicacionSeleccionadaVer.emit({
              ubicacion: undefined,
              mapaNum: index + 1,
            });
          });
        }
      });
    });
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // GESTIÓN DE INSTANCIAS
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private async recreateMapInstances() {
    // Capturar la versión actual. Si cambia antes de que terminemos, significa
    // que llegó un nuevo cambio de cantMapas y esta recreación quedó obsoleta.
    const myVersion = ++this.recreateVersion;

    // Bloquear los botones de cantMapas hasta que los mapas estén listos
    this.mapaService.setMapsRecreating(true);

    // Limpiar trails de seguimiento
    this.routeHistory.clear();

    // Limpiar resize observers de instancias anteriores
    this.resizeObservers.forEach((obs) => obs.disconnect());
    this.resizeObservers = [];

    // Destruir instancias existentes
    this.mapInstances.forEach((instance) => {
      if (instance.animationFrame) {
        cancelAnimationFrame(instance.animationFrame);
      }
      // Mismas limpiezas que ngOnDestroy: sin esto el loop de flujo, el flush
      // pendiente y el registro de imágenes seguían reteniendo el mapa removido.
      if (instance.vehiculoUpdateTimer !== undefined) {
        clearTimeout(instance.vehiculoUpdateTimer);
        instance.vehiculoUpdateTimer = undefined;
      }
      if (instance.flujoFrame !== undefined) {
        cancelAnimationFrame(instance.flujoFrame);
        instance.flujoFrame = undefined;
      }
      if (instance.cameraService) {
        instance.cameraService.destroy();
      }
      if (instance.htmlVehicleMarker) {
        instance.htmlVehicleMarker.destroy();
      }
      if (instance.popup) {
        instance.popup.remove();
      }
      this.vehicleCompositeIconService.cleanupMap(instance.map);
      instance.map.remove();
    });
    this.mapInstances = [];

    // Esperar un tick para que el DOM se actualice
    await new Promise((resolve) => setTimeout(resolve, 0));

    // Si llegó un nuevo cambio de cantMapas mientras esperábamos, abandonar
    if (myVersion !== this.recreateVersion) {
      this.mapaService.setMapsRecreating(false);
      return;
    }

    // Recargar seleccionados desde sessionStorage
    if (!this.seleccionadosGenerales) {
      const seleccionadosGuardados =
        this.serviceStorage.loadSeleccionadosFromSessionStorage(
          this.SESSION_STORAGE_KEY,
        );
      if (seleccionadosGuardados) {
        this.seleccionadosGenerales = seleccionadosGuardados;
      }
    }

    // Reinicializar (todos los mapas cargan en paralelo)
    await this.initMapInstances();

    // Si llegó otro cambio mientras cargaban los mapas, abandonar
    if (myVersion !== this.recreateVersion) {
      this.mapaService.setMapsRecreating(false);
      return;
    }

    // El estado de búsquedas de las instancias recreadas ya lo restauró
    // `initMapInstances`.

    // Mapas listos: habilitar botones
    this.mapaService.setMapsRecreating(false);
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // CAMBIO DE CAPA BASE
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public cambiarCapaBase(tipo: TipoMapaBase) {
    const idx = Math.max(0, this.mapaSeleccionado - 1);
    const instance = this.mapInstances[idx];
    if (!instance) return;

    // Guardar preferencia
    localStorage.setItem(this.getInstanceStorageKey(idx), tipo);
    this.tipoMapaBaseByIndex[idx] = tipo;
    this.tipoMapaBase = tipo;

    this.aplicarEstiloBase(instance, idx, tipo);
  }

  /**
   * Cambia el estilo base de un mapa y, cuando el nuevo estilo queda cargado,
   * vuelve a armar sources, layers y datos (`setStyle` se lleva todo puesto).
   *
   * Cada llamada invalida a las anteriores del mismo mapa (epoch) y cancela sus
   * listeners pendientes. Antes cada clic dejaba un `once('style.load')`: con
   * clics seguidos todos corrían juntos contra el mismo estilo, chocaban en
   * `addSource` ("There is already a source with ID") y el armado quedaba a
   * medias → vehículos que no volvían y el trabajo hecho dos o tres veces.
   */
  private aplicarEstiloBase(
    instance: MapboxMapInstance,
    idx: number,
    tipo: TipoMapaBase,
  ): void {
    const epoch = (this.estiloEpochPorMapa[idx] ?? 0) + 1;
    this.estiloEpochPorMapa[idx] = epoch;
    const vigente = () =>
      this.estiloEpochPorMapa[idx] === epoch &&
      this.mapInstances[idx] === instance;

    // Cancelar la reconstrucción pendiente de un cambio anterior que todavía
    // no se disparó: si no, sus listeners sobreviven y cruzan con el nuevo.
    this.estiloListoCleanupPorMapa[idx]?.();
    delete this.estiloListoCleanupPorMapa[idx];

    // Invalidar la carga de markers en vuelo (cargarMarkersForInstance corta
    // por generación). Si sigue, genera y registra cientos de íconos contra el
    // estilo que se está recargando: bloqueaba el hilo en tandas de ~5 s, el
    // estilo nuevo no terminaba de cargar y el mapa quedaba sin vehículos y
    // congelado.
    instance.cargaMarkersGen = (instance.cargaMarkersGen ?? 0) + 1;

    // Guardar posición actual antes de cambiar el estilo
    const currentCenter = instance.map.getCenter();
    const currentZoom = instance.map.getZoom();

    instance.map.setStyle(this.getMapboxStyle(tipo));

    // NO alcanza con `once('style.load')`: con estilos inline (capas
    // personalizadas) o entre estilos clásicos (light ↔ dark) Mapbox aplica el
    // cambio como diff y `style.load` no llega nunca. Se escucha también
    // `styledata` e `idle`, gateado por isStyleLoaded().
    //
    // Los listeners quedan vivos hasta el próximo cambio de capa (o el destroy):
    // si un estilo que llega tarde vuelve a borrar las capas, se rearman solas.
    this.ngZone.runOutsideAngular(() => {
      let reconstruyendo = false;
      let camara: { center: mapboxgl.LngLat; zoom: number } | undefined = {
        center: currentCenter,
        zoom: currentZoom,
      };

      const cleanup = () => {
        instance.map.off('style.load', onReady);
        instance.map.off('styledata', onReady);
        instance.map.off('idle', onReady);
      };

      const onReady = () => {
        if (reconstruyendo || !vigente() || !instance.map.isStyleLoaded()) {
          return;
        }
        // Mientras se descarga el JSON del estilo nuevo, el activo sigue siendo
        // el VIEJO (isStyleLoaded() = true, con nuestras capas puestas) y llegan
        // `idle`/`styledata` de ese estilo: reconstruir ahí no hacía nada y
        // después el estilo nuevo borraba todo → mapa sin vehículos. El estilo
        // nuevo (recarga completa o diff) siempre saca nuestros sources, así que
        // su ausencia es la señal de que ya está instalado.
        if (instance.map.getSource(instance.vehiculosSrc)) return;

        reconstruyendo = true;
        // La cámara se restaura sólo la primera vez: en un rearmado posterior
        // el usuario ya pudo haber movido el mapa.
        const camaraARestaurar = camara;
        camara = undefined;
        this.ngZone.run(() => {
          void this.reconstruirTrasCambioDeEstilo(
            instance,
            idx,
            tipo,
            camaraARestaurar,
            vigente,
          ).finally(() => {
            reconstruyendo = false;
          });
        });
      };

      this.estiloListoCleanupPorMapa[idx] = cleanup;
      instance.map.on('style.load', onReady);
      instance.map.on('styledata', onReady);
      instance.map.on('idle', onReady);
    });
  }

  private async reconstruirTrasCambioDeEstilo(
    instance: MapboxMapInstance,
    idx: number,
    tipo: TipoMapaBase,
    camara: { center: mapboxgl.LngLat; zoom: number } | undefined,
    vigente: () => boolean,
  ): Promise<void> {
    // 1. Configurar ciclo día/noche y otras configuraciones globales
    this.configurarCicloDiaNoche(instance.map, tipo);

    // 2. Restaurar posición del mapa
    if (camara) instance.map.jumpTo(camara);

    // 3. Re-crear imágenes, sources y layers. Si mientras tanto entró otro
    // cambio de capa, este armado quedó obsoleto y no sigue.
    const armado = await this.setupMapLayers(instance, idx, vigente);
    if (!armado || !vigente()) return;

    const seleccionadoMapa = this.seleccionadosGenerales?.find(
      (s) => s.mapaSeleccionado === idx + 1,
    );

    // 4. Re-aplicar datos si existen. Si hay selección para este mapa, los
    // markers los recarga applySearchStateToInstance (paso 5): cargarlos acá
    // también hacía la carga entera dos veces seguidas.
    if (!seleccionadoMapa && this.reportes && this.reportes.length > 0) {
      this.cargarMarkersForInstance(instance, idx);
    }
    if (this.ubicaciones && this.ubicaciones.length > 0) {
      this.serviceDibujar.addUbicacionesPolygons(
        instance,
        idx,
        this.ubicaciones,
        this.seleccionadosGenerales,
        this.appliedPrimaryColor,
      );
      this.aplicarResaltadoUbicacion(instance, idx);
    }
    if (this.recorridos && this.recorridos.length > 0) {
      this.serviceDibujar.addRecorridosLines(
        instance,
        idx,
        this.recorridos,
        this.seleccionadosGenerales,
        this.appliedAccentColor,
      );
      this.serviceDibujar.addCumplimientoRecorrido(
        instance,
        idx,
        this.recorridos,
        this.seleccionadosGenerales,
        this.appliedAccentColor,
      );
    }
    if (this.recorridosDinamicos && this.recorridosDinamicos.length > 0) {
      this.serviceDibujar.addParadasRecorrido(
        instance,
        idx,
        this.recorridosDinamicos,
        this.seleccionadosGenerales,
      );
    }

    // 5. Re-dibujar la búsqueda activa (traza del viaje, paradas, eventos,
    // exceso, círculo de zona). `setStyle` se lleva puestos todos los sources
    // y layers propios, y sin esto el recorrido que estabas mirando
    // desaparecía al cambiar a satélite.
    if (seleccionadoMapa) {
      this.applySearchStateToInstance(
        instance,
        idx,
        seleccionadoMapa,
        undefined,
        {
          // Cambiar de mapa base no es una búsqueda nueva: la cámara se queda
          // donde la tenías (ya se restauró el center/zoom más arriba).
          mantenerCamara: true,
        },
      );
    }

    // 6. Re-crear trail de seguimiento si existe
    const trailHistory = this.routeHistory.get(idx);
    if (trailHistory && trailHistory.length > 0) {
      this.createRouteLayer(idx, trailHistory);
    }
  }

  /**
   * Configura el ciclo día/noche para el estilo Mapbox Standard
   * Basado en la hora local, establece el preset de iluminación
   */
  private configurarCicloDiaNoche(
    map: mapboxgl.Map,
    tipoMapaBase: TipoMapaBase,
  ): void {
    // Solo aplicar si el estilo actual es Standard
    const isStandardStyle = tipoMapaBase === 'osm' || tipoMapaBase === 'google';

    if (!isStandardStyle) {
      return;
    }

    try {
      // Configurar 3D objects según preferencia del usuario
      const show3dObjects = this.helper.mapa3DEnabled;
      map.setConfigProperty('basemap', 'show3dObjects', show3dObjects);

      // Configurar POIs según preferencia del usuario
      const showPOIs = this.helper.mapaPOIsEnabled;
      map.setConfigProperty('basemap', 'showPointOfInterestLabels', showPOIs);

      // Configurar lightPreset solo si está habilitado el ciclo día/noche
      if (this.helper.mapaDiaNocheEnabled) {
        const lightPreset = this.calcularLightPreset();
        map.setConfigProperty('basemap', 'lightPreset', lightPreset);
      } else {
        // Si está deshabilitado, usar 'day' por defecto
        map.setConfigProperty('basemap', 'lightPreset', 'day');
      }
    } catch (error) {
      console.error('❌ Error configurando Mapbox Standard:', error);
    }
  }

  /**
   * Calcula el preset de luz según la hora actual
   */
  private calcularLightPreset(): 'dawn' | 'day' | 'dusk' | 'night' {
    const hora = new Date().getHours();
    if (hora >= 5 && hora < 8) return 'dawn';
    else if (hora >= 8 && hora < 18) return 'day';
    else if (hora >= 18 && hora < 21) return 'dusk';
    else return 'night';
  }

  /**
   * Actualiza la configuración de 3D en tiempo real en todas las instancias
   */
  private actualizarConfiguracion3D(enabled: boolean): void {
    this.mapInstances.forEach((instance) => {
      if (!instance.map) return;
      try {
        instance.map.setConfigProperty('basemap', 'show3dObjects', enabled);
      } catch (error) {
        // Silenciar error si no es estilo Standard
      }
    });
  }

  /**
   * Actualiza la configuración del ciclo día/noche en tiempo real en todas las instancias
   */
  private actualizarConfiguracionDiaNoche(enabled: boolean): void {
    this.mapInstances.forEach((instance) => {
      if (!instance.map) return;
      try {
        if (enabled) {
          const lightPreset = this.calcularLightPreset();
          instance.map.setConfigProperty('basemap', 'lightPreset', lightPreset);
        } else {
          instance.map.setConfigProperty('basemap', 'lightPreset', 'day');
        }
      } catch (error) {
        // Silenciar error si no es estilo Standard
      }
    });
  }

  /**
   * Actualiza el modo oscuro del mapa minimal en tiempo real
   */
  private actualizarModoOscuroMapa(enabled: boolean): void {
    this.mapInstances.forEach((instance, idx) => {
      const tipoMapa = this.tipoMapaBaseByIndex[idx];
      if (tipoMapa === 'cartodb') {
        // Mismo camino que el cambio de capa base: light ↔ dark es un diff
        // (no llega `style.load`) y además hay que volver a cargar los datos.
        this.aplicarEstiloBase(instance, idx, 'cartodb');
      }
    });
  }

  /**
   * Actualiza la configuración de POIs en tiempo real en todas las instancias
   */
  private actualizarConfiguracionPOIs(enabled: boolean): void {
    this.mapInstances.forEach((instance) => {
      if (!instance.map) return;
      try {
        instance.map.setConfigProperty(
          'basemap',
          'showPointOfInterestLabels',
          enabled,
        );
      } catch (error) {
        // Silenciar error si no es estilo Standard
      }
    });
  }

  private actualizarCapaTrafico(enabled: boolean): void {
    this.mapInstances.forEach((instance, idx) => {
      if (!instance.map) return;
      const trafficLayer = `map${idx}-traffic-layer`;
      try {
        if (instance.map.getLayer(trafficLayer)) {
          instance.map.setLayoutProperty(
            trafficLayer,
            'visibility',
            enabled ? 'visible' : 'none',
          );
        }
      } catch {
        // Silenciar error si la capa no existe aún
      }
    });
  }

  private getMapboxStyle(tipo: TipoMapaBase): string | mapboxgl.Style {
    // Detectar mapas personalizados (formato: personalizado_0, personalizado_1, etc.)
    if (tipo.startsWith('personalizado_')) {
      const index = parseInt(tipo.replace('personalizado_', ''));
      const mapaPersonalizado = this.mapasPersonalizados[index];

      if (mapaPersonalizado?.url) {
        return this.createCustomTileStyle(mapaPersonalizado);
      }
    }

    // Mapas predefinidos con nuevos estilos de Mapbox Standard
    const styles: Record<TipoMapaBase, string> = {
      osm: 'mapbox://styles/mapbox/standard',
      satelite: 'mapbox://styles/mapbox/standard-satellite',
      cartodb: this.helper.mapaOscuroEnabled
        ? 'mapbox://styles/mapbox/dark-v11'
        : 'mapbox://styles/mapbox/light-v11',
    };

    return styles[tipo] || styles['osm'];
  }

  /**
   * Convierte URLs de servidores externos a URLs del proxy cuando es necesario
   * Esto evita problemas de CORS con servidores que no tienen los headers apropiados
   */
  private convertToProxyUrl(originalUrl: string): string {
    // Configuración de servidores conocidos que requieren proxy
    const proxyMappings = [
      {
        // Servidor de Montevideo
        pattern:
          /geoserver\.montevideo\.gub\.uy\/geowebcache\/service\/tms\/1\.0\.0\/([^@]+)@[^\/]+\/\{z\}\/\{x\}\/\{-?y\}\.png/,
        service: 'montevideo',
        extractLayer: (match: RegExpMatchArray) => match[1], // Usar nombre original sin conversión
      },
    ];

    // Intentar hacer match con cada patrón conocido
    for (const mapping of proxyMappings) {
      const match = originalUrl.match(mapping.pattern);
      if (match) {
        const layerName = mapping.extractLayer(match);

        // Determinar la URL base del API según el ambiente
        const apiBaseUrl = this.getApiBaseUrl();

        // Construir URL del proxy
        return `${apiBaseUrl}/gestion-api-gestion/proxy-tiles/${mapping.service}/${layerName}/{z}/{x}/{y}.png`;
      }
    }

    // Si no es un servidor conocido, retornar la URL original
    return originalUrl;
  }

  /**
   * Obtiene la URL base del API según el ambiente
   * NOTA: Siempre usa producción para el proxy de tiles, ya que el backend está desplegado allí
   */
  private getApiBaseUrl(): string {
    // Siempre usar producción (el proxy está desplegado allí)
    return 'https://api.irix.ar';
  }

  /**
   * Crea un estilo personalizado de Mapbox con raster tiles
   */
  private createCustomTileStyle(
    layer: ILayerMapaPersonalizado,
  ): mapboxgl.Style {
    // Detectar si la URL usa formato TMS (con {-y})
    const isTMS = layer.url?.includes('{-y}');

    // Convertir URL a proxy si es necesario (para evitar CORS)
    let tileUrl = this.convertToProxyUrl(layer.url!);

    // Convertir {-y} a {y} para que Mapbox lo entienda
    tileUrl = isTMS ? tileUrl.replace('{-y}', '{y}') : tileUrl;

    return {
      version: 8,
      sources: {
        'custom-tiles': {
          type: 'raster',
          tiles: [tileUrl],
          tileSize: 256,
          scheme: isTMS ? 'tms' : 'xyz', // TMS invierte las coordenadas Y
          attribution: layer.nombre || '',
        },
      },
      layers: [
        {
          id: 'custom-tiles-layer',
          type: 'raster',
          source: 'custom-tiles',
          minzoom: 0,
          maxzoom: 22,
        },
      ],
    };
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // PERSISTENCIA (LocalStorage)
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public getInstanceStorageKey(index: number): string {
    return `${this.TIPO_MAPA_BASE_KEY}_${index}`;
  }

  private getSavedCenter(index: number): [number, number] | undefined {
    const lng = localStorage.getItem(`mapLngVehiculos_${index}`);
    const lat = localStorage.getItem(`mapLatVehiculos_${index}`);
    if (lng && lat) {
      return [parseFloat(lng), parseFloat(lat)];
    }
    return undefined;
  }

  private getSavedZoom(index: number): number {
    const zoom = localStorage.getItem(`mapZoomVehiculos_${index}`);
    return zoom ? parseFloat(zoom) : 15;
  }

  private getSavedTipoMapa(index: number): TipoMapaBase {
    const tipo = localStorage.getItem(this.getInstanceStorageKey(index));
    return (tipo as TipoMapaBase) || this.defaultTipoMapaBase;
  }

  private saveCenterAndZoom(index: number, map: mapboxgl.Map) {
    const center = map.getCenter();
    const zoom = map.getZoom();
    localStorage.setItem(`mapLngVehiculos_${index}`, center.lng.toString());
    localStorage.setItem(`mapLatVehiculos_${index}`, center.lat.toString());
    localStorage.setItem(`mapZoomVehiculos_${index}`, zoom.toString());
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // SELECCIÓN DE MAPA
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public onMapContainerClick(index: number) {
    const newSelected = index + 1;
    if (this.mapaSeleccionado !== newSelected) {
      // Finalizar cualquier drag/edición activa en el mapa anterior
      // para evitar que el círculo se propague al nuevo mapa
      const prevIdx = Math.max(0, this.mapaSeleccionado - 1);
      const prevInstance = this.mapInstances[prevIdx];
      if (prevInstance) {
        this.serviceDibujar.desactivarModoEdicionParaInstancia(prevInstance);
      }

      this.tipoMapaBase = this.getSavedTipoMapa(index);
      this.mapaSeleccionadoChange.emit(newSelected);
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // MÉTODOS HELPER
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public trackByIndex(index: number): number {
    return index;
  }

  public getMapIndices(): number[] {
    return Array.from({ length: this.cantMapas }, (_, i) => i);
  }

  public isSelected(index: number): boolean {
    return index === Math.max(0, this.mapaSeleccionado - 1);
  }

  // Interpolar entre dos colores hexadecimales según un factor (0-1)
  private interpolateColor(
    color1: string,
    color2: string,
    factor: number,
  ): string {
    factor = Math.max(0, Math.min(1, factor));

    const hex1 = color1.replace('#', '');
    const hex2 = color2.replace('#', '');

    const r1 = parseInt(hex1.substring(0, 2), 16);
    const g1 = parseInt(hex1.substring(2, 4), 16);
    const b1 = parseInt(hex1.substring(4, 6), 16);

    const r2 = parseInt(hex2.substring(0, 2), 16);
    const g2 = parseInt(hex2.substring(2, 4), 16);
    const b2 = parseInt(hex2.substring(4, 6), 16);

    const r = Math.round(r1 + (r2 - r1) * factor);
    const g = Math.round(g1 + (g2 - g1) * factor);
    const b = Math.round(b1 + (b2 - b1) * factor);

    const rHex = r.toString(16).padStart(2, '0');
    const gHex = g.toString(16).padStart(2, '0');
    const bHex = b.toString(16).padStart(2, '0');

    return `#${rHex}${gHex}${bHex}`;
  }

  private lightenColor(color: string, amount: number = 0.3): string {
    return this.interpolateColor(color, '#ffffff', amount);
  }

  // Obtener estilos del vehículo desde config markers
  private async getVehiculoEstilos(
    reporte: IReporteBase<'Tracker 4G'>,
    categoriaMarker: 'tipo' | 'funcion' = 'tipo',
  ): Promise<{
    vista: IVistaMarker;
    grupoColor?: string;
  } | null> {
    const configMarkers = this.mapaService.configMarkers;
    return this.helper.getVehiculoEstilos(
      reporte,
      configMarkers,
      categoriaMarker,
    );
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // CARGA DE MARKERS DE VEHÍCULOS
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  // Método auxiliar para calcular el color de estado según velocidad e ignición
  private getEstadoColor(
    velocidad: number,
    ignicion: boolean | undefined,
    movingDegraded: boolean,
  ): string {
    if (velocidad > 5) {
      // En movimiento (más de 5 km/h)
      if (movingDegraded) {
        // Degradado activado: interpolar de amarillo a rojo según velocidad
        const colorAmarillo = '#f9e425';
        const colorRojo = '#d82929';

        if (velocidad >= 120) {
          return colorRojo;
        } else {
          // Entre 5 y 120 km/h: interpolar entre amarillo y rojo
          const factor = (velocidad - 5) / (120 - 5); // 0 a 1
          return this.interpolateColor(colorAmarillo, colorRojo, factor);
        }
      } else {
        // Sin degradado: verde directo
        return '#388e3c';
      }
    } else {
      // Velocidad <= 5 km/h, verificar ignición
      if (ignicion === true) {
        // Detenido (encendido pero sin movimiento) - Azul
        return '#1976d2';
      } else {
        // Vehículo apagado - Gris
        return '#616161';
      }
    }
  }

  /**
   * Recomputa el ícono compuesto (anillo bakeado al bearing actual) de cada
   * vehículo del symbol layer y aplica los cambios con updateData (diff).
   *
   * Se usa en 'rotateend': el anillo se hornea con orientación efectiva
   * (orientacion - bearing), así que al rotar el mapa cambia el bucket de ángulo.
   * A diferencia de cargarMarkersForInstance, NO reconstruye la colección ni
   * re-evalúa filtros / HTML marker / texto (no cambian al rotar). Resultado
   * visual idéntico, z-order intacto (sigue siendo una imagen por marker).
   */
  private async refreshVehiculoOrientations(
    instance: MapboxMapInstance,
    index: number = 0,
  ) {
    const featuresMap = instance.vehiculoFeatures;
    if (!featuresMap || featuresMap.size === 0) return;

    const selectionForInstance = this.seleccionadosGenerales?.find(
      (s) => s.mapaSeleccionado === index + 1,
    );
    const categoriaMarker = selectionForInstance?.categoriaMarker || 'tipo';
    const mostrarFoto = selectionForInstance?.mostrarFoto ?? 'no';
    const mapBearing = instance.map.getBearing();

    const tareas: (() => Promise<void>)[] = [];

    for (const [vehicleId, feature] of featuresMap) {
      const props = (feature.properties || {}) as any;
      const reporte = instance.vehiculoReportes?.get(vehicleId);
      if (!reporte) continue;

      const estilosData = this.helper.getVehiculoEstilos(
        reporte,
        this.mapaService.configMarkers,
        categoriaMarker,
      );
      const vista = estilosData?.vista;
      const grupoColor = estilosData?.grupoColor;

      const estadoColor = this.vehicleIconGenerator.calculateRingColor(
        props.velocidad || 0,
        props.ignicion,
        {
          offColor: vista?.offColor,
          stopColor: vista?.stopColor,
          movingColor: vista?.movingColor,
          movingDegraded: vista?.movingDegraded,
        },
      );

      const effectiveOrientation =
        (((props.orientacion ?? 0) as number) - mapBearing + 360) % 360;

      const iconParams: MergedIconParams = {
        reporte,
        vista,
        grupoColor,
        categoriaMarker,
        isSelected: false,
        ringColor: estadoColor,
        orientation: effectiveOrientation,
        mostrarFoto,
      };

      const aplicar = (newKey: string) => {
        if (newKey === props.compositeIconKey) return;
        // Una carga completa reemplazó la colección: esta pasada quedó vieja.
        if (instance.vehiculoFeatures !== featuresMap) return;
        // Releer el feature vigente: durante el await un update de WS pudo
        // moverlo o cambiarle el ícono por un cambio de estado. Si cambió la
        // key, ese update es más nuevo que esta pasada y no se pisa; si no,
        // se conserva su posición y sólo se cambia el ícono.
        const actual = featuresMap.get(vehicleId);
        if (!actual) return;
        const propsActuales = (actual.properties || {}) as any;
        if (propsActuales.compositeIconKey !== props.compositeIconKey) return;
        const updated: GeoJSON.Feature = {
          ...actual,
          properties: { ...propsActuales, compositeIconKey: newKey },
        };
        featuresMap.set(vehicleId, updated);
        this.enqueueVehiculoFeature(instance, vehicleId, updated);
      };

      // Los buckets de ángulo se repiten entre vehículos, así que la mayoría de
      // las keys ya están generadas y se resuelven acá mismo, sin await.
      const keyDisponible = this.vehicleCompositeIconService.peekMergedIcon(
        instance.map,
        iconParams,
      );
      if (keyDisponible) {
        aplicar(keyDisponible);
        continue;
      }

      tareas.push(async () => {
        const newKey = await this.vehicleCompositeIconService.ensureMergedIcon(
          instance.map,
          iconParams,
        );
        aplicar(newKey);
      });
    }

    await this.conConcurrencia(tareas, mostrarFoto === 'no' ? 24 : 8);

    // Liberar imágenes de buckets de ángulo que ya no se usan.
    this.limpiarIconosSinUso(instance);
  }

  /**
   * Borra del mapa los íconos de vehículo que ya ningún feature usa.
   *
   * Las keys en uso se calculan ACÁ, sobre la colección autoritativa, y no con
   * el set que arma cada pasada antes de sus `await`: mientras una carga o un
   * refresco de orientación genera íconos, un update de WS puede asignarle a un
   * vehículo una key nueva (cambio de estado → otro color de anillo). Esa key no
   * estaba en el set de la pasada y la limpieza la borraba con el feature
   * apuntándole: «Image "vehicle-merged-…" could not be loaded» y el marker sin
   * ícono.
   *
   * Antes se aplican los updates encolados: si la fuente siguiera con la key
   * vieja de un vehículo y se borrara esa imagen, el re-layout que dispara
   * `removeImage` la pediría igual.
   */
  private limpiarIconosSinUso(instance: MapboxMapInstance): void {
    if (instance.vehiculoUpdateTimer !== undefined) {
      clearTimeout(instance.vehiculoUpdateTimer);
    }
    this.flushVehiculoUpdates(instance);

    const enUso = new Set<string>();
    for (const feature of instance.vehiculoFeatures?.values() ?? []) {
      const key = feature.properties?.['compositeIconKey'];
      if (typeof key === 'string' && key) enUso.add(key);
    }
    this.vehicleCompositeIconService.cleanupUnusedImages(instance.map, enUso);
  }

  private async cargarMarkersForInstance(
    instance: MapboxMapInstance,
    index: number = 0,
  ) {
    // Verificar si el mapa está listo (source existe)
    const source = instance.map.getSource(
      instance.vehiculosSrc,
    ) as mapboxgl.GeoJSONSource;
    if (!source) {
      // Mapa aún no inicializado, esperando map.on('load')
      return;
    }

    // Generación: cada llamada invalida las anteriores. Sin esto dos cargas
    // concurrentes (ej: cambio de filtro + tick del polling de 60s) compiten y
    // puede ganar la vieja, con lo que el cambio recién se veía en el refresco
    // siguiente.
    const gen = (instance.cargaMarkersGen ?? 0) + 1;
    instance.cargaMarkersGen = gen;
    const vigente = () => instance.cargaMarkersGen === gen;

    if (!this.reportes || this.reportes.length === 0) {
      // Limpiar markers si no hay reportes
      source.setData({ type: 'FeatureCollection', features: [] });
      return;
    }

    // Obtener selección para este mapa
    const selectionForInstance = this.seleccionadosGenerales?.find(
      (s) => s.mapaSeleccionado === index + 1,
    );

    // Verificar si debe mostrar vehículos
    const mostrarVehiculos = selectionForInstance?.mostrarVehiculos ?? true;
    if (!mostrarVehiculos) {
      const source = instance.map.getSource(
        instance.vehiculosSrc,
      ) as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({ type: 'FeatureCollection', features: [] });
      }
      return;
    }

    // Convertir reportes a GeoJSON features
    //
    // PASADA 1 (sin awaits): se arma la colección usando únicamente íconos que
    // ya están disponibles (peekMergedIcon) y se pinta de inmediato. Los que
    // falten se generan en la PASADA 2, en paralelo. Antes se generaba ícono por
    // ícono con await dentro del loop: con la flota completa (y sobre todo en
    // modo foto, donde cada activo implica cargar su imagen) el setData recién
    // llegaba segundos después, así que un cambio de filtro o del modo foto
    // parecía esperar el refresco siguiente.
    const features: GeoJSON.Feature[] = [];
    const featuresDiferidas: GeoJSON.Feature[] = [];
    const reportesPorVehiculo = new Map<string, IReporteBase<'Tracker 4G'>>();
    const pendientesIcono: {
      feature: GeoJSON.Feature;
      params: MergedIconParams;
    }[] = [];
    const featuresPrevias = instance.vehiculoFeatures;

    // Config del mapa: constante para toda la colección
    const categoriaColeccion = selectionForInstance?.categoriaMarker || 'tipo';
    const mostrarFotoColeccion = selectionForInstance?.mostrarFoto ?? 'no';
    const escalaColeccion = selectionForInstance?.escalaIconos ?? 1;
    const bearingColeccion = instance.map.getBearing();
    const aisladoEnEsteMapa = this.vehiculoAislado(selectionForInstance);

    let procesados = 0;

    for (const reporte of this.reportes) {
      // Ceder el hilo cada tanto: con la flota entera (el filtro de cliente
      // vacío muestra todo el scope) este loop es lo primero que traba la
      // página, antes incluso de generar un solo ícono.
      if (++procesados % REPORTES_ENTRE_CESIONES === 0) {
        await this.cederAlNavegador();
        if (!vigente()) return;
      }

      // Obtener ID del vehículo
      const vehicleId = reporte.idEntidad || reporte.idsAsignados?.[0];

      // PRIMERO: el filtro de busqueda de este mapa, que es un lookup en un Set.
      // Va antes que el resto: con la flota filtrada a un vehiculo, descarta
      // todo lo demas sin evaluar filtros de cliente, grupo, tipo ni funcion.
      // Si el Map tiene la key pero el Set esta vacio, es busqueda con 0
      // resultados (ocultar todos); si no tiene la key, no hay busqueda.
      const tieneBusquedaActiva = this.vehiculosFiltradosPorBusqueda.has(index);
      const filtroMapa = this.vehiculosFiltradosPorBusqueda.get(index);

      if (tieneBusquedaActiva) {
        const debeMostrarse =
          filtroMapa && vehicleId && filtroMapa.has(vehicleId);
        if (!debeMostrarse) {
          continue;
        }
      }

      // Aislamiento por recorrido: si este mapa muestra un recorrido, sólo se
      // dibuja su vehículo.
      if (aisladoEnEsteMapa && reporte.activo?._id !== aisladoEnEsteMapa) {
        continue;
      }

      // SEGUNDO: filtros de seleccion (cliente, grupo, funcion, tipo, etc.)
      if (
        !this.serviceGeneral.shouldShowReport(
          reporte,
          index,
          selectionForInstance,
        )
      ) {
        continue;
      }

      const valores = this.helper.getValores(reporte);
      const posicionSymbol = this.getPosicionParaMapa(valores, index + 1);
      if (!posicionSymbol?.coordinates) continue;

      const [lng, lat] = posicionSymbol.coordinates;

      // Calcular estilos del vehículo
      const velocidad = valores.velocidad || 0;
      const ignicion = valores.ignicion;
      const orientacion = valores.orientacion || 0;
      // El seleccionado también entra en la colección aunque lo dibuje el HTML
      // marker: se lo oculta con un filtro de capa (`aplicarFiltroSeleccion`).
      // Sacarlo de acá obligaba a rearmar la flota entera en cada cambio de
      // selección, que es lo que hacía cara la selección de un vehículo.
      const escala = escalaColeccion;

      // Obtener estilos personalizados desde config markers (resolución sincrónica)
      const estilosData = this.helper.getVehiculoEstilos(
        reporte,
        this.mapaService.configMarkers,
        categoriaColeccion,
      );
      const vista = estilosData?.vista;
      const grupoColor = estilosData?.grupoColor;

      // Calcular color del anillo según velocidad/ignición
      const estadoColor = this.vehicleIconGenerator.calculateRingColor(
        velocidad,
        ignicion,
        {
          offColor: vista?.offColor,
          stopColor: vista?.stopColor,
          movingColor: vista?.movingColor,
          movingDegraded: vista?.movingDegraded,
        },
      );

      // Calcular orientación efectiva compensando el bearing actual del mapa
      // para que el anillo (bakeado en el canvas) siempre apunte al heading geográfico correcto
      const effectiveOrientation =
        ((orientacion ?? 0) - bearingColeccion + 360) % 360;

      // Ícono unificado (anillo bakeado + fondo + ícono): solo se usa si ya está
      // disponible. Si falta, se genera en la pasada 2 y mientras tanto se
      // mantiene el ícono anterior del vehículo (si sigue registrado), para que
      // el marker no desaparezca al cambiar de modo.
      const iconParams: MergedIconParams = {
        reporte,
        vista,
        grupoColor,
        categoriaMarker: categoriaColeccion,
        isSelected: false,
        ringColor: estadoColor,
        orientation: effectiveOrientation,
        mostrarFoto: mostrarFotoColeccion,
      };
      const keyDisponible = this.vehicleCompositeIconService.peekMergedIcon(
        instance.map,
        iconParams,
      );
      const keyPrevia = vehicleId
        ? (featuresPrevias?.get(String(vehicleId))?.properties?.[
            'compositeIconKey'
          ] as string | undefined)
        : undefined;
      const compositeIconKey =
        keyDisponible ??
        (keyPrevia && instance.map.hasImage(keyPrevia) ? keyPrevia : null);

      // Crear feature con properties simplificadas
      const feature: GeoJSON.Feature = {
        type: 'Feature',
        // id de nivel feature: necesario para updateData(diff) por id (update incremental WS)
        id: vehicleId,
        geometry: {
          type: 'Point',
          coordinates: [lng, lat],
        },
        properties: {
          // IDs
          id: reporte.idEntidad || reporte._id,
          idActivo: reporte.activo?._id,
          vehicleId: vehicleId,
          tipo: 'vehiculo',
          categoria: reporte.activo?.categoria,
          identificacion: reporte.activo?.identificacion,

          // Datos de estado
          velocidad,
          ignicion,
          orientacion: orientacion || null,

          // Key del ícono unificado pre-renderizado (anillo + fondo + ícono)
          compositeIconKey,

          // El HTML marker es el que muestra la selección; en la colección
          // todos los markers son "normales".
          isSelected: false,

          // Escala
          escala,
        },
      };

      // El reporte completo va al Map de la instancia (lo usan el click y el
      // refresco de íconos), no a las properties del feature.
      if (vehicleId) {
        reportesPorVehiculo.set(String(vehicleId), reporte);
      }

      if (!keyDisponible) {
        pendientesIcono.push({ feature, params: iconParams });
      }

      if (compositeIconKey) {
        features.push(feature);
      } else {
        // Sin ícono todavía: se suma a la colección en la pasada 2 (pintarlo sin
        // ícono mostraría la etiqueta flotando sin marker).
        featuresDiferidas.push(feature);
      }
    }

    // Carga completa: descartar updates incrementales pendientes (quedan obsoletos)
    // y reconstruir el Map autoritativo de features por vehicleId.
    if (instance.vehiculoUpdateTimer !== undefined) {
      clearTimeout(instance.vehiculoUpdateTimer);
      instance.vehiculoUpdateTimer = undefined;
    }
    instance.vehiculoPendingUpdates?.clear();
    const featuresMap = new Map<string, GeoJSON.Feature>();
    for (const f of [...features, ...featuresDiferidas]) {
      if (f.id !== undefined && f.id !== null) {
        featuresMap.set(String(f.id), f);
      }
    }
    instance.vehiculoFeatures = featuresMap;
    instance.vehiculoReportes = reportesPorVehiculo;

    // Actualizar source (ya verificado que existe al inicio de la función)
    source.setData({
      type: 'FeatureCollection',
      features,
    });

    // Actualizar visibilidad del texto después de actualizar los datos
    this.updateTextLayerVisibility(instance, index);
    this.aplicarFiltroSeleccion(instance, index);

    if (!vigente()) return;

    await this.sincronizarHtmlMarkerSeleccionado(instance, index);
    if (!vigente()) return;

    // PASADA 2: generar en paralelo los íconos que faltaban y repintar con la
    // colección completa. En modo foto cada ícono implica cargar la imagen del
    // activo, así que se limita la concurrencia para no saturar la red.
    if (pendientesIcono.length) {
      const limite = mostrarFotoColeccion === 'no' ? 24 : 8;

      const repintar = () => {
        source.setData({
          type: 'FeatureCollection',
          features: [...(instance.vehiculoFeatures?.values() ?? [])].filter(
            (f) => !!f.properties?.['compositeIconKey'],
          ),
        });
      };

      // Repintado progresivo: la flota aparece a medida que se generan los
      // íconos, en vez de saltar entera recién al final.
      let ultimoRepintado = performance.now();

      await this.conConcurrencia(
        pendientesIcono.map((p) => async () => {
          if (!vigente()) return;
          const key = await this.vehicleCompositeIconService.ensureMergedIcon(
            instance.map,
            p.params,
          );

          // Escribir la key sobre el feature autoritativo: entre la pasada 1 y
          // la 2 un update de WS pudo reemplazarlo con una posición más nueva.
          const idStr = String(p.feature.id);
          const actual = instance.vehiculoFeatures?.get(idStr) ?? p.feature;
          const actualizado: GeoJSON.Feature = {
            ...actual,
            properties: {
              ...(actual.properties || {}),
              compositeIconKey: key,
            },
          };
          instance.vehiculoFeatures?.set(idStr, actualizado);
        }),
        limite,
        {
          alCeder: () => {
            if (!vigente()) return;
            const ahora = performance.now();
            if (ahora - ultimoRepintado < MS_ENTRE_REPINTADOS) return;
            ultimoRepintado = ahora;
            repintar();
          },
        },
      );

      if (!vigente()) return;

      repintar();
    }

    // Limpiar íconos compuestos no utilizados para liberar memoria.
    // Se hace al final: si corriera antes de la pasada 2 borraría imágenes que
    // se están por usar, y si corriera en una carga ya invalidada borraría las
    // de la carga vigente.
    if (!vigente()) return;
    this.limpiarIconosSinUso(instance);
  }

  /**
   * Cambió el vehículo seleccionado. Los markers de la flota no cambian: sólo
   * hay que correr el filtro que oculta al seleccionado y mover el HTML marker.
   */
  private async aplicarCambioDeSeleccion(
    instance: MapboxMapInstance,
    index: number,
  ): Promise<void> {
    this.aplicarFiltroSeleccion(instance, index);
    this.updateTextLayerVisibility(instance, index);
    await this.sincronizarHtmlMarkerSeleccionado(instance, index);
  }

  /**
   * Deja el HTML marker del vehículo seleccionado en línea con la selección
   * actual: lo crea, lo recrea si cambió el vehículo o el estilo del ícono, o
   * sólo le actualiza config y posición si es el mismo.
   */
  private async sincronizarHtmlMarkerSeleccionado(
    instance: MapboxMapInstance,
    index: number,
  ): Promise<void> {
    const selectionForInstance = this.seleccionadosGenerales?.find(
      (s) => s.mapaSeleccionado === index + 1,
    );

    // NUEVO: Crear o actualizar HTML marker del vehículo seleccionado
    const categoriaMarker = selectionForInstance?.categoriaMarker || 'tipo';
    const mostrarFotoHTML = selectionForInstance?.mostrarFoto ?? 'no';
    const escalaBase = selectionForInstance?.escalaIconos ?? 1;
    // Para el seleccionado, la escala es: base × 1.2 (ya incorporado en SELECTION_SIZE)
    // Entonces solo usamos escalaBase (el 1.2 está en el tamaño del canvas)
    const escalaSeleccionado = escalaBase;
    const mostrarNombreVehiculos =
      selectionForInstance?.mostrarNombreVehiculos ?? 'todos';
    const mostrarTexto = mostrarNombreVehiculos !== 'ninguno';
    const pitchAlignment =
      selectionForInstance?.iconPitchAlignment ?? 'viewport';
    const followCamera = !!selectionForInstance?.seguimientoActivo;
    const enableAnimation = selectionForInstance?.animacionesActivas ?? true;

    // IMPORTANTE: Solo recrear el HTML marker si cambió el vehículo seleccionado,
    // cambió la categoría (tipo/función) o el modo foto, que requieren regenerar el ícono,
    // o si no existe marker todavía. Esto evita recrear el marker constantemente
    // y permite que las actualizaciones en tiempo real funcionen correctamente.
    const selectedVehiculoId =
      selectionForInstance?.reporteSeleccionado?.activo?._id;
    const cambioVehiculo =
      instance.htmlVehicleMarker &&
      instance.htmlVehicleMarker.vehicleId !== selectedVehiculoId;
    const cambioCategoria =
      instance.htmlVehicleMarker &&
      instance.htmlVehicleMarker.categoria !== categoriaMarker;
    const cambioFoto =
      instance.htmlVehicleMarker &&
      instance.htmlVehicleMarker.mostrarFoto !== mostrarFotoHTML;
    const necesitaRecrear =
      !instance.htmlVehicleMarker ||
      !selectedVehiculoId ||
      cambioVehiculo ||
      cambioCategoria ||
      cambioFoto;

    if (necesitaRecrear) {
      await this.createOrUpdateSelectedHTMLMarker(
        instance,
        index,
        selectionForInstance?.reporteSeleccionado,
        categoriaMarker,
        mostrarFotoHTML,
        escalaSeleccionado,
        mostrarTexto,
      );
    } else if (instance.htmlVehicleMarker && selectedVehiculoId) {
      // El marker ya existe para el mismo vehículo y misma categoría
      // Aplicar cambios de configuración sin recrear (optimización para escalaIconos, etc.)
      // NOTA: categoria NO se actualiza aquí, cambiar categoria fuerza recreación (ver línea 1660)
      await instance.htmlVehicleMarker.updateConfig({
        scale: escalaSeleccionado,
        showText: mostrarTexto,
        pitchAlignment: pitchAlignment,
        followCamera: followCamera,
        enableAnimation: enableAnimation,
      });

      // Actualizar posición del marker con el reporte más reciente
      const reporteActualizado = this.reportes?.find(
        (r) => r.activo?._id === selectedVehiculoId,
      );

      if (reporteActualizado) {
        const valores = this.helper.getValores(reporteActualizado);
        const posicion = this.getPosicionParaMapa(valores, index + 1);
        if (posicion?.coordinates) {
          const [lng, lat] = posicion.coordinates;
          const orientacion = valores.orientacion || 0;
          const velocidad = valores.velocidad || 0;
          const ignicion = valores.ignicion;

          // Usar animación para actualizar posición
          instance.htmlVehicleMarker.updatePosition(
            lng,
            lat,
            orientacion,
            true,
          );

          // Actualizar color del anillo según nuevo estado (velocidad/ignición)
          instance.htmlVehicleMarker.updateState(velocidad, ignicion);
        }
      }
    }
  }

  /**
   * Ejecuta tareas asincrónicas con concurrencia acotada.
   * Reemplaza el `await` dentro del loop: N tareas seriales (cada una con su
   * carga de imagen) pasan a correr de a `limite` en paralelo.
   *
   * Cada `TAREAS_ENTRE_CESIONES` tareas se cede el hilo al navegador. Sin eso,
   * las tareas que resuelven de cache (la generación de íconos, una vez que la
   * imagen base ya está cargada) encadenan microtasks y las N terminan
   * corriendo dentro de una sola tarea larga: el navegador no pinta ni atiende
   * clicks hasta el final, que es lo que trababa la página al cambiar de
   * cliente (flota nueva = ningún ícono en cache).
   */
  private async conConcurrencia(
    tareas: (() => Promise<void>)[],
    limite: number,
    opciones?: { alCeder?: () => void },
  ): Promise<void> {
    if (!tareas.length) return;

    let cursor = 0;
    let completadas = 0;
    const workers = Array.from(
      { length: Math.min(Math.max(limite, 1), tareas.length) },
      async () => {
        while (cursor < tareas.length) {
          const i = cursor++;
          try {
            await tareas[i]();
          } catch (error) {
            console.warn('[MapaIndividual] Tarea de ícono falló:', error);
          }

          completadas++;
          if (completadas % TAREAS_ENTRE_CESIONES === 0) {
            opciones?.alCeder?.();
            await this.cederAlNavegador();
          }
        }
      },
    );

    await Promise.all(workers);
  }

  /**
   * Devuelve el control al navegador para que pinte y procese input. El
   * `setTimeout` va fuera de la zona de Angular: cederle el hilo no tiene por
   * qué disparar un ciclo de detección de cambios por lote.
   */
  private cederAlNavegador(): Promise<void> {
    return this.ngZone.runOutsideAngular(
      () => new Promise<void>((resolve) => setTimeout(resolve)),
    );
  }

  /**
   * Actualiza la visibilidad de la capa de texto de vehículos según la configuración
   * @param instance Instancia del mapa
   * @param index Índice del mapa (0-based)
   */
  /** Id del activo seleccionado en este mapa, si hay alguno. */
  private idSeleccionadoParaMapa(index: number): string | undefined {
    return this.seleccionadosGenerales?.find(
      (s) => s.mapaSeleccionado === index + 1,
    )?.reporteSeleccionado?.activo?._id;
  }

  /**
   * Oculta de las capas de símbolos al vehículo seleccionado, que se dibuja
   * aparte como HTML marker.
   *
   * Es un `setFilter`: cambiar de vehículo seleccionado no toca la colección de
   * features, así que no hay que rearmarla ni volver a generar íconos.
   */
  private aplicarFiltroSeleccion(
    instance: MapboxMapInstance,
    index: number,
  ): void {
    const iconLayerId = `${instance.vehiculosSrc}-composite`;
    if (!instance.map.getLayer(iconLayerId)) return;

    instance.map.setFilter(iconLayerId, this.filtroSinSeleccionado(index));
  }

  /** Expresión Mapbox que deja afuera al vehículo seleccionado. */
  private filtroSinSeleccionado(index: number): any[] {
    const idSeleccionado = this.idSeleccionadoParaMapa(index) ?? '';
    return ['!=', ['coalesce', ['get', 'idActivo'], ''], idSeleccionado];
  }

  private updateTextLayerVisibility(
    instance: MapboxMapInstance,
    index: number,
  ) {
    const textLayerId = `${instance.vehiculosSrc}-text`;

    // Verificar si la capa existe
    if (!instance.map.getLayer(textLayerId)) {
      return;
    }

    // Obtener configuración de este mapa
    const selectionForInstance = this.seleccionadosGenerales?.find(
      (s) => s.mapaSeleccionado === index + 1,
    );

    const mostrarNombreVehiculos =
      selectionForInstance?.mostrarNombreVehiculos ?? 'todos';

    // Aplicar filtro según configuración
    let filter: any[];

    switch (mostrarNombreVehiculos) {
      case 'ninguno':
        // No mostrar ningún texto
        filter = ['==', ['get', 'identificacion'], '____NEVER_MATCH____'];
        break;

      case 'todos':
        // Mostrar todos los textos que tengan identificación
        filter = ['!=', ['get', 'identificacion'], ''];
        break;

      case 'seleccionado':
        // Mostrar solo el texto del vehículo seleccionado
        filter = ['==', ['get', 'isSelected'], true];
        break;

      default:
        filter = ['==', ['get', 'identificacion'], '____NEVER_MATCH____'];
    }

    // El label del seleccionado lo pone su HTML marker: sin esto se ve dos
    // veces, una por cada capa.
    instance.map.setFilter(textLayerId, [
      'all',
      filter,
      this.filtroSinSeleccionado(index),
    ]);
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // HTML MARKER PARA VEHÍCULO SELECCIONADO
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Crea o actualiza el HTML marker del vehículo seleccionado
   * Crea DOS markers separados: uno para ícono/anillo, otro para texto
   * El marker de ícono respeta iconPitchAlignment, el de texto siempre usa viewport
   *
   * @param instance Instancia del mapa
   * @param index Índice del mapa (0-based)
   * @param reporteSeleccionado Reporte del vehículo seleccionado (null si no hay selección)
   * @param categoriaMarker Categoría del marker ('tipo' o 'funcion')
   * @param mostrarFoto Si mostrar foto del activo en lugar del ícono tipo/función
   * @param escala Escala del marker
   * @param mostrarTexto Si debe mostrar el texto de identificación
   */
  private async createOrUpdateSelectedHTMLMarker(
    instance: MapboxMapInstance,
    index: number,
    reporteSeleccionado: IReporteBase<'Tracker 4G'> | null | undefined,
    categoriaMarker: 'tipo' | 'funcion',
    mostrarFoto: 'no' | 'centrado' | 'completo',
    escala: number,
    mostrarTexto: boolean,
  ): Promise<void> {
    // Destruir marker existente si hay uno
    if (instance.htmlVehicleMarker) {
      instance.htmlVehicleMarker.destroy();
      instance.htmlVehicleMarker = undefined;

      // Limpiar historial de ruta cuando se deselecciona
      const mapIndex = this.mapInstances.indexOf(instance);
      this.clearRouteHistory(mapIndex);
    }

    // Detener cualquier seguimiento de cámara activo
    if (instance.cameraService) {
      instance.cameraService.stopFollow();
    }

    // Si no hay reporte seleccionado, no hacer nada más
    if (!reporteSeleccionado) {
      return;
    }

    // IMPORTANTE: Verificar que el reporte tenga la propiedad activo poblada
    if (!reporteSeleccionado.activo || !reporteSeleccionado.activo._id) {
      console.warn(
        '[MapaIndividual] ⚠️ Reporte sin activo poblado, esperando datos completos antes de crear HTML marker',
      );
      return;
    }

    // Verificar que el vehículo pase los filtros
    const selectionForInstance = this.seleccionadosGenerales?.find(
      (s) => s.mapaSeleccionado === index + 1,
    );

    if (
      !this.serviceGeneral.shouldShowReport(
        reporteSeleccionado,
        index,
        selectionForInstance,
      )
    ) {
      // El vehículo está filtrado, no mostrarlo
      return;
    }

    // Obtener coordenadas
    const valores = this.helper.getValores(reporteSeleccionado);
    const posicionSelected = this.getPosicionParaMapa(valores, index + 1);
    if (!posicionSelected?.coordinates) {
      return;
    }

    // Obtener configuración de pitch alignment
    const pitchAlignment =
      selectionForInstance?.iconPitchAlignment ?? 'viewport';
    const followCamera = !!selectionForInstance?.seguimientoActivo;
    const enableAnimation = selectionForInstance?.animacionesActivas ?? true;

    // Crear configuración del marker
    const config: HTMLVehicleMarkerConfig = {
      scale: escala,
      categoria: categoriaMarker,
      mostrarFoto,
      showText: mostrarTexto,
      pitchAlignment: pitchAlignment,
      animationDuration: 7000,
      enableAnimation: enableAnimation,
    };

    // Obtener índice del mapa
    const mapIndex = this.mapInstances.indexOf(instance);

    // Crear servicios helper para el marker
    const services = {
      helper: this.helper,
      vehicleIconGenerator: this.vehicleIconGenerator,
      getVehiculoEstilos: (
        reporte: IReporteBase<'Tracker 4G'>,
        categoria: 'tipo' | 'funcion',
      ) => this.getVehiculoEstilos(reporte, categoria),
      ngZone: this.ngZone,
      osrm: this.osrmService,
      onRoutePathUpdate: (pathCoordinates: [number, number][]) => {
        // Solo acumular trail si el seguimiento está activo para este mapa
        const mapaNum = mapIndex + 1;
        const selMapa = this.seleccionadosGenerales?.find(
          (s) => s.mapaSeleccionado === mapaNum,
        );
        if (selMapa?.seguimientoActivo) {
          this.updateRouteHistory(mapIndex, pathCoordinates);
        }
      },
    };

    // Crear e inicializar el nuevo marker
    const htmlMarker = new HTMLVehicleMarker(
      instance.map,
      reporteSeleccionado,
      config,
      services,
    );

    // Pasar las coordenadas ya resueltas (snapped o raw) para que el marker
    // se posicione correctamente desde el inicio según el toggle activo
    await htmlMarker.initialize(
      posicionSelected.coordinates as [number, number],
    );

    // Guardar referencia
    instance.htmlVehicleMarker = htmlMarker;

    // Usar servicio centralizado de cámara para el seguimiento
    // Throttle para change detection: solo cada 500ms para evitar sobrecarga
    if (followCamera && instance.cameraService) {
      let lastChangeDetection = 0;
      const onPositionUpdate = () => {
        const now = Date.now();
        if (now - lastChangeDetection > 500) {
          lastChangeDetection = now;
          // Forzar change detection dentro de Angular zone
          this.ngZone.run(() => {
            // Esto fuerza a Angular a actualizar la UI (vista de detalles, etc.)
          });
        }
      };

      instance.cameraService.followTarget(
        () => htmlMarker.getPosition(),
        onPositionUpdate,
      );
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ACTUALIZACIÓN EN TIEMPO REAL (WEBSOCKET)
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Configura la suscripción a actualizaciones en tiempo real vía WebSocket
   * Solo actualiza coordenadas y propiedades del feature sin recrear íconos
   */
  private setupRealTimeUpdates(): void {
    // Path per-vehículo (seleccionado/seguido): un reporte por emisión.
    this.realTimeSubscription = this.mapaService.reporteRealTime$.subscribe(
      (reporte) => {
        if (!reporte) return;
        this.ngZone.runOutsideAngular(() => {
          this.mapInstances.forEach((instance, idx) => {
            this.updateVehiclePositionRealTime(instance, idx, reporte);
          });
        });
      },
    );

    // Path batch (mapa, all-vehicles): un array por emisión → un solo callback
    // rxjs por batch en vez de uno por vehículo (mucho menos overhead).
    this.realTimeBatchSubscription =
      this.mapaService.reporteRealTimeBatch$.subscribe((reportes) => {
        if (!reportes?.length) return;
        this.ngZone.runOutsideAngular(() => {
          for (let i = 0; i < this.mapInstances.length; i++) {
            const instance = this.mapInstances[i];
            for (const reporte of reportes) {
              this.updateVehiclePositionRealTime(instance, i, reporte);
            }
          }
        });
      });
  }

  /**
   * Actualiza eficientemente la posición de un vehículo específico en tiempo real
   * DUAL PATH: Si es el seleccionado → actualiza HTML marker, si no → actualiza GeoJSON
   *
   * @param instance Instancia del mapa
   * @param index Índice del mapa (0-based)
   * @param reporte Reporte actualizado del vehículo
   */
  private async updateVehiclePositionRealTime(
    instance: MapboxMapInstance,
    index: number,
    reporte: IReporteBase<'Tracker 4G'>,
  ): Promise<void> {
    // Obtener el ID del vehículo
    const vehicleId = reporte.idEntidad || reporte.idsAsignados?.[0];
    if (!vehicleId) return;

    // Verificar si el vehículo debe mostrarse según el filtro de búsqueda de este mapa
    // IMPORTANTE: Si el Map tiene la key pero el Set está vacío, significa búsqueda con 0 resultados (ocultar todos)
    // Si el Map no tiene la key, significa que no hay búsqueda activa (mostrar todos)
    const tieneBusquedaActiva = this.vehiculosFiltradosPorBusqueda.has(index);
    const filtroMapa = this.vehiculosFiltradosPorBusqueda.get(index);
    const debeMostrarse =
      !tieneBusquedaActiva || (filtroMapa && filtroMapa.has(vehicleId));

    if (!debeMostrarse) {
      // Filtrado por búsqueda: quitar el feature del mapa si estaba presente.
      this.removeVehiculoFeature(instance, vehicleId);
      return;
    }

    // Obtener selección para este mapa
    const mapaNum = index + 1;
    const seleccionadoMapa = this.seleccionadosGenerales?.find(
      (s) => s.mapaSeleccionado === mapaNum,
    );

    // Con un recorrido dibujado, el mapa muestra sólo su vehículo.
    const aisladoEnEsteMapa = this.vehiculoAislado(seleccionadoMapa);
    if (aisladoEnEsteMapa && reporte.activo?._id !== aisladoEnEsteMapa) {
      this.removeVehiculoFeature(instance, vehicleId);
      return;
    }

    const vehiculoSeleccionadoId =
      seleccionadoMapa?.reporteSeleccionado?.activo?._id;
    // La selección se identifica por activo._id (igual que cargarMarkersForInstance).
    // OJO: vehicleId es idEntidad||idsAsignados[0], que puede NO ser activo._id;
    // comparar contra activo._id evita enrutar mal al PATH del symbol layer y que
    // el HTML marker del seleccionado no se actualice.
    const esSeleccionado =
      !!vehiculoSeleccionadoId &&
      (reporte.activo?._id === vehiculoSeleccionadoId ||
        vehicleId === vehiculoSeleccionadoId);

    // Obtener valores del reporte
    const valores = this.helper.getValores(reporte);
    const posicion = this.getPosicionParaMapa(valores, index + 1);
    if (!posicion?.coordinates) return;

    const [lng, lat] = posicion.coordinates;
    const velocidad = valores.velocidad || 0;
    const ignicion = valores.ignicion;
    const orientacion = valores.orientacion || 0;

    // Filtro "solo encendidos": si está activo y el vehículo está apagado,
    // quitarlo del mapa en vivo (al volver a encender reaparece en el próximo
    // poll/cargarMarkers, igual que altas/bajas).
    if (
      seleccionadoMapa?.filtrosSeleccionados?.soloEncendidos &&
      ignicion !== true
    ) {
      this.removeVehiculoFeature(instance, vehicleId);
      return;
    }

    // DUAL PATH: HTML Marker vs Symbol Layer
    if (esSeleccionado && instance.htmlVehicleMarker) {
      // PATH 1: Actualizar HTML Marker con animación
      instance.htmlVehicleMarker.updatePosition(lng, lat, orientacion, true);
      instance.htmlVehicleMarker.updateState(velocidad, ignicion);

      // El seleccionado sigue estando en la colección, escondido por el filtro
      // de capa. Se lo mantiene al día para que al deseleccionarlo aparezca
      // donde está y no donde estaba cuando lo seleccionaste.
      const featureOculta = instance.vehiculoFeatures?.get(vehicleId);
      if (featureOculta) {
        const actualizada: GeoJSON.Feature = {
          ...featureOculta,
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: {
            ...(featureOculta.properties || {}),
            velocidad,
            ignicion,
            orientacion,
          },
        };
        instance.vehiculoFeatures?.set(vehicleId, actualizada);
        instance.vehiculoReportes?.set(vehicleId, reporte);
        this.enqueueVehiculoFeature(instance, vehicleId, actualizada);
      }
    } else {
      // PATH 2: Symbol layer — update incremental por feature.id (sin reconstruir
      // toda la colección ni leer source._data). Solo mueve/recolorea este vehículo.

      // Si el mapa tiene "ocultar todos" (mostrarVehiculos=false), no re-agregar
      // features por WS: cargarMarkersForInstance limpia el symbol source pero los
      // updates en vivo lo harían reaparecer. Quitar si quedó presente y salir.
      // (El seleccionado va por PATH 1 / HTML marker, que se maneja aparte.)
      const mostrarVehiculos = seleccionadoMapa?.mostrarVehiculos ?? true;
      if (!mostrarVehiculos) {
        this.removeVehiculoFeature(instance, vehicleId);
        return;
      }

      const current = instance.vehiculoFeatures?.get(vehicleId);
      if (!instance.vehiculoFeatures || !current) {
        // El vehículo no está en este mapa (filtrado, seleccionado, o aún no cargado).
        return;
      }

      const props = (current.properties || {}) as any;

      // Detectar cambio de estado (off/stopped/moving) para regenerar el ícono con nuevo color
      const oldVelocidad = props.velocidad || 0;
      const oldIgnicion = props.ignicion;
      const stateChanged =
        this.getEstadoMarker(oldVelocidad, oldIgnicion) !==
        this.getEstadoMarker(velocidad, ignicion);

      let compositeIconKey = props.compositeIconKey;

      // Si cambió el estado, regenerar el ícono composite con el nuevo color de anillo
      if (stateChanged) {
        const categoriaMarker = seleccionadoMapa?.categoriaMarker || 'tipo';
        const mostrarFotoUpdate = seleccionadoMapa?.mostrarFoto ?? 'no';
        const estilosData = await this.getVehiculoEstilos(
          reporte,
          categoriaMarker,
        );
        const vista = estilosData?.vista;
        const grupoColor = estilosData?.grupoColor;

        const estadoColor = this.vehicleIconGenerator.calculateRingColor(
          velocidad,
          ignicion,
          {
            offColor: vista?.offColor,
            stopColor: vista?.stopColor,
            movingColor: vista?.movingColor,
            movingDegraded: vista?.movingDegraded,
          },
        );

        const mapBearing = instance.map.getBearing();
        const effectiveOrientation =
          ((orientacion ?? 0) - mapBearing + 360) % 360;

        compositeIconKey =
          await this.vehicleCompositeIconService.ensureMergedIcon(
            instance.map,
            {
              reporte,
              vista,
              grupoColor,
              categoriaMarker,
              isSelected: false,
              ringColor: estadoColor,
              orientation: effectiveOrientation,
              mostrarFoto: mostrarFotoUpdate,
            },
          );
      }

      // Construir feature actualizado (updateData reemplaza el feature con este id).
      const updated: GeoJSON.Feature = {
        ...current,
        geometry: { type: 'Point', coordinates: [lng, lat] },
        properties: {
          ...props,
          velocidad,
          ignicion,
          orientacion,
          compositeIconKey,
        },
      };
      instance.vehiculoFeatures.set(vehicleId, updated);
      instance.vehiculoReportes?.set(vehicleId, reporte);

      // Encolar y agrupar el flush en un solo updateData por frame.
      this.enqueueVehiculoFeature(instance, vehicleId, updated);

      // Si hay seguimiento activo en este vehículo NO seleccionado, centrar el mapa
      if (
        seleccionadoMapa?.followTargetId === vehicleId &&
        instance.cameraService
      ) {
        this.ngZone.run(() => {
          // Usar servicio centralizado para centrar sin cambiar zoom
          instance.cameraService!.centerPreservingZoom([lng, lat], 800);
        });
      }
    }
  }

  /**
   * Encola un feature de vehículo para aplicarlo en el próximo frame con un solo
   * updateData (agrupa ráfagas de mensajes WS → un update por frame por mapa).
   */
  private enqueueVehiculoFeature(
    instance: MapboxMapInstance,
    vehicleId: string,
    feature: GeoJSON.Feature,
  ): void {
    if (!instance.vehiculoPendingUpdates) {
      instance.vehiculoPendingUpdates = new Map<string, GeoJSON.Feature>();
    }
    instance.vehiculoPendingUpdates.set(vehicleId, feature);

    // Throttle: agrupar ráfagas WS y aplicar como máx. cada VEHICULO_FLUSH_MS.
    // Antes era requestAnimationFrame (hasta ~60 updateData/s con flota grande),
    // lo que disparaba re-render/re-placement de símbolos en Mapbox cada frame
    // (CPU alta). Los reportes llegan cada varios segundos por vehículo y el marker
    // salta igual, así que ~5/s es indistinguible visualmente y mucho más barato.
    // NOTA: el vehículo seleccionado NO pasa por acá (va por el HTML marker, PATH 1).
    if (instance.vehiculoUpdateTimer !== undefined) return;
    this.ngZone.runOutsideAngular(() => {
      instance.vehiculoUpdateTimer = setTimeout(
        () => this.flushVehiculoUpdates(instance),
        this.VEHICULO_FLUSH_MS,
      );
    });
  }

  /**
   * Aplica todos los updates de vehículos pendientes con un solo updateData(diff).
   * Si la versión de mapbox-gl no soporta updateData, cae a setData con la
   * colección completa mantenida en vehiculoFeatures.
   */
  private flushVehiculoUpdates(instance: MapboxMapInstance): void {
    instance.vehiculoUpdateTimer = undefined;
    const pending = instance.vehiculoPendingUpdates;
    if (!pending || pending.size === 0) return;

    const source = instance.map.getSource(
      instance.vehiculosSrc,
    ) as mapboxgl.GeoJSONSource & {
      updateData?: (data: GeoJSON.GeoJSON) => void;
    };
    if (!source) {
      pending.clear();
      return;
    }

    const features = Array.from(pending.values());
    pending.clear();

    if (typeof source.updateData === 'function') {
      // Update incremental: solo los features cambiados (match por id).
      source.updateData({ type: 'FeatureCollection', features });
    } else {
      // Fallback (mapbox-gl sin dynamic/updateData): colección completa.
      source.setData({
        type: 'FeatureCollection',
        features: Array.from(instance.vehiculoFeatures?.values() ?? []),
      });
    }
  }

  /**
   * Quita un vehículo del mapa (filtro de búsqueda) vía updateData con geometry null.
   */
  private removeVehiculoFeature(
    instance: MapboxMapInstance,
    vehicleId: string,
  ): void {
    if (!instance.vehiculoFeatures?.has(vehicleId)) return;
    instance.vehiculoFeatures.delete(vehicleId);
    instance.vehiculoReportes?.delete(vehicleId);
    // updateData elimina el feature cuyo id coincide y trae geometry null.
    const removal = {
      type: 'Feature',
      id: vehicleId,
      geometry: null,
      properties: {},
    } as unknown as GeoJSON.Feature;
    this.enqueueVehiculoFeature(instance, vehicleId, removal);
  }

  /**
   * Compara dos arrays de paradas para detectar si cambiaron
   */
  private paradasChanged(
    prev: Stop[] | undefined,
    current: Stop[] | undefined,
  ): boolean {
    // Si alguno es undefined o vacío, comparar directamente
    if (!prev && !current) return false;
    if (!prev || !current) return true;
    if (prev.length !== current.length) return true;

    // Comparar propiedades de las paradas en orden
    for (let i = 0; i < prev.length; i++) {
      // Usar positionId como identificador principal
      if (prev[i].positionId !== current[i].positionId) return true;
      // Si no hay positionId, comparar por startTime y coordenadas
      if (prev[i].startTime !== current[i].startTime) return true;
      if (prev[i].latitude !== current[i].latitude) return true;
      if (prev[i].longitude !== current[i].longitude) return true;
    }

    return false;
  }

  /**
   * Detecta si cambió el pitch alignment de los íconos
   */
  private cambioPitchAlignment(
    prev: any | undefined,
    current: any | undefined,
  ): boolean {
    return prev?.iconPitchAlignment !== current?.iconPitchAlignment;
  }

  /**
   * Actualiza la propiedad icon-pitch-alignment en las capas de vehículos
   * y en el HTML marker del vehículo seleccionado
   */
  private actualizarPitchAlignment(
    instance: MapboxMapInstance,
    alignment?: 'viewport' | 'map',
  ): void {
    const pitchAlignment = alignment ?? 'viewport';

    // Actualizar Symbol Layers
    instance.vehiculosLayers.forEach((layerId) => {
      if (instance.map.getLayer(layerId)) {
        instance.map.setLayoutProperty(
          layerId,
          'icon-pitch-alignment',
          pitchAlignment,
        );
      }
    });

    // Actualizar HTML marker del vehículo seleccionado
    if (instance.htmlVehicleMarker) {
      // Actualizar configuración del marker sin recrearlo
      instance.htmlVehicleMarker.updateConfig({
        pitchAlignment: pitchAlignment,
      });
    }
  }

  /**
   * Estado del marker, el mismo criterio con el que se elige su color
   * (getEstadoColor / calculateRingColor): 'off' es el gris.
   */
  private getEstadoMarker(
    velocidad: number,
    ignicion: boolean | undefined,
  ): 'moving' | 'stopped' | 'off' {
    if (velocidad > 5) return 'moving';
    if (ignicion === true) return 'stopped';
    return 'off';
  }

  /**
   * Devuelve la posición a usar según el toggle usarCoordenadasSnapped del mapa
   * indicado.
   *
   * Con el toggle activo se usa la posición ajustada SÓLO en los vehículos que
   * no están apagados: el snap corre la posición a la calle más cercana, que es
   * lo correcto para el que circula, pero al apagado lo saca de donde realmente
   * está (playa, garage, banquina) y lo deja sobre la calle. Los apagados —los
   * grises— van siempre en la posición original.
   */
  private getPosicionParaMapa(
    valores: any,
    mapaNum: number,
  ): { coordinates: [number, number] } | undefined {
    const seleccionadoMapa = this.seleccionadosGenerales?.find(
      (s) => s.mapaSeleccionado === mapaNum,
    );
    const usarSnapped = seleccionadoMapa?.usarCoordenadasSnapped ?? true;
    const apagado =
      this.getEstadoMarker(valores?.velocidad ?? 0, valores?.ignicion) ===
      'off';
    if (usarSnapped && !apagado) {
      return valores?.snappedGeojson || valores?.geojson;
    }
    return valores?.geojson;
  }

  /**
   * Detecta si cambió alguna propiedad que requiere recargar los markers
   * (selección de vehículo, filtros, escala, etc.)
   */
  private requiereRecargaMarkers(
    prev: any | undefined,
    current: any | undefined,
  ): boolean {
    if (!prev && !current) return false;
    if (!prev || !current) return true;

    // IMPORTANTE: Solo propiedades que REALMENTE requieren recrear markers
    // followTargetId NO debe estar aquí (solo centra el mapa, no recrea markers)
    // mostrarVehiculos afecta Symbol Layers pero el HTML marker se maneja por separado
    const propsSimples = [
      'mostrarVehiculos',
      'categoriaMarker',
      'mostrarFoto',
      'escalaIconos',
      'mostrarNombreVehiculos',
      'usarCoordenadasSnapped',
    ];

    for (const prop of propsSimples) {
      if (prev[prop] !== current[prop]) {
        return true;
      }
    }

    // El cambio de selección NO entra acá: se resuelve con un filtro de capa y
    // el HTML marker (`aplicarCambioDeSeleccion`), sin rearmar la colección.

    // Cambios en los filtros que afectan qué vehículos se muestran.
    // IMPORTANTE: incluye el filtro de clientes. Antes solo se comparaba
    // idsVehiculosFiltrados, que queda vacío cuando el único filtro es por
    // cliente, así que elegir otro cliente no recargaba los markers: el mapa
    // recién se actualizaba en el polling siguiente.
    return (
      this.firmaFiltrosVehiculos(prev) !== this.firmaFiltrosVehiculos(current)
    );
  }

  /**
   * Vehículo al que queda aislado un mapa: el del recorrido que tiene dibujado.
   *
   * Sale del estado del propio mapa y no de un canal aparte, porque el panel de
   * recorridos se destruye al cambiar de mapa o de tipo de búsqueda, y con él
   * se iba cualquier cosa que hubiera dejado anotada.
   */
  private vehiculoAislado(
    seleccionado: SeleccionadosBarraLateral | undefined,
  ): string | undefined {
    const recorrido = seleccionado?.seleccionadosVehiculosRecorridos;
    return recorrido?.busquedaRealizada?.polylines?.length
      ? recorrido.idVehiculo
      : undefined;
  }

  /**
   * Firma de los filtros que determinan qué vehículos se muestran en el mapa
   * (los mismos campos que evalúa `ServiceGeneralVehiculos.shouldShowReport`).
   */
  private firmaFiltrosVehiculos(seleccionado: any | undefined): string {
    const f = seleccionado?.filtrosSeleccionados;
    if (!f) return '';

    const lista = (arr: unknown[] | undefined) =>
      arr && arr.length ? [...arr].join(',') : '';

    return [
      // El recorrido dibujado aísla a su vehículo: entra en la firma para que
      // dibujarlo o limpiarlo recargue los markers.
      this.vehiculoAislado(seleccionado) ?? '',
      lista(f.idsClientesVehiculos),
      lista(f.idsVehiculosFiltrados),
      lista(f.idsVehiculos),
      lista(f.gruposVehiculos),
      lista(f.funcionVehiculos),
      lista(f.tiposVehiculo),
      f.includeChildren ? '1' : '0',
      f.soloEncendidos ? '1' : '0',
    ].join('|');
  }

  /**
   * Firma de los filtros de ubicaciones (clientes + ubicaciones puntuales).
   */
  private firmaFiltrosUbicaciones(seleccionado: any | undefined): string {
    const f = seleccionado?.filtrosSeleccionados;
    if (!f) return '';

    const lista = (arr: unknown[] | undefined) =>
      arr && arr.length ? [...arr].join(',') : '';

    return [
      lista(f.idsClientesUbicaciones),
      lista(f.idsUbicaciones),
      lista(f.idsUbicacionesFiltradas),
    ].join('|');
  }

  /**
   * Firma de los filtros de recorridos (clientes + recorridos puntuales).
   */
  private firmaFiltrosRecorridos(seleccionado: any | undefined): string {
    const f = seleccionado?.filtrosSeleccionados;
    if (!f) return '';

    const lista = (arr: unknown[] | undefined) =>
      arr && arr.length ? [...arr].join(',') : '';

    return [
      lista(f.idsClientesRecorridos),
      lista(f.idsRecorridos),
      lista(f.idsRecorridosFiltrados),
    ].join('|');
  }

  /**
   * Compara dos objetos SeleccionadosBarraLateral para detectar cambios relevantes
   * Solo compara propiedades que afectan el renderizado del mapa
   */
  private seleccionadoCambio(
    prev: any | undefined,
    current: any | undefined,
  ): boolean {
    // Si ambos son undefined/null, no hay cambio
    if (!prev && !current) return false;
    // Si solo uno es undefined/null, hay cambio
    if (!prev || !current) return true;

    // Comparar propiedades que afectan el renderizado del mapa
    const relevantProps = [
      'followTargetId',
      'tipoBusqueda',
      'puedeDibujar',
      'mostrarVehiculos',
      'mostrarUbicaciones',
      'mostrarNombreUbicaciones',
      'mostrarRecorridos',
      'mostrarNombreRecorridos',
      'mostrarNombreVehiculos',
      'categoriaMarker',
      'mostrarFoto',
      'escalaIconos',
      'iconPitchAlignment',
      'usarCoordenadasSnapped',
    ];

    for (const prop of relevantProps) {
      if (prev[prop] !== current[prop]) {
        return true;
      }
    }

    // Comparar reporteSeleccionado (por ID de activo)
    // IMPORTANTE: Esto permite que el círculo de selección se actualice cuando se hace click en otro vehículo
    const prevReporteId = prev.reporteSeleccionado?.activo?._id;
    const currentReporteId = current.reporteSeleccionado?.activo?._id;
    if (prevReporteId !== currentReporteId) {
      return true;
    }

    // Comparar zona seleccionada (por ID): cambia el resaltado del polígono
    if (
      prev.ubicacionSeleccionada?._id !== current.ubicacionSeleccionada?._id
    ) {
      return true;
    }

    // Comparar búsquedas activas (objetos anidados)
    const busquedaProps = [
      'seleccionadosVehiculoZona',
      'seleccionadosVehiculosRecorridos',
    ];

    for (const prop of busquedaProps) {
      // Comparar por referencia o por busquedaRealizada
      const prevBusqueda = prev[prop];
      const currentBusqueda = current[prop];

      if (prevBusqueda !== currentBusqueda) {
        // Si las referencias cambiaron, verificar si realmente cambió el contenido
        if (prop === 'seleccionadosVehiculoZona') {
          // Para búsqueda de zona: detectar cambios en resultados, circulo, y sub-solapas
          const prevVehiculos =
            prevBusqueda?.busquedaVehiculosPorZona?.vehiculosPasos;
          const currentVehiculos =
            currentBusqueda?.busquedaVehiculosPorZona?.vehiculosPasos;
          if (
            prevVehiculos !== currentVehiculos ||
            prevBusqueda?.circulo !== currentBusqueda?.circulo ||
            prevBusqueda?.tipoBusqueda !== currentBusqueda?.tipoBusqueda ||
            prevBusqueda?.modoBusquedaZona !==
              currentBusqueda?.modoBusquedaZona ||
            prevBusqueda?.busquedaVisitas?.polylines !==
              currentBusqueda?.busquedaVisitas?.polylines ||
            prevBusqueda?.busquedaVisitas?.visitaSeleccionadaKey !==
              currentBusqueda?.busquedaVisitas?.visitaSeleccionadaKey
          ) {
            return true;
          }
        } else {
          const prevRealizada = prevBusqueda?.busquedaRealizada;
          const currentRealizada = currentBusqueda?.busquedaRealizada;
          if (prevRealizada !== currentRealizada) {
            return true;
          }
        }
      }
    }

    // Detectar cambios en filtrosSeleccionados (vehículos, ubicaciones y
    // recorridos): se compara por contenido, no por referencia.
    if (
      this.firmaFiltrosVehiculos(prev) !==
        this.firmaFiltrosVehiculos(current) ||
      this.firmaFiltrosUbicaciones(prev) !==
        this.firmaFiltrosUbicaciones(current) ||
      this.firmaFiltrosRecorridos(prev) !== this.firmaFiltrosRecorridos(current)
    ) {
      return true;
    }

    return false;
  }

  /**
   * Actualiza la posición del mapa para vehículos en seguimiento
   * NOTA: Con el nuevo sistema, el HTMLVehicleMarker maneja el seguimiento de cámara.
   * Este método solo centra el mapa si NO hay un HTMLVehicleMarker activo.
   */
  private updateFollowingVehicles(): void {
    this.mapInstances.forEach((instance, index) => {
      const mapaNum = index + 1;

      // Solo procesar si este mapa está siguiendo un vehículo
      if (!instance.siguendoA) {
        return;
      }

      const followTargetId = instance.siguendoA;

      // Buscar el reporte del vehículo seguido
      const reporteSeguido = this.reportes?.find(
        (r) =>
          r.activo?._id === followTargetId ||
          r.idEntidad === followTargetId ||
          (Array.isArray(r.idsAsignados) &&
            r.idsAsignados.includes(followTargetId)),
      );

      if (reporteSeguido) {
        const valores = this.helper.getValores(reporteSeguido);
        const posicionFollowUpdate = this.getPosicionParaMapa(valores, mapaNum);
        if (posicionFollowUpdate?.coordinates) {
          const [lng, lat] = posicionFollowUpdate.coordinates;

          // Acumular coordenada al trail para vehículos SIN HTMLVehicleMarker
          // (los que tienen HTMLVehicleMarker acumulan vía onRoutePathUpdate con geometría OSRM)
          if (!instance.htmlVehicleMarker) {
            const seleccionadoMapa = this.seleccionadosGenerales?.find(
              (s) => s.mapaSeleccionado === mapaNum,
            );
            if (seleccionadoMapa?.seguimientoActivo) {
              const history = this.routeHistory.get(index) || [];
              const last = history[history.length - 1];
              if (!last || last[0] !== lng || last[1] !== lat) {
                this.updateRouteHistory(index, [[lng, lat]]);
              }
            }
          }

          // Si hay HTMLVehicleMarker activo, él y el CameraFollowController manejan el centrado
          if (!instance.htmlVehicleMarker && instance.cameraService) {
            instance.cameraService.centerPreservingZoom([lng, lat], 800);
          }
        }
      }
    });
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // DIBUJO DE CÍRCULOS
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public dibujar() {
    const idx = Math.max(0, this.mapaSeleccionado - 1);
    const instance = this.mapInstances[idx];
    if (
      !instance ||
      !this.seleccionadoBarraLateral?.puedeDibujar ||
      instance.dibujando
    ) {
      return;
    }

    const editando = !!instance.circle?.coordinates;
    instance.dibujando = true;

    if (editando) {
      // Modo edición: habilitar resize/move interactivo del círculo existente
      this.serviceDibujar.enablePersistentEdgeResize(
        instance,
        this.dibujarCallbacks,
      );
      this.serviceDibujar.enableCircleEdit(instance, this.dibujarCallbacks);
    } else {
      // Modo creación: dibujar nuevo círculo
      this.serviceDibujar.enableCircleDraw(instance, this.dibujarCallbacks);
    }

    // Hacer fit al círculo si existe
    if (instance.circle?.coordinates && instance.circle.radius) {
      this.serviceDibujar.fitBoundsToCircle(
        instance,
        instance.circle.coordinates as [number, number],
        instance.circle.radius,
      );
    }
  }

  public borrarDibujos() {
    const idx = Math.max(0, this.mapaSeleccionado - 1);
    const instance = this.mapInstances[idx];
    if (!instance) return;

    this.serviceDibujar.disablePersistentEdgeResize(instance);

    const source = instance.map.getSource(
      instance.drawSrc,
    ) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData({ type: 'FeatureCollection', features: [] });
    }

    // Limpiar completamente el círculo (undefined, no objeto con campos vacíos)
    instance.circle = undefined;

    // Emitir undefined para que la cadena limpie seleccionadosGenerales
    this.circuloBusquedaChange.emit(undefined);
    this.desactivarModoEdicion();
  }

  public desactivarModoEdicion() {
    const idx = Math.max(0, this.mapaSeleccionado - 1);
    const instance = this.mapInstances[idx];
    if (!instance) return;
    this.serviceDibujar.desactivarModoEdicionParaInstancia(instance);
  }

  public get dibujandoEnMapaSeleccionado(): boolean {
    const idx = Math.max(0, this.mapaSeleccionado - 1);
    return this.mapInstances[idx]?.dibujando || false;
  }

  public get tieneCirculoDibujado(): boolean {
    const idx = Math.max(0, this.mapaSeleccionado - 1);
    return !!this.mapInstances[idx]?.circle?.coordinates;
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ANIMACIÓN DE TRIPS
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * La reproducción pide un mapa solo.
   *
   * Es un loop por cuadro que además mueve la cámara: con la vista partida, cada
   * mapa sigue recibiendo reportes y redibujando, y el viaje se ve a los tirones.
   */
  public get puedeAnimar(): boolean {
    return this.mapInstances.length <= 1;
  }

  /** La instancia sobre la que trabaja el reproductor: la del mapa elegido. */
  public get instanciaViaje(): MapboxMapInstance | undefined {
    return this.mapInstances[Math.max(0, this.mapaSeleccionado - 1)];
  }

  public toggleAnimationOnSelected() {
    if (!this.puedeAnimar) return;

    const idx = Math.max(0, this.mapaSeleccionado - 1);
    const instance = this.mapInstances[idx];
    if (!instance) return;

    if (instance.animating) {
      this.stopAnimation(instance);
    } else {
      this.startAnimation(instance);
    }
  }

  /**
   * Pinta la barra con el avance actual del viaje y la hora del punto que se
   * está viendo. Sin instancia la deja en cero, que es lo que corresponde
   * cuando no hay viaje o se lo detuvo.
   */
  private pintarProgreso(instance?: MapboxMapInstance): void {
    const barra = this.progresoViajeRef?.nativeElement;
    if (!barra) return;

    const total = instance?.tripTotalLength ?? 0;
    const avance = Math.min(
      1,
      Math.max(0, total ? (instance?.distance ?? 0) / total : 0),
    );

    barra.value = String(Math.round(avance * 1000));

    // Lo recorrido queda pintado y lo que falta en gris: el slider nativo no
    // distingue una cosa de la otra por sí solo.
    const porcentaje = avance * 100;
    barra.style.background = `linear-gradient(90deg, var(--theme-primary-600) 0%, var(--theme-primary-400) ${porcentaje}%, rgba(0, 0, 0, 0.18) ${porcentaje}%)`;

    const etiqueta = this.fechaProgresoRef?.nativeElement;
    if (!etiqueta) return;
    const fecha = instance
      ? this.fechaEnDistancia(instance, instance.distance ?? 0)
      : undefined;
    const texto = fecha
      ? this.formatoFechaViaje.format(fecha).replace(',', '')
      : SIN_FECHA;
    // Escribir el mismo texto en cada cuadro le da trabajo al navegador de
    // gusto: la hora sólo cambia cada tanto.
    if (etiqueta.textContent !== texto) etiqueta.textContent = texto;

    // La hora de fin no cambia mientras sea el mismo viaje: se repinta sólo
    // cuando cambia la línea de tiempo.
    const etiquetaFin = this.fechaFinViajeRef?.nativeElement;
    const linea = instance?.tripLineaTiempo;
    if (!etiquetaFin || linea === this.lineaTiempoPintada) return;
    this.lineaTiempoPintada = linea;
    const fin = linea?.[linea.length - 1]?.t;
    etiquetaFin.textContent = fin
      ? this.formatoFechaViaje.format(fin).replace(',', '')
      : SIN_FECHA;
  }

  /**
   * Se arrastró la barra: salta a ese punto del recorrido. Con el viaje en
   * pausa también mueve el punto y la cámara, que es lo que se espera de un
   * reproductor.
   */
  public onProgresoInput(): void {
    const instance = this.instanciaViaje;
    const barra = this.progresoViajeRef?.nativeElement;
    if (!barra) return;

    this.asegurarLargoViaje(instance);
    if (!instance?.tripTotalLength) {
      this.pintarProgreso(instance);
      return;
    }

    instance.distance = (Number(barra.value) / 1000) * instance.tripTotalLength;
    this.pintarProgreso(instance);

    // Con la animación corriendo el loop ya redibuja y centra solo.
    if (instance.animating) return;
    this.asegurarCapasViaje(instance);
    const punto = this.dibujarPuntoViaje(instance);
    if (punto) instance.map.setCenter({ lng: punto[0], lat: punto[1] });
  }

  /** Fecha ISO a epoch; `undefined` si no hay fecha o no se entiende. */
  private aEpoch(fecha?: string): number | undefined {
    if (!fecha) return undefined;
    const t = Date.parse(fecha);
    return Number.isFinite(t) ? t : undefined;
  }

  /**
   * El largo del viaje, que es contra lo que se mide el avance. Se calcula una
   * sola vez por viaje: recorrer la traza entera es O(n).
   */
  private asegurarLargoViaje(instance?: MapboxMapInstance): void {
    if (!instance || instance.tripTotalLength) return;
    const coords = instance.tripCoords;
    if (!coords || coords.length < 2) return;

    let largo = 0;
    for (let i = 0; i < coords.length - 1; i++) {
      largo += this.serviceDibujar.calculateDistance(coords[i], coords[i + 1]);
    }
    instance.tripTotalLength = largo;
    this.asegurarLineaTiempo(instance);
  }

  /**
   * Traduce el viaje a "a los tantos metros eran las tantas (e iba a tantos
   * km/h)". Cada reporte se ubica sobre la traza con `ubicarReportesEnTraza`,
   * que explica por qué no alcanza con el punto más cercano.
   */
  private asegurarLineaTiempo(instance: MapboxMapInstance): void {
    if (instance.tripLineaTiempo) return;

    const coords = instance.tripCoords;
    const puntos = instance.tripPuntosFecha;
    if (!coords || coords.length < 2 || !puntos || puntos.length < 2) return;

    const { hastaVertice, distancias } = ubicarReportesEnTraza(
      coords,
      puntos,
      (a, b) => this.serviceDibujar.calculateDistance(a, b),
    );
    const acumulado = hastaVertice[coords.length - 1];

    // El arranque es el del viaje si el panel lo sabe: el primer reporte puede
    // haber llegado unos segundos después.
    const arranque = Math.min(
      puntos[0].t,
      instance.tripFechaInicio ?? puntos[0].t,
    );
    const linea: { d: number; t: number; v: number }[] = [
      { d: 0, t: arranque, v: puntos[0].v },
    ];

    puntos.forEach((punto, i) => {
      // El reloj no vuelve atrás aunque dos reportes lleguen desordenados.
      const previa = linea[linea.length - 1].t;
      linea.push({
        d: distancias[i],
        t: Math.max(punto.t, previa),
        v: punto.v,
      });
    });

    // El final de la barra es el fin del viaje que muestra el panel. La traza
    // suele terminar antes: los reportes de cuando ya estaba quieto caen todos
    // en el mismo lugar y el snap a calle los junta.
    const ultima = linea[linea.length - 1];
    const fin = Math.max(
      puntos[puntos.length - 1].t,
      ultima.t,
      instance.tripFechaFin ?? 0,
    );
    if (acumulado > ultima.d) linea.push({ d: acumulado, t: fin, v: ultima.v });
    else ultima.t = fin;

    instance.tripLineaTiempo = linea;
  }

  /**
   * Qué hora era cuando el viaje llevaba esa distancia recorrida. Interpola
   * entre los dos reportes que rodean al punto: entre uno y otro no hay dato,
   * así que la hora es aproximada.
   */
  private fechaEnDistancia(
    instance: MapboxMapInstance,
    distancia: number,
  ): number | undefined {
    this.asegurarLineaTiempo(instance);
    const linea = instance.tripLineaTiempo;
    if (!linea?.length) return undefined;

    if (distancia <= linea[0].d) return linea[0].t;
    const ultima = linea[linea.length - 1];
    if (distancia >= ultima.d) return ultima.t;

    let bajo = 0;
    let alto = linea.length - 1;
    while (alto - bajo > 1) {
      const medio = (bajo + alto) >> 1;
      if (linea[medio].d <= distancia) bajo = medio;
      else alto = medio;
    }

    const tramo = linea[alto].d - linea[bajo].d;
    if (tramo <= 0) return linea[alto].t;
    const avance = (distancia - linea[bajo].d) / tramo;
    return linea[bajo].t + avance * (linea[alto].t - linea[bajo].t);
  }

  /**
   * Qué velocidad llevaba el viaje a esa distancia recorrida. Igual que la
   * hora, se interpola entre los dos reportes que rodean al punto: entre uno y
   * otro no hay dato, así que es aproximada.
   */
  private velocidadEnDistancia(
    instance: MapboxMapInstance,
    distancia: number,
  ): number | undefined {
    this.asegurarLineaTiempo(instance);
    const v = valorInterpolado(instance.tripLineaTiempo ?? [], distancia);
    return v !== undefined && Number.isFinite(v) ? v : undefined;
  }

  /**
   * Las veces que el viaje pasó por ese punto de la pantalla (en orden), con
   * la distancia recorrida hasta ahí, el lugar exacto sobre la traza y a
   * cuántos píxeles quedó del punto. Vacío si no cae sobre la traza.
   *
   * Una traza puede pisarse a sí misma (ida y vuelta por la misma calle), así
   * que no alcanza con el vértice más cercano: cada tramo seguido de traza
   * cerca del punto es una pasada, y de cada una se toma su punto más cercano.
   * Se mide en píxeles para que la tolerancia sea la misma a cualquier zoom.
   */
  private pasadasViajeEnPunto(
    instance: MapboxMapInstance,
    punto: mapboxgl.Point,
  ): PasadaViaje[] {
    const coords = instance.tripCoords;
    if (!coords || coords.length < 2) return [];

    // Con la traza oculta desde el panel no hay nada que tocar.
    const capa = `${instance.polylinesSrc}-line`;
    if (
      !instance.map.getLayer(capa) ||
      instance.map.getLayoutProperty(capa, 'visibility') === 'none'
    ) {
      return [];
    }

    const pasadas: PasadaViaje[] = [];
    let mejor: PasadaViaje | undefined;
    let acumulado = 0;
    let desde = instance.map.project(coords[0]);

    for (let i = 1; i < coords.length; i++) {
      const hasta = instance.map.project(coords[i]);
      const largo = this.serviceDibujar.calculateDistance(
        coords[i - 1],
        coords[i],
      );

      // Proyección del click sobre el tramo, en pantalla.
      const dx = hasta.x - desde.x;
      const dy = hasta.y - desde.y;
      const largoPx = dx * dx + dy * dy;
      const t = largoPx
        ? Math.min(
            1,
            Math.max(
              0,
              ((punto.x - desde.x) * dx + (punto.y - desde.y) * dy) / largoPx,
            ),
          )
        : 0;
      const px = Math.hypot(
        punto.x - (desde.x + t * dx),
        punto.y - (desde.y + t * dy),
      );

      if (px <= TOLERANCIA_CLICK_TRAZA_PX) {
        if (!mejor || px < mejor.px) {
          const [lngDesde, latDesde] = coords[i - 1];
          const [lngHasta, latHasta] = coords[i];
          mejor = {
            px,
            d: acumulado + t * largo,
            coord: [
              lngDesde + t * (lngHasta - lngDesde),
              latDesde + t * (latHasta - latDesde),
            ],
          };
        }
      } else if (mejor) {
        pasadas.push(mejor);
        mejor = undefined;
      }

      acumulado += largo;
      desde = hasta;
    }
    if (mejor) pasadas.push(mejor);

    return pasadas;
  }

  /**
   * Lleva el reproductor a una de las pasadas por el punto clickeado, igual que
   * si se hubiera arrastrado la barra hasta ahí. La cámara no se mueve: el
   * lugar ya está a la vista, es donde se hizo click.
   *
   * Si el viaje pasó varias veces por ahí, el primer click va a la primera
   * pasada y cada click más en el mismo lugar, a la siguiente.
   */
  private irAPasadaViaje(
    instance: MapboxMapInstance,
    pasadas: PasadaViaje[],
  ): void {
    this.asegurarLargoViaje(instance);
    if (!instance.tripTotalLength) return;

    // "El mismo lugar" es estar a menos del doble de la tolerancia del click,
    // pasado a metros según el zoom (tiles de 512 px).
    const lat = (instance.map.getCenter().lat * Math.PI) / 180;
    const metrosPorPx =
      (40075016.686 * Math.cos(lat)) / (512 * 2 ** instance.map.getZoom());
    const cerca = 2 * TOLERANCIA_CLICK_TRAZA_PX * metrosPorPx;

    const actual = instance.distance ?? 0;
    const enCurso = pasadas.findIndex((p) => Math.abs(p.d - actual) <= cerca);
    instance.distance = pasadas[(enCurso + 1) % pasadas.length].d;
    this.pintarProgreso(instance);

    // Con la animación corriendo el loop ya redibuja desde ahí.
    if (instance.animating) return;
    this.asegurarCapasViaje(instance);
    this.dibujarPuntoViaje(instance);
  }

  /**
   * Muestra la hora (y la velocidad) a la que el viaje pasó por donde está el
   * mouse, pegada a la traza, o la oculta si el mouse no está sobre ella. Si
   * pasó varias veces, lista todas las pasadas.
   *
   * Anda también con la vista partida, donde el reproductor no: saber a qué
   * hora pasó no necesita reproducir nada. El cursor de click, en cambio, sólo
   * aparece cuando el click lleva el reproductor ahí.
   */
  private actualizarTooltipTraza(
    instance: MapboxMapInstance,
    punto: mapboxgl.Point,
  ): void {
    const pasadas = instance.dibujando
      ? []
      : this.pasadasViajeEnPunto(instance, punto);
    const momentos = pasadas
      .map((p) => ({
        t: this.fechaEnDistancia(instance, p.d),
        v: this.velocidadEnDistancia(instance, p.d),
      }))
      .filter(
        (m): m is { t: number; v: number | undefined } => m.t !== undefined,
      );

    if (!momentos.length) {
      this.ocultarTooltipTraza(instance);
      return;
    }

    const canvas = instance.map.getCanvas();
    // Sólo se toca el cursor si nadie más lo puso (vehículo, parada, etc.).
    if (this.puedeAnimar && !canvas.style.cursor) {
      canvas.style.cursor = 'pointer';
      instance.cursorTraza = true;
    }

    // El tooltip se pega a la pasada que quedó más cerca del mouse.
    const cercana = pasadas.reduce((a, b) => (b.px < a.px ? b : a));

    const filas = momentos
      .map(
        ({ t, v }) =>
          `<span style="font-size:12px;font-weight:500;white-space:nowrap;">${this.formatoFechaViaje.format(t).replace(',', '')}${
            v !== undefined
              ? `<span style="color:var(--dp-label);font-weight:400;"> · ${Math.round(v)} km/h</span>`
              : ''
          }</span>`,
      )
      .join('');
    const pie = this.puedeAnimar
      ? `<span style="font-size:10px;color:var(--dp-label);white-space:nowrap;">${
          momentos.length > 1
            ? 'Click: ir a cada pasada'
            : 'Click: ir a este momento'
        }</span>`
      : '';
    const html = `
      <div style="font-family:'Roboto',sans-serif;background:var(--dp-bg);color:var(--dp-text);border-radius:8px;padding:6px 10px;display:flex;flex-direction:column;gap:2px;">
        ${filas}${pie}
      </div>
    `;

    if (!instance.tooltipTraza) {
      instance.tooltipTraza = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'dark-popup tooltip-traza',
        anchor: 'bottom',
        offset: 10,
      });
    }
    const tooltip = instance.tooltipTraza;
    tooltip.setLngLat(cercana.coord);
    // Rehacer el HTML en cada movimiento es trabajo de más: la hora cambia
    // cada varios píxeles.
    if (instance.tooltipTrazaHtml !== html) {
      tooltip.setHTML(sanitizarHtml(html));
      instance.tooltipTrazaHtml = html;
    }
    if (!tooltip.isOpen()) tooltip.addTo(instance.map);
  }

  private ocultarTooltipTraza(instance: MapboxMapInstance): void {
    instance.tooltipTraza?.remove();
    if (instance.cursorTraza) {
      instance.map.getCanvas().style.cursor = '';
      instance.cursorTraza = false;
    }
  }

  /**
   * Corta la animación del viaje y la deja lista para arrancar de cero. A
   * diferencia del play/pausa, que preserva el progreso para poder retomar.
   */
  public stopAnimationOnSelected() {
    const idx = Math.max(0, this.mapaSeleccionado - 1);
    const instance = this.mapInstances[idx];
    if (!instance) return;

    this.stopAnimation(instance, true);
  }

  private startAnimation(instance: MapboxMapInstance) {
    if (!instance.tripCoords || instance.tripCoords.length < 2) {
      console.warn('No hay trip coords para animar');
      return;
    }

    this.asegurarCapasViaje(instance);

    // Detener seguimiento de vehículo e iniciar seguimiento del punto animado del viaje.
    // CameraFollowController pausa automáticamente durante zoomstart y reanuda tras zoomend,
    // lo que permite hacer zoom sin que el setCenter del loop lo interfiera.
    // Determinar si es un resume (distancia previa guardada) o inicio desde cero
    const resumeDistance = instance.distance ?? 0;

    if (instance.cameraService) {
      instance.cameraService.stopFollow();
      // Si es resume, usar la posición actual guardada; si es inicio, usar la primera coord
      if (!instance.tripCurrentPoint || resumeDistance === 0) {
        const startCoords = instance.tripCoords[0];
        instance.tripCurrentPoint = {
          lng: startCoords[0],
          lat: startCoords[1],
        };
      }
      instance.cameraService.followTarget(
        () => instance.tripCurrentPoint ?? instance.map.getCenter(),
      );
    }

    // El largo del viaje es contra lo que se mide el avance; se calcula una
    // sola vez, no en cada cuadro.
    this.asegurarLargoViaje(instance);

    // Iniciar animación (preservar distancia para resume; solo resetear si es inicio nuevo)
    instance.animating = true;
    instance.distance = resumeDistance;
    instance.lastTime = performance.now();
    instance.speed = this.speed; // km/h

    // Fuera de zone: el loop se re-agenda a sí mismo vía requestAnimationFrame
    // en cada frame, igual que el resto de los loops de Mapbox de este archivo.
    this.ngZone.runOutsideAngular(() => this.animateMarker(instance));
  }

  private animateMarker(instance: MapboxMapInstance) {
    if (!instance.animating || !instance.tripCoords) return;

    const now = performance.now();
    const deltaTime = now - (instance.lastTime || now);
    instance.lastTime = now;

    // Convertir velocidad de km/h a metros/segundo
    const speedMetersPerSecond = ((instance.speed || this.speed) * 1000) / 3600;

    // Actualizar distancia recorrida
    instance.distance =
      (instance.distance || 0) + (speedMetersPerSecond * deltaTime) / 1000;

    // Usar longitud total pre-calculada (calculada una sola vez en startAnimation)
    const totalLength = instance.tripTotalLength ?? 0;

    this.pintarProgreso(instance);

    // Si llegamos al final, reiniciar o detener
    if (instance.distance >= totalLength) {
      instance.distance = 0; // Reiniciar animación
      // O detener: this.stopAnimation(instance); return;
    }

    // Mover el marker. Deja apuntado el punto actual para que
    // CameraFollowController centre el mapa suavemente: el controlador pausa el
    // seguimiento durante zoomstart, permitiendo zoom sin interferencias.
    this.dibujarPuntoViaje(instance);

    // Continuar animación
    instance.animationFrame = requestAnimationFrame(() =>
      this.animateMarker(instance),
    );
  }

  /**
   * Source y capas del viaje. Viven aparte de la traza dibujada porque son las
   * que muestran la reproducción: se crean la primera vez que se la usa, sea
   * por play o por arrastrar la barra.
   */
  private asegurarCapasViaje(instance: MapboxMapInstance): void {
    if (instance.tripSrc) return;

    const tripSrcId = `${instance.container}-trip`;
    const tripMarkerSrcId = `${instance.container}-trip-marker`;

    instance.tripSrc = tripSrcId;
    instance.tripMarkerSrc = tripMarkerSrcId;

    // Source para la línea del trip
    instance.map.addSource(tripSrcId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });

    // Source para el marker animado
    instance.map.addSource(tripMarkerSrcId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });

    // Layer para la línea del trip
    instance.map.addLayer({
      id: `${tripSrcId}-line`,
      type: 'line',
      source: tripSrcId,
      paint: {
        'line-color': this.colorReproduccion,
        'line-width': 4,
      },
    });

    // Layers para el marker animado
    instance.map.addLayer({
      id: `${tripMarkerSrcId}-circle`,
      type: 'circle',
      source: tripMarkerSrcId,
      paint: {
        'circle-radius': 8,
        'circle-color': this.colorReproduccion,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });

    instance.tripLayers = [`${tripSrcId}-line`, `${tripMarkerSrcId}-circle`];
  }

  /**
   * Deja el punto del viaje donde indica la distancia recorrida y devuelve esa
   * posición.
   */
  private dibujarPuntoViaje(
    instance: MapboxMapInstance,
  ): [number, number] | null {
    if (!instance.tripCoords || !instance.tripMarkerSrc) return null;

    const punto = this.getPointAlongLine(
      instance.tripCoords,
      instance.distance ?? 0,
    );
    const markerSource = instance.map.getSource(instance.tripMarkerSrc) as
      | mapboxgl.GeoJSONSource
      | undefined;
    if (!markerSource || !punto) return null;

    markerSource.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: punto },
          properties: {},
        },
      ],
    });
    instance.tripCurrentPoint = { lng: punto[0], lat: punto[1] };

    return punto;
  }

  private getPointAlongLine(
    coords: [number, number][],
    distance: number,
  ): [number, number] | null {
    let accumulatedDistance = 0;

    for (let i = 0; i < coords.length - 1; i++) {
      const segmentDistance = this.serviceDibujar.calculateDistance(
        coords[i],
        coords[i + 1],
      );

      if (accumulatedDistance + segmentDistance >= distance) {
        // El punto está en este segmento
        const remainingDistance = distance - accumulatedDistance;
        const ratio = remainingDistance / segmentDistance;

        const lng = coords[i][0] + (coords[i + 1][0] - coords[i][0]) * ratio;
        const lat = coords[i][1] + (coords[i + 1][1] - coords[i][1]) * ratio;

        return [lng, lat];
      }

      accumulatedDistance += segmentDistance;
    }

    // Si no encontramos el punto, devolver el último
    return coords[coords.length - 1];
  }

  /**
   * Muestra un popup con los detalles de un punto de exceso de velocidad
   */
  private mostrarPopupExcesoVelocidad(
    instance: MapboxMapInstance,
    punto: {
      coordinate: [number, number];
      fechaDevice: string;
      velocidad: number;
      address?: string;
    },
  ): void {
    if (!instance.popup) return;

    const fecha = punto.fechaDevice
      ? formatDate(punto.fechaDevice, 'dd/MM/yyyy HH:mm:ss', 'es-AR')
      : 'Sin fecha';

    const velocidad = Math.floor(punto.velocidad);
    const lat = punto.coordinate[1].toFixed(5);
    const lng = punto.coordinate[0].toFixed(5);

    const rows: { label: string; value: string }[] = [
      {
        label: 'Velocidad',
        value: `<span style="font-size:14px;font-weight:700;color:var(--dp-speed)">${velocidad} km/h</span>`,
      },
      { label: 'Fecha', value: fecha },
    ];
    if (punto.address) {
      const addr = String(punto.address)
        .replace(/https?:\/\/\S+/g, '')
        .trim();
      if (addr) rows.push({ label: 'Dirección', value: escaparHtml(addr) });
    }

    const html = this.buildPopupHTML({
      color: '#c62828',
      icon: 'speed',
      title: 'EXCESO DE VELOCIDAD',
      rows,
      coords: `${lat}, ${lng}`,
    });

    instance.popup
      .setLngLat(punto.coordinate)
      .setHTML(sanitizarHtml(html))
      .addTo(instance.map);
  }

  /**
   * Muestra un popup con los detalles de una parada
   */
  private mostrarPopupParada(
    instance: MapboxMapInstance,
    parada: any,
    coords: [number, number],
  ): void {
    if (!instance.popup) return;

    const fmt = (d: string) => HelperService.parseDate(new Date(d));

    const durHora = parada.duration ? Math.floor(parada.duration / 3600) : 0;
    const durMin = parada.duration
      ? Math.floor((parada.duration % 3600) / 60)
      : 0;
    const durSeg = parada.duration ? Math.floor(parada.duration % 60) : 0;
    let durStr: string;
    if (!parada.duration) {
      durStr = 'N/A';
    } else if (durHora > 0) {
      // 60 min o más → horas, minutos y segundos
      durStr =
        `${durHora} h` +
        (durMin > 0 ? ` ${durMin} min` : '') +
        (durSeg > 0 ? ` ${durSeg} seg` : '');
    } else if (durMin > 0) {
      durStr = `${durMin} min${durSeg > 0 ? ' ' + durSeg + ' seg' : ''}`;
    } else {
      durStr = `${durSeg} seg`;
    }

    const lat = (parada.latitude ?? coords[1]).toFixed(5);
    const lng = (parada.longitude ?? coords[0]).toFixed(5);

    const rows: { label: string; value: string }[] = [
      { label: 'Desde', value: fmt(parada.startTime) },
    ];
    if (parada.endTime)
      rows.push({ label: 'Hasta', value: fmt(parada.endTime) });
    rows.push({
      label: 'Duración',
      value: `<span style="font-weight:700;color:var(--dp-duration)">${durStr}</span>`,
    });
    if (parada.address) {
      // Mostrar solo la parte de texto de la dirección (sin URLs de Google Maps)
      const addr = String(parada.address)
        .replace(/https?:\/\/\S+/g, '')
        .trim();
      if (addr) rows.push({ label: 'Dirección', value: escaparHtml(addr) });
    }

    const html = this.buildPopupHTML({
      color: '#0277bd',
      icon: 'pause_circle',
      title: 'DETENIDO',
      rows,
      coords: `${lat}, ${lng}`,
    });

    instance.popup
      .setLngLat(coords)
      .setHTML(sanitizarHtml(html))
      .addTo(instance.map);
  }

  /**
   * Muestra un popup con los detalles de un evento
   */
  private mostrarPopupEvento(
    instance: MapboxMapInstance,
    evento: any,
    coords: [number, number],
  ): void {
    if (!instance.popup) return;

    const valores = evento.valores as any;
    const titulo =
      valores?.titulo || evento.categoria || evento.tipoEvento || 'Evento';
    const mensaje =
      valores?.mensaje && valores.mensaje !== titulo ? valores.mensaje : null;
    const direccion = valores?.direccion ?? null;

    const fecha = evento.fechaCreacion
      ? formatDate(evento.fechaCreacion, 'dd/MM/yyyy HH:mm:ss', 'es-AR')
      : 'Sin fecha';

    // Usar las coordenadas reales del evento para mostrar en el contenido,
    // independientemente de si el marker fue desplazado por resolveOverlapOffsets
    const geojsonCoords = valores?.geojson?.coordinates;
    const displayCoords: [number, number] =
      geojsonCoords?.length >= 2 ? geojsonCoords : coords;
    const lat = displayCoords[1].toFixed(5);
    const lng = displayCoords[0].toFixed(5);

    const rows: { label: string; value: string }[] = [
      { label: 'Fecha', value: fecha },
    ];
    if (mensaje) rows.push({ label: 'Mensaje', value: escaparHtml(mensaje) });
    if (direccion)
      rows.push({ label: 'Dirección', value: escaparHtml(direccion) });

    const html = this.buildPopupHTML({
      color: '#6a1b9a',
      icon: 'report',
      title: titulo.toUpperCase(),
      rows,
      coords: `${lat}, ${lng}`,
    });

    instance.popup
      .setLngLat(coords)
      .setHTML(sanitizarHtml(html))
      .addTo(instance.map);
  }

  /**
   * Genera el HTML de un popup con diseño consistente (fondo oscuro, header coloreado)
   */
  private buildPopupHTML(opts: {
    color: string;
    icon: string;
    title: string;
    rows: { label: string; value: string }[];
    coords: string;
  }): string {
    const rowsHtml = opts.rows
      .map(
        (r) => `
        <div style="display:flex;justify-content:space-between;align-items:baseline;gap:16px;">
          <span style="font-size:11px;color:var(--dp-label);white-space:nowrap;flex-shrink:0;">${r.label}</span>
          <span style="font-size:12px;font-weight:500;text-align:right;color:var(--dp-text);word-break:break-word;max-width:190px;">${r.value}</span>
        </div>`,
      )
      .join('');

    return `
      <div style="font-family:'Roboto',sans-serif;min-width:210px;background:var(--dp-bg);border-radius:8px;overflow:hidden;color:var(--dp-text);">
        <div style="background:${opts.color};padding:8px 32px 8px 12px;display:flex;align-items:center;gap:8px;">
          <span class="material-icons" style="font-size:16px;color:white;flex-shrink:0;">${opts.icon}</span>
          <span style="font-size:11px;font-weight:700;color:white;letter-spacing:0.8px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escaparHtml(opts.title)}</span>
        </div>
        <div style="padding:10px 12px;display:flex;flex-direction:column;gap:7px;">
          ${rowsHtml}
          <div style="display:flex;justify-content:space-between;align-items:baseline;gap:16px;border-top:1px solid var(--dp-border);padding-top:7px;margin-top:2px;">
            <span style="font-size:11px;color:var(--dp-label);white-space:nowrap;flex-shrink:0;">Posición</span>
            <span style="font-size:11px;font-family:monospace;color:var(--dp-coords);">${opts.coords}</span>
          </div>
        </div>
      </div>`;
  }

  private stopAnimation(instance: MapboxMapInstance, resetProgress = false) {
    instance.animating = false;

    // Se hace ahora la recarga que se salteo mientras el viaje corria.
    if (instance.recargaPendiente) {
      instance.recargaPendiente = false;
      const idx = this.mapInstances.indexOf(instance);
      if (idx >= 0) void this.cargarMarkersForInstance(instance, idx);
    }
    // Preservar tripCurrentPoint, distance y el largo del viaje para permitir
    // resume y para poder arrastrar la barra con la reproducción en pausa.
    // Solo limpiar si se pide reset explícito (ej: al cambiar de viaje)
    if (resetProgress) {
      instance.tripTotalLength = undefined;
      instance.tripCurrentPoint = undefined;
      instance.distance = 0;
      this.pintarProgreso();
    }

    // Detener el seguimiento de cámara del viaje
    if (instance.cameraService) {
      instance.cameraService.stopFollow();
    }

    if (instance.animationFrame) {
      cancelAnimationFrame(instance.animationFrame);
      instance.animationFrame = undefined;
    }

    // Solo limpiar las capas del trip si es un reset completo (cambio de viaje).
    // En pausa simple se preservan para que el punto siga visible.
    if (resetProgress) {
      if (instance.tripSrc) {
        const tripLineSource = instance.map.getSource(
          instance.tripSrc,
        ) as mapboxgl.GeoJSONSource;
        if (tripLineSource) {
          tripLineSource.setData({ type: 'FeatureCollection', features: [] });
        }
      }
      if (instance.tripMarkerSrc) {
        const markerSource = instance.map.getSource(
          instance.tripMarkerSrc,
        ) as mapboxgl.GeoJSONSource;
        if (markerSource) {
          markerSource.setData({ type: 'FeatureCollection', features: [] });
        }
      }
    }
  }

  /**
   * Cambió el color de la reproducción: se aplica sobre las capas que ya
   * existan (las crea el play, así que pueden no estar todavía) y se guarda.
   */
  public onColorReproduccionChange(): void {
    for (const instance of this.mapInstances) {
      for (const capa of instance.tripLayers ?? []) {
        if (!instance.map.getLayer(capa)) continue;

        instance.map.setPaintProperty(
          capa,
          capa.includes('marker') ? 'circle-color' : 'line-color',
          this.colorReproduccion,
        );
      }
    }

    try {
      localStorage.setItem(this.claveColorReproduccion, this.colorReproduccion);
    } catch (error) {
      console.warn(
        '[Mapa] No se pudo guardar el color de reproducción:',
        error,
      );
    }
  }

  private get claveColorReproduccion(): string {
    return `recorrido-color-reproduccion-${LoginService.getUsuario()?._id ?? 'anonimo'}`;
  }

  private cargarColorReproduccion(): void {
    try {
      const guardado = localStorage.getItem(this.claveColorReproduccion);
      if (guardado) this.colorReproduccion = guardado;
    } catch (error) {
      console.warn('[Mapa] No se pudo leer el color de reproducción:', error);
    }
  }

  public onSpeedChange(speed: number) {
    this.speed = speed;

    // Actualizar speed de todas las instancias que estén animando
    this.mapInstances.forEach((instance) => {
      if (instance.animating) {
        instance.speed = speed;
      }
    });
  }

  public togglePolylineVisibility(
    instance: MapboxMapInstance,
    visible: boolean,
  ) {
    // Alternar visibilidad de las capas de polyline
    instance.polylinesLayers.forEach((layerId) => {
      instance.map.setLayoutProperty(
        layerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    });
    instance.polylinesVisible = visible;
  }

  /**
   * Muestra u oculta los puntos de reporte del recorrido. Las banderas de
   * inicio y fin se quedan: son del recorrido, no reportes sueltos.
   */
  public toggleReportesVisibility(instance: MapboxMapInstance) {
    const capa = `${instance.pointsSrc}-circle`;
    if (!instance.map.getLayer(capa)) return;

    // Arranca visible: la primera vez que se toca, se ocultan.
    const visibles = instance.reportesVisibles ?? true;
    instance.reportesVisibles = !visibles;

    instance.map.setLayoutProperty(
      capa,
      'visibility',
      instance.reportesVisibles ? 'visible' : 'none',
    );
  }

  // POPUP DE AYUDA
  public toggleHelp() {
    this.showHelp = !this.showHelp;
  }

  // CENTRAR EN VEHÍCULOS ENCONTRADOS POR BÚSQUEDA
  public centrarEnVehiculos(
    reportes: IReporteBase<'Tracker 4G'>[],
    mapIndex?: number,
  ) {
    // Usar el mapIndex pasado como parámetro, o calcular desde mapaSeleccionado si no se proporciona
    const targetMapIndex =
      mapIndex !== undefined ? mapIndex : this.mapaSeleccionado - 1;

    // Si no hay reportes, limpiar filtro (mostrar todos)
    if (!reportes || reportes.length === 0) {
      this.vehiculosFiltradosPorBusqueda.set(targetMapIndex, new Set());
      this.actualizarVisibilidadVehiculos(targetMapIndex);
      return;
    }

    // Actualizar lista de vehículos filtrados para este mapa específico
    const filtroMapa = new Set(
      reportes
        .map((r) => r.idEntidad || r.idsAsignados?.[0])
        .filter((id): id is string => !!id),
    );
    this.vehiculosFiltradosPorBusqueda.set(targetMapIndex, filtroMapa);

    // Actualizar visibilidad de vehículos inmediatamente
    this.actualizarVisibilidadVehiculos(targetMapIndex);

    // Filtrar reportes con coordenadas válidas
    const reportesConCoordenadas = reportes.filter((r) => {
      const valores = this.helper.getValores(r);
      const posicion = this.getPosicionParaMapa(valores, targetMapIndex + 1);
      return (
        posicion?.coordinates &&
        posicion.coordinates[0] &&
        posicion.coordinates[1]
      );
    });

    if (reportesConCoordenadas.length === 0) {
      return;
    }

    const instance = this.mapInstances[targetMapIndex];

    if (!instance?.map) {
      return;
    }

    // Calcular padding considerando el sidebar (está a la derecha)
    // El sidebar ocupa entre 250-450px, por lo que ajustamos el padding derecho
    const paddingConfig = {
      top: 50,
      bottom: 50,
      left: 50,
      right: 350, // Mayor padding a la derecha para compensar el sidebar
    };

    // Si solo hay un vehículo, centrar en él con zoom
    if (reportesConCoordenadas.length === 1) {
      const valores = this.helper.getValores(reportesConCoordenadas[0]);
      const posicion = this.getPosicionParaMapa(valores, targetMapIndex + 1);
      if (posicion?.coordinates) {
        const [lng, lat] = posicion.coordinates;

        const sidebarWidth = 350; // Aproximadamente el ancho del sidebar
        const offset = -(sidebarWidth / 2); // Desplazar a la izquierda

        instance.map.easeTo({
          center: [lng, lat],
          zoom: 15,
          duration: 1000,
          offset: [offset, 0], // Desplazar para centrar en el espacio visible
        });
      }
      return;
    }

    // Si hay múltiples vehículos, calcular bounds y ajustar
    const bounds = new mapboxgl.LngLatBounds();
    reportesConCoordenadas.forEach((r) => {
      const valores = this.helper.getValores(r);
      const posicion = this.getPosicionParaMapa(valores, targetMapIndex + 1);
      if (posicion?.coordinates) {
        const [lng, lat] = posicion.coordinates;
        bounds.extend([lng, lat]);
      }
    });

    instance.map.fitBounds(bounds, {
      padding: paddingConfig,
      duration: 1000,
      maxZoom: 15,
    });
  }

  // Actualizar visibilidad de vehículos según filtro de búsqueda
  private actualizarVisibilidadVehiculos(mapIndex?: number): void {
    // Usar el mapIndex pasado como parámetro, o usar el mapa seleccionado actual
    const targetMapIndex =
      mapIndex !== undefined ? mapIndex : this.mapaSeleccionado - 1;
    const instance = this.mapInstances[targetMapIndex];

    if (!instance?.map) return;

    // Obtener filtro específico de este mapa
    // IMPORTANTE: Si el Map tiene la key pero el Set está vacío, significa búsqueda con 0 resultados (ocultar todos)
    // Si el Map no tiene la key, significa que no hay búsqueda activa (mostrar todos)
    const tieneBusquedaActiva =
      this.vehiculosFiltradosPorBusqueda.has(targetMapIndex);
    const filtroMapa = this.vehiculosFiltradosPorBusqueda.get(targetMapIndex);

    // Actualizar visibilidad del marcador HTML si existe
    if (instance.htmlVehicleMarker) {
      const vehicleId = instance.htmlVehicleMarker.dataset.vehicleId;
      if (vehicleId) {
        const mostrar =
          !tieneBusquedaActiva || (filtroMapa && filtroMapa.has(vehicleId));
        const element = instance.htmlVehicleMarker;

        // Usar visibility en lugar de display para mejor performance
        element.style.visibility = mostrar ? 'visible' : 'hidden';
      }
    }

    // Forzar re-render del symbol layer para aplicar el filtro
    this.cargarMarkersForInstance(instance, targetMapIndex);
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // HISTORIAL DE RUTA CON OSRM
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Actualiza el historial de ruta con nuevas coordenadas
   */
  private updateRouteHistory(
    mapIndex: number,
    newPathCoordinates: [number, number][],
  ): void {
    const history = this.routeHistory.get(mapIndex) || [];

    // Evitar duplicados consecutivos al unir tramos OSRM
    if (history.length > 0 && newPathCoordinates.length > 0) {
      const last = history[history.length - 1];
      const first = newPathCoordinates[0];
      if (last[0] === first[0] && last[1] === first[1]) {
        newPathCoordinates = newPathCoordinates.slice(1);
      }
    }

    if (newPathCoordinates.length === 0) return;

    // La animación del HTML marker emite cada ~50ms la punta interpolada sobre
    // el tramo actual. Si el último punto quedó en medio del segmento que va
    // del anterior al nuevo, no aporta nada al dibujo: se reemplaza en vez de
    // acumularlo (antes el trail crecía ~20 puntos/s y cada setData mandaba
    // la línea entera, cada vez más pesada con la pestaña abierta horas).
    for (const punto of newPathCoordinates) {
      const n = history.length;
      if (n >= 2 && this.estaEntre(history[n - 2], history[n - 1], punto)) {
        history[n - 1] = punto;
      } else {
        history.push(punto);
      }
    }
    // Tope de seguridad: descartar lo más viejo del trail.
    if (history.length > this.MAX_ROUTE_HISTORY) {
      history.splice(0, history.length - this.MAX_ROUTE_HISTORY);
    }
    this.routeHistory.set(mapIndex, history);
    this.updateRouteLayer(mapIndex, history);
  }

  /** `b` está sobre el segmento a→c (colineal y entre ambos). */
  private estaEntre(
    a: [number, number],
    b: [number, number],
    c: [number, number],
  ): boolean {
    const abx = b[0] - a[0];
    const aby = b[1] - a[1];
    const bcx = c[0] - b[0];
    const bcy = c[1] - b[1];
    const cross = abx * bcy - aby * bcx;
    const escala = Math.hypot(abx, aby) * Math.hypot(bcx, bcy);
    if (escala === 0) return true; // punto repetido
    return Math.abs(cross) <= escala * 1e-6 && abx * bcx + aby * bcy >= 0;
  }

  /**
   * Actualiza el layer de ruta en el mapa con el historial acumulado
   */
  private updateRouteLayer(
    mapIndex: number,
    coordinates: [number, number][],
  ): void {
    const instance = this.mapInstances[mapIndex];
    if (!instance || !instance.map) return;

    // No actualizar/crear si hay menos de 2 coordenadas (LineString necesita >= 2)
    if (coordinates.length < 2) return;

    const sourceId = `vehicle-route-${mapIndex}`;

    const geojsonData: GeoJSON.Feature = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: coordinates,
      },
    };

    const source = instance.map.getSource(sourceId);
    if (source) {
      (source as mapboxgl.GeoJSONSource).setData(geojsonData);
    } else {
      this.createRouteLayer(mapIndex, coordinates);
    }
  }

  /**
   * Crea el source y layers de ruta de seguimiento en el mapa
   * Incluye borde, línea principal y flechas de dirección
   */
  private createRouteLayer(
    mapIndex: number,
    coordinates: [number, number][],
  ): void {
    const instance = this.mapInstances[mapIndex];
    if (!instance || !instance.map) return;

    // No crear layers para LineString con menos de 2 puntos (GeoJSON inválido)
    if (coordinates.length < 2) return;

    const sourceId = `vehicle-route-${mapIndex}`;
    const borderLayerId = `vehicle-route-border-${mapIndex}`;
    const lineLayerId = `vehicle-route-line-${mapIndex}`;
    const arrowLayerId = `vehicle-route-arrow-${mapIndex}`;

    try {
      // Agregar source
      instance.map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: coordinates,
          },
        },
      });

      // Verificar que el beforeLayer exista; si no, agregar encima de todo
      const beforeLayer = instance.vehiculosLayers[0];
      const validBeforeLayer =
        beforeLayer && instance.map.getLayer(beforeLayer)
          ? beforeLayer
          : undefined;

      // Capa 1: Borde oscuro para contraste
      instance.map.addLayer(
        {
          id: borderLayerId,
          type: 'line',
          source: sourceId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': '#1a3a7a',
            'line-width': 7,
            'line-opacity': 0.5,
          },
        },
        validBeforeLayer,
      );

      // Capa 2: Línea principal
      instance.map.addLayer(
        {
          id: lineLayerId,
          type: 'line',
          source: sourceId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': '#4285F4',
            'line-width': 4,
            'line-opacity': 0.9,
          },
        },
        validBeforeLayer,
      );

      // Capa 3: Flechas de dirección
      instance.map.addLayer(
        {
          id: arrowLayerId,
          type: 'symbol',
          source: sourceId,
          layout: {
            'symbol-placement': 'line',
            'symbol-spacing': 80,
            'icon-image': 'arrow',
            'icon-size': 0.5,
            'icon-rotate': 90,
            'icon-rotation-alignment': 'map',
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          },
          paint: {
            'icon-color': '#ffffff',
            'icon-opacity': 0.9,
          },
        },
        validBeforeLayer,
      );
    } catch (e) {
      console.error('Error creando route layer:', e);
    }
  }

  /**
   * Limpia el historial de ruta de un mapa
   */
  private clearRouteHistory(mapIndex: number): void {
    this.routeHistory.delete(mapIndex);

    const instance = this.mapInstances[mapIndex];
    if (!instance || !instance.map) return;

    const sourceId = `vehicle-route-${mapIndex}`;
    const borderLayerId = `vehicle-route-border-${mapIndex}`;
    const lineLayerId = `vehicle-route-line-${mapIndex}`;
    const arrowLayerId = `vehicle-route-arrow-${mapIndex}`;

    // Remover layers en orden inverso
    [arrowLayerId, lineLayerId, borderLayerId].forEach((id) => {
      if (instance.map.getLayer(id)) {
        instance.map.removeLayer(id);
      }
    });

    if (instance.map.getSource(sourceId)) {
      instance.map.removeSource(sourceId);
    }
  }
}
